/**
 * FODMAP Intelligence Engine
 * 
 * Provides Monash University laboratory-tested FODMAP evaluations,
 * serving-size thresholds, multi-ingredient recipe decomposition,
 * and clinically accepted low-FODMAP swaps.
 * 
 * Includes:
 * 1. Instant offline core database of top 150+ common foods, allergens, and culprits.
 * 2. Background sync & caching of full 1,670+ Monash dataset from fodmapibsdiet.com.
 * 3. Multi-ingredient recipe evaluator with traffic-light verdicts (Low, Moderate, High).
 * 4. Smart low-FODMAP alternatives & serving-size tips.
 */

import {
  Meal,
  FodmapStackingAlert,
  PlateDish,
  DetailedFodmapStackingReport,
  FodmapSubtype,
  FodmapSubtypeLoad,
  MealIngredientItem,
  CustomFoodItem,
  FoodDatabaseOverride,
  FoodComposition100g,
  FoodProvenance,
} from './types';
import { localStore } from './storage';
import { canonicalizeIngredient, isProtectedLowFodmap, formatIngredientTitle } from './ingredientUtils';
import OPEN_FODMAP_DATABASE from './openFodmapDatabase.json';

export type FodmapRating = 'low' | 'moderate' | 'high';

export interface FodmapCarbLevels {
  fructose: 1 | 2 | 3;
  lactose: 1 | 2 | 3;
  fructans: 1 | 2 | 3;
  gos: 1 | 2 | 3;
  sorbitol: 1 | 2 | 3;
  mannitol: 1 | 2 | 3;
}

export interface FodmapFoodItem {
  id: string;
  name: string;
  slug?: string;
  category: string;
  rating: FodmapRating;
  primaryCulprit?: string;
  provenance?: FoodProvenance;
  compositionPer100g?: FoodComposition100g;
  safeGrams?: number;
  moderateMaxGrams?: number;
  servings?: {
    standard?: { title?: string; grams?: number; comment?: string };
    low?: { title?: string; grams?: number; comment?: string };
    moderate?: { title?: string; grams?: number; comment?: string };
    high?: { title?: string; grams?: number; comment?: string };
  };
  fodmaps?: FodmapCarbLevels;
  swapSuggestion?: string;
  notes?: string;
  isCustom?: boolean;
  isCloudShared?: boolean;
  isHidden?: boolean;
  customNotes?: string;
}

export interface IngredientEvaluation {
  raw: string;
  matchedFood?: FodmapFoodItem;
  rating: FodmapRating;
  primaryCulprit?: string; // e.g., 'Fructans', 'Lactose', 'Excess Fructose', 'Sorbitol', 'Mannitol', 'GOS'
  swapSuggestion?: string;
  servingLimitNote?: string;
  clinicalGuidance?: string;
}

export interface FodmapAnalysisResult {
  overallRating: FodmapRating;
  summary: string;
  detectedIngredients: IngredientEvaluation[];
  highFodmapCount: number;
  moderateFodmapCount: number;
  lowFodmapCount: number;
  flaggedCulprits: string[]; // e.g. ['Fructans', 'Lactose']
  smartSwaps: { original: string; swap: string; reason: string }[];
  safePortionTips: string[];
  clinicalGuidance?: string;
}

// -------------------------------------------------------------
// SINGLE SOURCE OF TRUTH: Clean USDA + Open Composition Database
// -------------------------------------------------------------

export const CORE_FODMAP_DATABASE: FodmapFoodItem[] = OPEN_FODMAP_DATABASE as FodmapFoodItem[];

// Local cache key
const FODMAP_STORAGE_KEY = 'digestlog_monash_fodmap_dataset_v1';
const FODMAP_TIMESTAMP_KEY = 'digestlog_monash_fodmap_updated_at';

// In-memory lookup map
let cachedFoodItems: FodmapFoodItem[] = [...CORE_FODMAP_DATABASE];
let isSyncing = false;
let aliasToFoodItemMap: Map<string, FodmapFoodItem> = new Map();

const GENERIC_ADJECTIVES = new Set([
  'white', 'brown', 'red', 'green', 'yellow', 'black', 'plain', 'raw', 'firm',
  'unripe', 'ripe', 'sweet', 'light', 'dark', 'whole', 'skim', 'skimmed', 'low',
  'high', 'extra', 'regular', 'pure', 'fresh', 'canned', 'dried', 'frozen',
  'cooked', 'steamed', 'fried', 'baked', 'roasted', 'diced', 'sliced', 'chopped',
  'ground', 'minced', 'head', 'heads', 'bulb', 'bulbs', 'leaves', 'tops', 'top',
  'clove', 'cloves', 'slice', 'slices', 'piece', 'pieces', 'only', 'with', 'without',
  'and', 'or', 'in', 'part', 'parts', 'self', 'raising', 'all', 'purpose',
  'flour', 'flours', 'oil', 'oils', 'milk', 'milks', 'cheese', 'cheeses',
  'bread', 'breads', 'pasta', 'pastas', 'meal', 'powder', 'powders',
  'starch', 'starches', 'seed', 'seeds', 'syrup', 'syrups', 'extract', 'grain', 'grains'
]);

const PREPARATION_DESCRIPTORS = new Set([
  'raw', 'cooked', 'fresh', 'dried', 'canned', 'frozen', 'boiled', 'fried', 'baked',
  'roasted', 'steamed', 'broiled', 'braised', 'poached', 'scrambled', 'hard-boiled',
  'whole', 'sliced', 'diced', 'chopped', 'ground', 'minced', 'peeled', 'unpeeled',
  'with skin', 'without skin', 'flesh only', 'meat only', 'plain', 'salted', 'unsalted',
  'regular', 'commercial', 'prepared'
]);

const IGNORED_PARENS_PREFIXES = /^(includes|contain|may contain|with|without|e\.g\.|such as|prepared|made with|commercial|usda|nfs|recipe|from|pack|drained|solids|edible|canned|frozen|raw|cooked|boiled|fried|baked|roasted|dry mix|refrigerated|stabilized|reduced|glucose reduced|not fortified|fortified|chops|roasts|fillet|steak)/i;

// Fundamental staples mapped directly to representative base Monash items
const CANONICAL_STAPLE_MAPPINGS: Record<string, string> = {
  // Eggs
  'egg': '4GDO6VKY1R', // Egg, chicken, whole
  'eggs': '4GDO6VKY1R',
  'whole egg': '4GDO6VKY1R',
  'whole eggs': '4GDO6VKY1R',
  'egg white': '1ARQQX6L72',
  'egg whites': '1ARQQX6L72',
  'egg yolk': '4GDO6VKY1R',
  'egg yolks': '4GDO6VKY1R',
  'fried egg': '4GDO6VKY1R',
  'scrambled egg': '4GDO6VKY1R',
  'scrambled eggs': '4GDO6VKY1R',
  'hard boiled egg': '4GDO6VKY1R',
  'hard-boiled egg': '4GDO6VKY1R',
  'boiled egg': '4GDO6VKY1R',

  // Dairy & Cheeses
  'milk': 'DGGTSZDZJS', // Milk, cow, regular fat
  'whole milk': 'DGGTSZDZJS',
  'cow milk': 'DGGTSZDZJS',
  'cows milk': 'DGGTSZDZJS',
  'skim milk': 'ITK7CCTPUY', // Milk, cow, skim
  'reduced fat milk': 'SJ45Z2E9EI',
  'lactose-free milk': '3OQXS6MC1V', // Milk, cow, lactose free, regular fat
  'lactose free milk': '3OQXS6MC1V',
  'almond milk': 'UYTMYSWISE', // Milk, almond
  'oat milk': '8PTL9ADJZS', // Milk, oat
  'soy milk': '5EU2GQHWXY', // Milk, soy (soy protein)
  'butter': '488SNHLARV', // Butter
  'cheddar': 'QNNTAIH6AN', // Cheese, cheddar, tasty / Colby, natural, regular fat
  'cheddar cheese': 'QNNTAIH6AN',
  'parmesan': '0MS3HL6JXK', // Cheese, parmesan
  'parmesan cheese': '0MS3HL6JXK',
  'mozzarella': 'BGYDPOD05G', // Cheese, mozzarella
  'mozzarella cheese': 'BGYDPOD05G',
  'feta': '7J2IJQ5GXL', // Cheese, fetta
  'feta cheese': '7J2IJQ5GXL',
  'fetta': '7J2IJQ5GXL',
  'swiss cheese': '50BZJ2FLRY', // Cheese, Swiss
  'brie': '4K648E75I6', // Cheese, brie, French
  'camembert': 'R72HMFCVBU', // Cheese, Camembert
  'ricotta': '4LRL4HLZ71', // Cheese, ricotta
  'cottage cheese': 'ZINF4HZ6ET', // Cheese, cottage
  'cream cheese': 'BA9UNHS52O', // Cheese, cream, plain, regular fat
  'yogurt': 'M3ZDDUUC5G', // Yoghurt, Greek, plain, regular fat
  'greek yogurt': 'M3ZDDUUC5G',
  'yoghurt': 'M3ZDDUUC5G',
  'greek yoghurt': 'M3ZDDUUC5G',
  'lactose free yogurt': 'ZLAF88E67M',
  'lactose free yoghurt': 'ZLAF88E67M',

  // Meats & Poultry & Fish
  'chicken': 'PEL4B3A1C0', // Chicken, all cuts, plain, cooked
  'chicken breast': 'PEL4B3A1C0',
  'chicken thigh': 'PEL4B3A1C0',
  'beef': 'CNYPIGWT9Q', // Beef, all cuts, plain, cooked
  'ground beef': 'CNYPIGWT9Q',
  'steak': 'CNYPIGWT9Q',
  'pork': '3CE7RBTIJ1', // Bacon / Pork
  'pork chop': '3CE7RBTIJ1',
  'bacon': '3CE7RBTIJ1',
  'lamb': 'R3ZY8GC1TA', // Lamb, all cuts, plain, cooked
  'salmon': '7HRZHJDPIF', // Salmon, plain, canned in brine, drained
  'fresh salmon': '7HRZHJDPIF',
  'tuna': 'KG2C0FHCIG', // Tuna, plain, canned in brine, drained
  'canned tuna': 'KG2C0FHCIG',
  'turkey': 'S05IHJVOE8',

  // Produce & Vegetables
  'potato': 'E0OB9TFP6V', // Potato, white, unpeeled, raw
  'potatoes': 'E0OB9TFP6V',
  'white potato': 'E0OB9TFP6V',
  'white potatoes': 'E0OB9TFP6V',
  'red potato': 'F8F8VQJZ6X', // Potato, red, unpeeled, raw
  'red potatoes': 'F8F8VQJZ6X',
  'sweet potato': 'OXWOZ8VKHK', // Potato, sweet / kumara, orange, unpeeled, raw
  'sweet potatoes': 'OXWOZ8VKHK',
  'kumara': 'OXWOZ8VKHK',
  'mashed potato': 'E0OB9TFP6V',
  'mashed potatoes': 'E0OB9TFP6V',
  'tomato': 'IRPUPUVVVQ', // Tomato, common, raw
  'tomatoes': 'IRPUPUVVVQ',
  'cherry tomato': 'L3ZA95U6ZL', // Tomato, cherry, raw
  'cherry tomatoes': 'L3ZA95U6ZL',
  'roma tomato': 'MVAT1ODFCT', // Tomato, Roma / plum, raw
  'roma tomatoes': 'MVAT1ODFCT',
  'cucumber': '9N6F4RUT1V', // Cucumber, continental, raw
  'cucumbers': '9N6F4RUT1V',
  'spinach': '9MQYRUUOOL', // Spinach, English, raw
  'baby spinach': 'DP3BWLZ4L5', // Spinach, baby, raw
  'english spinach': '9MQYRUUOOL',
  'courgette': '2B5QAM1GDL', // Zucchini / Courgette, raw
  'zucchini': 'SX25J9WM9N',
  'eggplant': 'VGAQSSGVFX', // Aubergine / Brinjal / Eggplant, unpeeled, raw
  'aubergine': 'VGAQSSGVFX',
  'lettuce': '414D3IP9RX', // Lettuce, iceberg, raw
  'iceberg lettuce': '414D3IP9RX',
  'romaine lettuce': 'UTIB18CRTT', // Lettuce, cos / romaine, raw
  'cos lettuce': 'UTIB18CRTT',
  'capsicum': 'N9F4Z8SE9K', // Bell pepper / Capsicum, green, raw
  'bell pepper': 'N9F4Z8SE9K',
  'red pepper': 'VMRLKQ593X',
  'green pepper': 'N9F4Z8SE9K',
  'red capsicum': 'VMRLKQ593X',
  'green capsicum': 'N9F4Z8SE9K',
  'mushrooms': 'GJ6UTXB2DH', // Mushroom, champignons / white button, canned in brine, drained
  'mushroom': 'GJ6UTXB2DH',
  'button mushroom': 'GJ6UTXB2DH',
  'button mushrooms': 'GJ6UTXB2DH',
  'cremini mushroom': 'YC0YZ504ZP', // Mushroom, portobella / portobello / giant cremini, raw
  'cremini mushrooms': 'YC0YZ504ZP',
  'portobello mushroom': 'YC0YZ504ZP',
  'portobello mushrooms': 'YC0YZ504ZP',
  'shiitake mushroom': 'RGPU2JTOZ0', // Mushroom, shiitake, raw
  'shiitake mushrooms': 'RGPU2JTOZ0',
  'oyster mushroom': 'OBJ2FT5KKT', // Mushroom, oyster / hiratake, raw
  'oyster mushrooms': 'OBJ2FT5KKT',
  'raw oyster mushrooms': 'OBJ2FT5KKT',
  'fresh oyster mushrooms': 'OBJ2FT5KKT',
  'canned champignons': 'GJ6UTXB2DH',
  'canned mushrooms': 'GJ6UTXB2DH',
  'garlic': 'ID0OG9OWY1', // Garlic, peeled, raw
  'garlic clove': 'ID0OG9OWY1',
  'garlic cloves': 'ID0OG9OWY1',
  'garlic powder': 'ZC1M6AOWXR', // Spice, garlic powder
  'onion': 'ZMM957FO1Q', // Onion, white, raw
  'onions': 'ZMM957FO1Q',
  'white onion': 'ZMM957FO1Q',
  'red onion': '12UZIQCDRE', // Onion, Spanish / red, raw
  'brown onion': 'ZMM957FO1Q',
  'yellow onion': 'ZMM957FO1Q',
  'shallot': '5OWXB3T5TB', // Onion, shallot, raw
  'shallots': '5OWXB3T5TB',
  'spring onion': '72SUK08590', // Onion, spring / scallion (green tops only), raw
  'spring onions': '72SUK08590',
  'scallion': '72SUK08590',
  'scallions': '72SUK08590',
  'green onion': '72SUK08590',
  'green onions': '72SUK08590',
  'leek': 'KAVXFMGVMP', // Leek (green leaves only), raw
  'leeks': 'KAVXFMGVMP',

  // Fruits
  'banana': 'QI8HWSQGOM', // Banana, common, firm, peeled, raw
  'bananas': 'QI8HWSQGOM',
  'firm banana': 'QI8HWSQGOM',
  'ripe banana': 'ECSU2CY1UT', // Banana, common, ripe, peeled, raw
  'apple': '7X9EPAXNA0', // Apple, Pink Lady, raw
  'apples': '7X9EPAXNA0',
  'avocado': 'IAEIERH8BX', // Avocado, Hass, peeled, pitted, raw
  'avocados': 'IAEIERH8BX',
  'strawberry': '7OREAT3CUL', // Strawberry, raw
  'strawberries': '7OREAT3CUL',
  'blueberry': 'OQJVQJJESX', // Blueberry, raw
  'blueberries': 'OQJVQJJESX',
  'raspberry': '0USXUUDLM9', // Raspberry, raw
  'raspberries': '0USXUUDLM9',
  'orange': '43WPOUK1BM', // Orange, navel, peeled, raw
  'oranges': '43WPOUK1BM',
  'lemon': 'AUT10HXHNR', // Lemon, juice, fresh
  'lime': 'AM5VBRIINU', // Lime, juice, fresh
  'grapes': '4OV7MALYU8', // Grape, red, seedless, raw
  'watermelon': '79EQ33BPZX', // Watermelon, seedless, peeled, raw
  'pineapple': 'S631B7XX5Q', // Pineapple, peeled, raw

  // Grains & Breads & Pasta
  'white rice': '5RMY2X0OKS', // Rice, white, cooked
  'cooked white rice': '5RMY2X0OKS',
  'brown rice': 'VK9UPIXJ03', // Rice, brown, cooked
  'basmati rice': 'PAL6CO6RTX', // Basmati Rice
  'rice': '5RMY2X0OKS',
  'oats': 'GNEP2A40V1', // Oats, rolled, uncooked
  'rolled oats': 'GNEP2A40V1',
  'oatmeal': 'JPSFROG02A', // Oatmeal
  'porridge': 'BK8XEJBZ3Z', // Oats, rolled, cooked
  'sourdough': 'HGCX9BLHP0', // Bread, 100% Spelt Sourdough / Sourdough
  'sourdough bread': 'HGCX9BLHP0',
  'spelt sourdough': 'HGCX9BLHP0',
  'spelt bread': 'HGCX9BLHP0',
  'wheat bread': '44HIFJI31L', // Bread, wheat, white
  'white bread': '44HIFJI31L',
  'wholemeal bread': '09KIZNY1UZ', // Bread, wheat, wholemeal
  'gluten-free bread': '6QNR51921X', // Bread, gluten free, white
  'gluten free bread': '6QNR51921X',
  'pasta': 'YAAB4TACH4', // Pasta, gluten free / standard
  'wheat pasta': 'M0FB3KTXTV',
  'gluten-free pasta': 'YAAB4TACH4',
  'gluten free pasta': 'YAAB4TACH4',

  // Oils & Condiments
  'olive oil': 'JD2UKJBE5O', // Oil, olive
  'extra virgin olive oil': 'JD2UKJBE5O',
  'garlic oil': '1WDCFFKH9A', // Oil, olive, garlic infused
  'garlic-infused olive oil': '1WDCFFKH9A',
  'garlic infused olive oil': '1WDCFFKH9A',
  'garlic infused oil': '1WDCFFKH9A',
  'soy sauce': '3B0QM9XQP9', // Sauce, soy
  'tamari': 'PTOPVW3KWA', // Sauce, tamari
  'maple syrup': 'TA5L09Q1E0', // Syrup, maple, pure
  'honey': 'JY79HC8ZCI', // Honey
  'tofu': '8E8H05Z795', // Tofu (soy bean curd), firm, drained
  'firm tofu': '8E8H05Z795',
  'silken tofu': '5DAGN4BTJ6', // Tofu (soy bean curd), silken
};

/**
 * Returns generalized clinical guidance, testing status, and gut-irritant context
 * for any food or beverage category (e.g. Zinfandel, Espresso, spirits, aged cheeses, oils).
 */
export function getClinicalGuidanceForFood(name: string, category?: string, matchedFood?: FodmapFoodItem): string {
  const n = (name || '').toLowerCase();
  const cat = (category || matchedFood?.category || '').toLowerCase();

  // 1. Wines & Alcohol
  if (/zinfandel|pinot noir|cabernet|merlot|syrah|shiraz|malbec|rioja|chianti|sangiovese|red wine/i.test(n)) {
    return `${name} has not been tested in isolation for FODMAPs, but is considered Low FODMAP based on general dry red wine composition (1 glass / 150ml). Many types of alcohol are Low FODMAP including beer, red and white wine, gin, vodka, and whiskey. Other alcohols including rum and sweet sherry are High FODMAP (excess fructose). Note: Alcohol is a known gastrointestinal irritant and gut motility stimulant, so intake should generally be limited.`;
  }
  if (/sauvignon blanc|chardonnay|pinot grigio|pinot gris|riesling|chenin blanc|white wine/i.test(n)) {
    return `${name} is considered Low FODMAP based on dry white wine clinical standards (1 glass / 150ml). Sweet dessert wines and late-harvest varietals contain higher residual fructose. Note: Alcohol can irritate the gut lining; keep to 1–2 standard glasses.`;
  }
  if (/prosecco|champagne|cava|sparkling wine/i.test(n)) {
    return `Dry sparkling wine (such as Brut Prosecco, Champagne, and Cava) is Low FODMAP at standard 150ml servings. Note: Both alcohol and dissolved carbonation bubbles can trigger bloating or abdominal distension in sensitive individuals.`;
  }
  if (/rum|sherry|port wine|dessert wine|sticky wine/i.test(n)) {
    return `${name} is High in Excess Fructose. Unlike distilled clear spirits, rum and fortified dessert wines retain high levels of fermentable fructose sugars. Swap for dry wine, gin, vodka, or whiskey.`;
  }
  if (/gin|vodka|tequila|whiskey|whisky|bourbon|scotch|brandy/i.test(n)) {
    return `Pure distilled spirits (vodka, gin, tequila, whiskey) contain 0g carbohydrates and are Low FODMAP (1–2 standard shots / 30–60ml). Avoid mixers made with high-fructose corn syrup, honey, or fruit juices high in sorbitol/fructose. Alcohol is a GI irritant.`;
  }
  if (/beer|lager|ale|stout|pilsner/i.test(n)) {
    return `Standard beer is Low FODMAP at 1 can/bottle (375ml). While beer contains gluten (which affects celiac disease), gluten is a protein, not a FODMAP carbohydrate. The fructan level in 1 standard beer remains within the Low FODMAP threshold.`;
  }
  if (/cider/i.test(n)) {
    return `Apple and pear ciders are High FODMAP due to high concentrations of sorbitol and excess fructose extracted from the fruit. Swap for dry wine, beer, or distilled spirits with low-FODMAP mixers.`;
  }

  // 2. Coffee, Teas & Caffeine
  if (/espresso|americano|filter coffee|black coffee|cold brew/i.test(n)) {
    return `Espresso and black brewed coffee are certified Low FODMAP (up to 500ml). However, caffeine is a natural physiological gut stimulant and gastric acid trigger that can increase intestinal motility and provoke IBS cramping independent of FODMAPs.`;
  }
  if (/chai tea|chamomile|fennel tea|oolong/i.test(n)) {
    return `Strongly brewed chai, chamomile, and fennel teas are High in Fructans. Swap for peppermint tea, green tea, or weak black tea with lactose-free milk.`;
  }
  if (/peppermint tea|green tea|white tea|rooibos|ginger tea/i.test(n)) {
    return `Certified Low FODMAP. Peppermint oil and ginger additionally possess natural antispasmodic properties that help soothe visceral gut hypersensitivity.`;
  }

  // 3. Dairy & Cheeses
  if (/cheddar|parmesan|swiss|pecorino|manchego|gouda|edam|gruyere/i.test(n)) {
    return `Hard and aged cheeses undergo extended bacterial fermentation where nearly all lactose is converted to lactic acid (<0.1g lactose per serving), making them certified Low FODMAP.`;
  }
  if (/ricotta|cottage cheese|cream cheese|mascarpone|quark/i.test(n)) {
    return `Fresh unripened soft cheeses retain significant whey and lactose (2.0–4.0g lactose per 100g). Safe Low FODMAP portions are strictly limited to 2 tbsp (30g–40g) unless labeled 100% Lactose-Free.`;
  }
  if (/milk|yogurt|ice cream|custard/i.test(n) && !/lactose[- ]free|almond|soy|oat|coconut/i.test(n)) {
    return `Standard dairy milk and yogurt contain high concentrations of lactose (4.0–5.0g per 100ml). 1 cup is High FODMAP. Use Lactose-Free milk/yogurt or fortified almond milk as drop-in Low FODMAP swaps.`;
  }

  // 4. Meats, Seafood & Eggs
  if (/egg|eggs|chicken|beef|steak|pork|lamb|turkey|salmon|cod|tuna|prawn|shrimp/i.test(n) && !/breaded|battered|sauce/i.test(n)) {
    return `Animal proteins, poultry, fish, and eggs are pure proteins and fats containing 0g fermentable carbohydrates. They are naturally FODMAP-free and safe in unlimited culinary amounts (500g+). Note: High dietary fat can slow stomach emptying and trigger gut spasms in some individuals.`;
  }

  // 5. Oils, Fats & Condiments
  if (/oil|butter|ghee/i.test(n) && !/garlic|onion/i.test(n)) {
    return `Pure fats and oils contain 0g FODMAP carbohydrates and cannot ferment in the colon. Garlic-infused olive oil is also certified Low FODMAP because fructans are water-soluble, not fat-soluble.`;
  }

  // 6. Potatoes & Root Vegetables
  if (/sweet potato|yam|kumara|satsuma/i.test(n)) {
    return `${name} is certified Low FODMAP at 75g (approx. 1/2 cup cubed). Portions of 100g or more contain moderate to high amounts of mannitol. For unlimited root vegetable sides, standard potatoes (Russet, Yukon Gold, Maris Piper, Red, White) and carrots contain no detectable FODMAPs.`;
  }
  if (/potato|potatoes|hash brown|french fries|rosti|gnocchi/i.test(n) && !/sweet potato|yam|kumara/i.test(n)) {
    return `${name} (all standard potato varieties including Russet, Yukon Gold, Maris Piper, King Edward, Red, White, and New/Baby potatoes) contains pure starch with no detectable FODMAP carbohydrates. They are certified Low FODMAP and can be enjoyed freely.`;
  }

  // 7. Default from matched food notes or generic clinical message
  if (matchedFood?.notes && matchedFood.notes.length > 15) {
    return matchedFood.notes;
  }

  if (matchedFood?.rating === 'high') {
    return `${name} is High in ${matchedFood.primaryCulprit || 'fermentable carbohydrates'} at standard serving sizes. Consume within the safe limit (${matchedFood.safeGrams}g) or use suggested low-FODMAP alternatives.`;
  }

  if (matchedFood?.rating === 'moderate') {
    return `${name} is Moderate in FODMAPs (${matchedFood.primaryCulprit || 'fermentable carbohydrates'}). Keep portions within ${matchedFood.safeGrams}g for strict low-FODMAP compliance.`;
  }

  return `${name} is evaluated as Low FODMAP per clinical standard portion guidelines.`;
}

function isValidStandaloneAlias(alias: string): boolean {
  if (!alias || alias.length < 3) return false;
  const lower = alias.trim().toLowerCase();
  if (GENERIC_ADJECTIVES.has(lower)) return false;
  if (!lower.includes(' ') && GENERIC_ADJECTIVES.has(lower)) return false;
  return true;
}

const itemAliasesMemo = new Map<string, string[]>();

/**
 * Generates natural language permutations, inverted-comma variants, and alias tokens
 * for a FODMAP food item (e.g., "Sugar, brown" <-> "Brown sugar", "Oil, olive" <-> "Olive oil",
 * "Pepper, bell, red" <-> "Red bell pepper", "Onion, spring, green tops" <-> "Spring onion green tops",
 * "White / Brown / Caster Sugar (Sucrose)" -> ["white sugar", "brown sugar", "caster sugar", "sucrose"]).
 */
export function generateItemAliases(name: string): string[] {
  if (!name || typeof name !== 'string') return [];
  const cached = itemAliasesMemo.get(name);
  if (cached) return cached;

  const aliases = new Set<string>();
  const lower = name.toLowerCase().trim();
  aliases.add(lower);

  // 1. Without parentheses content: "White / Brown Sugar (Sucrose)" -> "white / brown sugar"
  const withoutParens = lower.replace(/\s*\(.*?\)/g, ' ').replace(/\s+/g, ' ').trim();
  if (withoutParens && isValidStandaloneAlias(withoutParens)) aliases.add(withoutParens);

  // 2. Parentheses contents: Only extract if not an ingredient list or descriptor
  const parensMatches = lower.matchAll(/\((.*?)\)/g);
  for (const m of parensMatches) {
    const inside = m[1].trim();
    if (!inside || IGNORED_PARENS_PREFIXES.test(inside)) continue;
    if (isValidStandaloneAlias(inside)) {
      aliases.add(inside);
    }
    inside.split(/[/,]/).forEach((sub) => {
      const cleanSub = sub.trim();
      if (cleanSub && !IGNORED_PARENS_PREFIXES.test(cleanSub) && isValidStandaloneAlias(cleanSub)) {
        aliases.add(cleanSub);
      }
    });
  }

  // 3. Comma inversions (Monash Inverted Nomenclature: "Category/Head, Subtype, Qualifier")
  if (lower.includes(',')) {
    const commaParts = lower.split(',').map((p) => p.trim()).filter(Boolean);
    if (commaParts.length === 2) {
      const [head, sub] = commaParts;
      const inverted = `${sub} ${head}`.trim();
      if (isValidStandaloneAlias(inverted)) aliases.add(inverted);
      const joined = `${head} ${sub}`.trim();
      if (isValidStandaloneAlias(joined)) aliases.add(joined);

      // Only add head as alias if sub is just a prep/form descriptor (e.g. 'Apples, raw' -> 'apples')
      if (PREPARATION_DESCRIPTORS.has(sub) && isValidStandaloneAlias(head)) {
        aliases.add(head);
      }
    } else if (commaParts.length === 3) {
      const [head, mid, tail] = commaParts;
      const inv1 = `${tail} ${mid} ${head}`.trim();
      const inv2 = `${mid} ${tail} ${head}`.trim();
      const inv3 = `${tail} ${head}`.trim();
      const inv4 = `${mid} ${head}`.trim();
      const jn1 = `${head} ${mid}`.trim();
      const jn2 = `${head} ${tail}`.trim();
      const jn3 = `${head} ${mid} ${tail}`.trim();
      [inv1, inv2, inv3, inv4, jn1, jn2, jn3].forEach((v) => {
        if (isValidStandaloneAlias(v)) aliases.add(v);
      });
      if (PREPARATION_DESCRIPTORS.has(mid) && PREPARATION_DESCRIPTORS.has(tail) && isValidStandaloneAlias(head)) {
        aliases.add(head);
      }
    } else if (commaParts.length > 3) {
      const inv = commaParts.slice().reverse().join(' ');
      if (isValidStandaloneAlias(inv)) aliases.add(inv);
      const jn = commaParts.join(' ');
      if (isValidStandaloneAlias(jn)) aliases.add(jn);
      const allDescriptors = commaParts.slice(1).every((p) => PREPARATION_DESCRIPTORS.has(p) || /raw|cooked|fresh|dried|whole/i.test(p));
      if (allDescriptors && isValidStandaloneAlias(commaParts[0])) {
        aliases.add(commaParts[0]);
      }
    }
  }

  // 4. Slash alternatives (e.g., "White / Brown / Caster Sugar", "Pumpkin Seeds / Pepitas")
  if (lower.includes('/')) {
    const slashParts = lower.split('/').map((p) => p.trim()).filter(Boolean);
    const lastPart = slashParts[slashParts.length - 1];
    const wordsInLast = lastPart.replace(/\s*\(.*?\)/g, '').trim().split(' ').filter(Boolean);
    const suffixNoun = wordsInLast.length > 1 ? wordsInLast.slice(1).join(' ') : '';

    for (const part of slashParts) {
      const cleanPart = part.replace(/\s*\(.*?\)/g, '').trim();
      if (cleanPart) {
        if (suffixNoun && !cleanPart.includes(suffixNoun)) {
          const combined = `${cleanPart} ${suffixNoun}`.trim();
          if (isValidStandaloneAlias(combined)) aliases.add(combined);
        }
        if (isValidStandaloneAlias(cleanPart)) {
          aliases.add(cleanPart);
        }
      }
    }
  }

  // 5. Cleaned punctuation variants
  const currentAliases = Array.from(aliases);
  for (const a of currentAliases) {
    const noPunct = a.replace(/[()[\]{}.,;:*•/\\|"'’#~!?]/g, ' ').replace(/\s+/g, ' ').trim();
    if (noPunct && isValidStandaloneAlias(noPunct)) aliases.add(noPunct);

    // Singular / Plural expansions
    if (a.endsWith('ies') && a.length > 4) {
      const singular = `${a.slice(0, -3)}y`;
      if (isValidStandaloneAlias(singular)) aliases.add(singular);
    } else if (a.endsWith('oes') && a.length > 4) {
      const singular = a.slice(0, -2);
      if (isValidStandaloneAlias(singular)) aliases.add(singular);
    } else if (a.endsWith('es') && !a.endsWith('ses') && !a.endsWith('ches') && !a.endsWith('shes') && a.length > 3) {
      const singular = a.slice(0, -2);
      if (isValidStandaloneAlias(singular)) aliases.add(singular);
    } else if (a.endsWith('s') && !a.endsWith('ss') && a.length > 3) {
      const singular = a.slice(0, -1);
      if (isValidStandaloneAlias(singular)) aliases.add(singular);
    } else if (a.endsWith('o') && a.length > 2) {
      const plural = `${a}es`;
      if (isValidStandaloneAlias(plural)) aliases.add(plural);
    } else if (!a.endsWith('s') && a.length > 2) {
      const plural = `${a}s`;
      if (isValidStandaloneAlias(plural)) aliases.add(plural);
    }
  }

  const result = Array.from(aliases);
  itemAliasesMemo.set(name, result);
  return result;
}

let aliasIndexBuilt = false;

export function ensureAliasIndex(): void {
  if (aliasIndexBuilt) return;
  aliasIndexBuilt = true;
  aliasToFoodItemMap.clear();

  // Index map by ID and Slug for fast lookup
  const itemByIdOrSlug = new Map<string, FodmapFoodItem>();
  for (const item of cachedFoodItems) {
    if (item.id) itemByIdOrSlug.set(item.id.toLowerCase(), item);
    if (item.slug) itemByIdOrSlug.set(item.slug.toLowerCase(), item);
  }

  // Step 1: Seed canonical staples first to protect fundamental everyday foods
  for (const [key, idOrSlug] of Object.entries(CANONICAL_STAPLE_MAPPINGS)) {
    const stapleItem = itemByIdOrSlug.get(idOrSlug.toLowerCase());
    if (stapleItem) {
      aliasToFoodItemMap.set(key.toLowerCase(), stapleItem);
    }
  }

  // Step 2: Index all cached food items
  for (const item of cachedFoodItems) {
    if (item.isHidden) continue;
    const aliases = generateItemAliases(item.name);
    for (const alias of aliases) {
      if (!aliasToFoodItemMap.has(alias)) {
        aliasToFoodItemMap.set(alias, item);
      }
    }
    if (item.id && !aliasToFoodItemMap.has(item.id.toLowerCase())) {
      aliasToFoodItemMap.set(item.id.toLowerCase(), item);
    }
    if (item.slug && !aliasToFoodItemMap.has(item.slug.toLowerCase())) {
      aliasToFoodItemMap.set(item.slug.toLowerCase(), item);
    }
    if ((item as any).synonyms && Array.isArray((item as any).synonyms)) {
      for (const syn of (item as any).synonyms) {
        const synLower = syn.toLowerCase().trim();
        if (synLower && !aliasToFoodItemMap.has(synLower)) {
          aliasToFoodItemMap.set(synLower, item);
        }
      }
    }
    if ((item as any).eNumber) {
      const eCode = (item as any).eNumber.toLowerCase().trim();
      aliasToFoodItemMap.set(eCode, item);
      aliasToFoodItemMap.set(`e ${eCode.replace(/^e/, '')}`, item);
      aliasToFoodItemMap.set(`e-${eCode.replace(/^e/, '')}`, item);
    }
  }
}

function rebuildAliasIndex(): void {
  aliasIndexBuilt = false;
  matchEvaluationCache.clear();
  ensureAliasIndex();
}

// Lazy background warmup when idle without blocking initial bundle evaluation
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => ensureAliasIndex());
  } else {
    setTimeout(() => ensureAliasIndex(), 50);
  }
}

let foodsLoadedAndMerged = false;

/**
 * Loads and merges full Monash raw ingredients dataset, core items, custom foods, and user overrides/hidden flags.
 */
export function loadAndMergeFoods(): FodmapFoodItem[] {
  const hiddenIds = new Set<string>();
  const overrides: Record<string, Partial<FodmapFoodItem>> = {};
  let customFoods: FodmapFoodItem[] = [];

  if (typeof window !== 'undefined') {
    try {
      // Purge legacy dataset caches if present
      const legacyKeys = ['digestlog_food_dataset_v1', 'digestlog_cached_foods_v1', 'digestlog_open_fodmap_dataset_v1', 'digestlog_raw_foods_v1'];
      for (const lk of legacyKeys) {
        if (localStorage.getItem(lk)) localStorage.removeItem(lk);
      }

      // Purge and sanitize hidden IDs
      const rawHidden = localStore.getHiddenFoodIds() || [];
      const cleanHidden = rawHidden.filter((id) => id && !id.startsWith('usda_') && !id.startsWith('core-'));
      if (cleanHidden.length !== rawHidden.length) {
        localStore.saveHiddenFoodIds(cleanHidden);
      }
      cleanHidden.forEach((id) => hiddenIds.add(id));

      // Purge and sanitize overrides
      const rawOverrides = localStore.getFoodOverrides() || {};
      const cleanOverrides: Record<string, Partial<FodmapFoodItem>> = {};
      let overridesChanged = false;
      for (const [k, v] of Object.entries(rawOverrides)) {
        if (k && !k.startsWith('usda_') && !k.startsWith('core-')) {
          cleanOverrides[k] = v;
        } else {
          overridesChanged = true;
        }
      }
      if (overridesChanged) {
        localStore.saveFoodOverrides(cleanOverrides);
      }
      Object.assign(overrides, cleanOverrides);

      // User Local Custom Foods (filter out legacy usda_ and core- custom foods)
      const rawCustom = localStore.getCustomFoods() || [];
      const cleanCustom = rawCustom.filter((c: any) => c && c.id && !c.id.startsWith('usda_') && !c.id.startsWith('core-'));
      if (cleanCustom.length !== rawCustom.length) {
        localStore.saveCustomFoods(cleanCustom);
      }

      const userCustomFoods = cleanCustom.map((c: any) => ({
        id: c.id,
        name: c.name,
        category: c.category || 'other',
        rating: c.rating || 'low',
        primaryCulprit: c.primaryCulprit,
        servings: c.safeGrams || c.safeServingTitle ? {
          low: {
            grams: c.safeGrams,
            title: c.safeServingTitle || (c.safeGrams ? `${c.safeGrams}g` : undefined),
          }
        } : undefined,
        notes: c.notes,
        swapSuggestion: c.swapSuggestion,
        isCustom: true,
        isHidden: hiddenIds.has(c.id),
      }));

      customFoods = userCustomFoods;
    } catch (e) {
      console.warn('Error reading food customizations:', e);
    }
  }

  // Fast path: If already merged and no custom foods/overrides/hidden items exist, return immediately
  if (foodsLoadedAndMerged && customFoods.length === 0 && Object.keys(overrides).length === 0 && hiddenIds.size === 0) {
    return cachedFoodItems;
  }

  // 1. Build Base Open Composition Items
  const hasCustomizations = customFoods.length > 0 || Object.keys(overrides).length > 0 || hiddenIds.size > 0;

  if (!hasCustomizations) {
    cachedFoodItems = [...CORE_FODMAP_DATABASE];
    foodsLoadedAndMerged = true;
    rebuildAliasIndex();
    return cachedFoodItems;
  }

  const normMap = new Map<string, FodmapFoodItem>();

  const getNormKey = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[()[\]{}.,;:*•/\\|"'’#~!?]/g, ' ')
      .replace(/\b(raw|cooked|fresh|dried|whole|mature|plain|unprepared|all varieties|variety|medium|large|small)\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  for (const rawItem of (OPEN_FODMAP_DATABASE as FodmapFoodItem[])) {
    const isHidden = hiddenIds.has(rawItem.id);
    const itemOverride = overrides[rawItem.id];
    const item: FodmapFoodItem = {
      ...rawItem,
      swapSuggestion: rawItem.swapSuggestion || generateDefaultSwap(rawItem.name, rawItem.rating, rawItem.fodmaps, rawItem.category),
      isHidden: itemOverride?.isHidden !== undefined ? itemOverride.isHidden : isHidden,
      ...(itemOverride || {}),
    };
    normMap.set(getNormKey(rawItem.name), item);
  }

  const uniqueBaseItems = Array.from(normMap.values());

  // 2. Combine with custom foods
  const existingIds = new Set(uniqueBaseItems.map((b) => b.id));
  const finalFoods = [...uniqueBaseItems];

  for (const cf of customFoods) {
    if (existingIds.has(cf.id)) {
      const idx = finalFoods.findIndex((f) => f.id === cf.id);
      if (idx !== -1) finalFoods[idx] = cf;
    } else {
      finalFoods.push(cf);
      existingIds.add(cf.id);
    }
  }

  cachedFoodItems = finalFoods;
  foodsLoadedAndMerged = true;
  rebuildAliasIndex();
  return cachedFoodItems;
}

// Listen for updates from other tabs or settings
if (typeof window !== 'undefined') {
  window.addEventListener('digestlog_foods_updated', () => {
    loadAndMergeFoods();
  });
}

/**
 * Legacy stub (now 100% offline local store)
 */
export async function syncGlobalSharedFoods(): Promise<number> {
  return 0;
}

/**
 * Automatically register newly discovered ingredients from AI scans into the local database
 */
export async function autoRegisterDiscoveredFoods(items: any[]): Promise<CustomFoodItem[]> {
  if (!Array.isArray(items) || items.length === 0) return [];

  const currentFoods = loadAndMergeFoods();
  const existingNames = new Set(currentFoods.map((f) => f.name.toLowerCase().trim()));

  const novelItems: CustomFoodItem[] = [];
  const customList = localStore.getCustomFoods() || [];
  const customNameSet = new Set(customList.map((c) => c.name.toLowerCase().trim()));

  for (const item of items) {
    if (!item) continue;
    const cleanName = (typeof item === 'string' ? item : item.name || item.title || '').trim();
    if (cleanName.length < 2 || /^(water|tap water|salt|table salt|sea salt|black pepper|pepper|cooking spray)$/i.test(cleanName)) {
      continue;
    }
    const lower = cleanName.toLowerCase();

    // Check if food already exists in base or custom
    const directMatches = searchFodmapDatabase(cleanName, 1);
    const hasExactMatch = directMatches.length > 0 && directMatches[0].name.toLowerCase() === lower;

    if (!hasExactMatch && !existingNames.has(lower) && !customNameSet.has(lower)) {
      const newFood: CustomFoodItem = {
        id: `custom-ai-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: cleanName,
        category: (item as any).category || 'other',
        rating: (item as any).rating || 'low',
        primaryCulprit: (item as any).primaryCulprit,
        safeGrams: (item as any).safeGrams || 50,
        safeServingTitle: (item as any).safeServingTitle || '50g',
        notes: (item as any).notes || 'Added from AI recipe breakdown',
        createdAtIso: new Date().toISOString(),
      };

      novelItems.push(newFood);
      customList.push(newFood);
      customNameSet.add(lower);
      existingNames.add(lower);
    }
  }

  if (novelItems.length > 0) {
    localStore.saveCustomFoods(customList);
    loadAndMergeFoods();
  }

  return novelItems;
}

let initFodmapPromise: Promise<FodmapFoodItem[]> | null = null;

/**
 * Initialize and populate the FODMAP database
 */
export async function initializeFodmapDatabase(): Promise<FodmapFoodItem[]> {
  if (initFodmapPromise) return initFodmapPromise;
  initFodmapPromise = (async () => {
    const foods = loadAndMergeFoods();
    ensureAliasIndex();
    return foods;
  })();
  return initFodmapPromise;
}

/**
 * Legacy sync stub (kept for backwards compatibility; now uses local curated store)
 */
export async function syncFullMonashDatabase(): Promise<number> {
  await syncGlobalSharedFoods();
  loadAndMergeFoods();
  return cachedFoodItems.length;
}

/**
 * Generate a smart swap recommendation based on food name and FODMAP breakdown
 */
function generateDefaultSwap(name: string, rating: FodmapRating, fodmaps?: FodmapCarbLevels, category?: string): string | undefined {
  if (rating === 'low') return undefined;

  const lower = name.toLowerCase();

  // 1. Beverages & Drinks
  if (/kombucha|kefir/i.test(lower)) return 'Water kefir, chilled peppermint tea, or sparkling water with citrus/berries.';
  if (/beer|lager|ale|stout/i.test(lower)) return 'Certified gluten-free beer, dry hard cider, or gin/vodka with sparkling soda and lime.';
  if (/juice|cider/i.test(lower)) return 'Fresh orange juice (max 125ml), cranberry juice, or infused water with mint/cucumber.';
  if (/coffee|latte|cappuccino|mocha/i.test(lower)) return 'Lactose-free flat white or unsweetened almond milk latte.';
  if (/tea|chai|chamomile|fennel/i.test(lower)) return 'Peppermint tea, green tea, ginger tea, or rooibos tea.';
  if (/smoothie/i.test(lower)) return 'Strawberry-banana smoothie made with lactose-free or almond milk.';
  if (category === 'beverages' || /drink|beverage/i.test(lower)) return 'Water kefir, green tea, peppermint tea, or sparkling water with citrus.';

  // 2. Alliums & Seasonings
  if (lower.includes('garlic')) return 'Garlic-infused olive oil or chives.';
  if (lower.includes('onion') || lower.includes('shallot') || lower.includes('leek')) return 'Green spring onion tops (scallion greens) or fresh chives.';

  // 3. Dairy & Lactose
  if (lower.includes('milk') || lower.includes('cream') || lower.includes('yogurt') || lower.includes('cheese') || lower.includes('ricotta')) return 'Lactose-free dairy alternative, hard cheddar, or unsweetened almond milk.';

  // 4. Grains, Bread & Pasta
  if (lower.includes('wheat') || lower.includes('bread') || lower.includes('pasta') || lower.includes('flour') || lower.includes('noodle')) return 'Authentic spelt sourdough bread, gluten-free pasta, or brown rice noodles.';

  // 5. Sweeteners
  if (lower.includes('honey') || lower.includes('agave') || lower.includes('hfcs') || lower.includes('corn syrup')) return '100% Pure maple syrup, rice malt syrup, or cane sugar.';

  // 6. Vegetables
  if (lower.includes('mushroom')) return 'Oyster mushrooms or rinsed canned champignons.';
  if (lower.includes('cauliflower')) return 'Broccoli florets (heads only, max 75g) or fine green beans.';
  if (lower.includes('celery')) return 'Cucumber ribbons, radishes, or bok choy.';

  // 7. Fruits
  if (lower.includes('apple') || lower.includes('pear')) return 'Strawberries, blueberries, or oranges.';
  if (lower.includes('watermelon') || lower.includes('mango') || lower.includes('cherry') || lower.includes('cherries')) return 'Cantaloupe / rockmelon (up to 120g), strawberries, or pineapple.';

  // 8. Legumes & Nuts
  if (lower.includes('bean') || lower.includes('lentil') || lower.includes('chickpea')) return 'Firm tofu, tempeh, or small rinsed canned lentils (¼ cup).';
  if (lower.includes('cashew') || lower.includes('pistachio')) return 'Walnuts, pecans, or pumpkin seeds.';

  // 9. Category-Informed Fallbacks
  if (category === 'fruits' || /fruit/i.test(category || '')) return 'Choose low-FODMAP fruits like strawberries, oranges, or blueberries.';
  if (category === 'vegetables' || /veg/i.test(category || '')) return 'Choose low-FODMAP vegetables like carrots, zucchini, or green beans.';
  if (category === 'dairy') return 'Choose a 100% lactose-free or plant-based equivalent.';
  if (category === 'grains' || category === 'bakery') return 'Swap for gluten-free grains or spelt sourdough.';

  if (fodmaps?.lactose && fodmaps.lactose >= 2) return 'Choose a 100% lactose-free or plant-based equivalent.';
  if (fodmaps?.fructans && fodmaps.fructans >= 2) return 'Swap for gluten-free grains or allium-free seasonings.';
  if (fodmaps?.sorbitol && fodmaps.sorbitol >= 2) return 'Choose low-sorbitol fruits like strawberries, citrus, or bananas.';
  if (fodmaps?.mannitol && fodmaps.mannitol >= 2) return 'Swap for low-mannitol options like oyster mushrooms or green beans.';

  return 'Consider a certified Low FODMAP alternative or reducing portion size.';
}

/**
 * Get all loaded FODMAP foods (excluding hidden foods unless includeHidden = true)
 */
export function getAllFodmapFoods(includeHidden = false): FodmapFoodItem[] {
  if (includeHidden) return cachedFoodItems;
  return cachedFoodItems.filter((f) => !f.isHidden);
}

/**
 * Returns all database foods with hidden and custom status for management UI
 */
export function getAllDatabaseFoods(includeHidden = true): FodmapFoodItem[] {
  if (includeHidden) return cachedFoodItems;
  return cachedFoodItems.filter((f) => !f.isHidden);
}

/**
 * Add or update a custom food item in storage
 */
export function saveCustomFoodItem(food: CustomFoodItem): void {
  const current = localStore.getCustomFoods();
  const existingIdx = current.findIndex((f) => f.id === food.id);
  let updated: CustomFoodItem[];
  if (existingIdx !== -1) {
    updated = [...current];
    updated[existingIdx] = food;
  } else {
    updated = [food, ...current];
  }
  localStore.saveCustomFoods(updated);
  loadAndMergeFoods();
}

/**
 * Delete a custom food item
 */
export function deleteCustomFoodItem(id: string): void {
  const current = localStore.getCustomFoods();
  const filtered = current.filter((f) => f.id !== id);
  localStore.saveCustomFoods(filtered);
  loadAndMergeFoods();
}

/**
 * Toggle whether a food is hidden from the logger and searches
 */
export function toggleFoodItemHidden(id: string, isHidden?: boolean): void {
  const current = localStore.getHiddenFoodIds();
  const set = new Set(current);
  const targetHidden = isHidden !== undefined ? isHidden : !set.has(id);
  if (targetHidden) {
    set.add(id);
  } else {
    set.delete(id);
  }
  localStore.saveHiddenFoodIds(Array.from(set));
  loadAndMergeFoods();
}

/**
 * Save an override for an existing food item
 */
export function saveFoodItemOverride(id: string, override: Partial<FodmapFoodItem>): void {
  const current = localStore.getFoodOverrides();
  current[id] = { ...(current[id] || {}), ...override };
  localStore.saveFoodOverrides(current);
  loadAndMergeFoods();
}

/**
 * Reset all custom foods, overrides, and hidden flags to default Monash database
 */
export function resetAllFoodOverridesAndCustom(): void {
  localStore.saveCustomFoods([]);
  localStore.saveHiddenFoodIds([]);
  localStore.saveFoodOverrides({});
  loadAndMergeFoods();
}

/**
 * Export all database foods (base items, overrides, and custom items) as standard CSV
 */
export function exportDatabaseToCsv(): string {
  const foods = getAllDatabaseFoods(true);
  const headers = [
    'id',
    'name',
    'category',
    'rating',
    'primaryCulprit',
    'standardServingGrams',
    'standardServingTitle',
    'safeGrams',
    'safeServingTitle',
    'moderateMaxGrams',
    'fructansG',
    'gosG',
    'excessFructoseG',
    'lactoseG',
    'sorbitolG',
    'mannitolG',
    'swapSuggestion',
    'notes',
    'isHidden',
  ];

  const escapeCsv = (val: any): string => {
    if (val === null || val === undefined) return '""';
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };

  const rows = foods.map((f) => {
    return [
      escapeCsv(f.id),
      escapeCsv(f.name),
      escapeCsv(f.category),
      escapeCsv(f.rating),
      escapeCsv(f.primaryCulprit || 'None'),
      f.servings?.standard?.grams ?? '',
      escapeCsv(f.servings?.standard?.title ?? ''),
      f.safeGrams ?? f.servings?.low?.grams ?? '',
      escapeCsv(f.servings?.low?.title ?? (f.safeGrams ? `${f.safeGrams}g` : '500g')),
      f.moderateMaxGrams ?? f.servings?.moderate?.grams ?? '',
      f.compositionPer100g?.fructansG ?? '',
      f.compositionPer100g?.gosG ?? '',
      f.compositionPer100g?.excessFructoseG ?? '',
      f.compositionPer100g?.lactoseG ?? '',
      f.compositionPer100g?.sorbitolG ?? '',
      f.compositionPer100g?.mannitolG ?? '',
      escapeCsv(f.swapSuggestion || ''),
      escapeCsv(f.notes || ''),
      f.isHidden ? 'true' : 'false',
    ].join(',');
  });

  return [headers.join(','), ...rows].join('\n');
}

/**
 * Parses and imports CSV rows to update existing foods or add custom foods
 */
export function importCsvToDatabase(csvText: string): { importedCount: number; errors: string[] } {
  if (!csvText || typeof csvText !== 'string') {
    return { importedCount: 0, errors: ['Empty CSV content'] };
  }

  const parseCsvLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    return values.map((v) => v.trim());
  };

  const lines = csvText.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { importedCount: 0, errors: ['CSV must have a header row and at least one data row'] };
  }

  const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/[^a-z0-9_]/g, ''));
  const getCol = (cols: string[], name: string): string => {
    const idx = header.indexOf(name.toLowerCase().replace(/[^a-z0-9_]/g, ''));
    return idx !== -1 && idx < cols.length ? cols[idx] : '';
  };

  const overrides = localStore.getFoodOverrides();
  const customList = localStore.getCustomFoods();
  const customMap = new Map<string, CustomFoodItem>(customList.map((c) => [c.id, c]));
  const hiddenIds = new Set(localStore.getHiddenFoodIds());

  let importedCount = 0;
  const errors: string[] = [];

  for (let lineIdx = 1; lineIdx < lines.length; lineIdx++) {
    try {
      const cols = parseCsvLine(lines[lineIdx]);
      const name = getCol(cols, 'name');
      if (!name) continue;

      let id = getCol(cols, 'id');
      if (!id) {
        id = `custom-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      }

      const category = getCol(cols, 'category') || 'other';
      const rawRating = getCol(cols, 'rating').toLowerCase();
      const rating: FodmapRating = rawRating === 'high' ? 'high' : rawRating === 'moderate' ? 'moderate' : 'low';
      const primaryCulprit = getCol(cols, 'primaryculprit') || undefined;
      const standardServingGrams = parseFloat(getCol(cols, 'standardservinggrams')) || undefined;
      const standardServingTitle = getCol(cols, 'standardservingtitle') || undefined;
      const safeGrams = parseFloat(getCol(cols, 'safegrams')) || undefined;
      const safeServingTitle = getCol(cols, 'safeservingtitle') || (safeGrams ? `${safeGrams}g` : undefined);
      const moderateMaxGrams = parseFloat(getCol(cols, 'moderatemaxgrams')) || undefined;
      const swapSuggestion = getCol(cols, 'swapsuggestion') || undefined;
      const notes = getCol(cols, 'notes') || undefined;
      const isHiddenStr = getCol(cols, 'ishidden').toLowerCase();
      const isHidden = isHiddenStr === 'true' || isHiddenStr === '1' || isHiddenStr === 'yes';

      const fructansG = parseFloat(getCol(cols, 'fructansg')) || undefined;
      const gosG = parseFloat(getCol(cols, 'gosg')) || undefined;
      const excessFructoseG = parseFloat(getCol(cols, 'excessfructoseg')) || undefined;
      const lactoseG = parseFloat(getCol(cols, 'lactoseg')) || undefined;
      const sorbitolG = parseFloat(getCol(cols, 'sorbitolg')) || undefined;
      const mannitolG = parseFloat(getCol(cols, 'mannitolg')) || undefined;

      const hasComposition = fructansG !== undefined || gosG !== undefined || excessFructoseG !== undefined || lactoseG !== undefined || sorbitolG !== undefined || mannitolG !== undefined;

      if (isHidden) {
        hiddenIds.add(id);
      } else {
        hiddenIds.delete(id);
      }

      // Check if it matches existing core food
      const isCoreFood = cachedFoodItems.some((f) => f.id === id && !f.isCustom);

      if (isCoreFood) {
        overrides[id] = {
          name,
          category,
          rating,
          primaryCulprit,
          servings: {
            standard: standardServingGrams || standardServingTitle ? {
              grams: standardServingGrams,
              title: standardServingTitle,
            } : undefined,
            low: {
              grams: safeGrams,
              title: safeServingTitle,
            },
            moderate: moderateMaxGrams ? {
              grams: moderateMaxGrams,
              title: `${moderateMaxGrams}g`,
            } : undefined,
          },
          compositionPer100g: hasComposition ? {
            fructansG,
            gosG,
            excessFructoseG,
            lactoseG,
            sorbitolG,
            mannitolG,
          } : undefined,
          swapSuggestion,
          notes,
          isHidden,
        };
      } else {
        const customItem: CustomFoodItem = {
          id,
          name,
          category,
          rating,
          primaryCulprit,
          safeGrams,
          safeServingTitle,
          notes,
          swapSuggestion,
          createdAtIso: customMap.get(id)?.createdAtIso || new Date().toISOString(),
        };
        customMap.set(id, customItem);
      }
      importedCount++;
    } catch (err: any) {
      errors.push(`Row ${lineIdx + 1}: ${err?.message || 'Parse error'}`);
    }
  }

  localStore.saveFoodOverrides(overrides);
  localStore.saveCustomFoods(Array.from(customMap.values()));
  localStore.saveHiddenFoodIds(Array.from(hiddenIds));
  loadAndMergeFoods();

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('digestlog_foods_updated'));
  }

  return { importedCount, errors };
}

export interface FoodCategoryDef {
  id: string;
  name: string;
  aliases: string[];
  icon: string;
  count?: number;
}

export const FOOD_CATEGORY_DEFINITIONS: FoodCategoryDef[] = [
  { id: 'vegetables', name: 'Vegetables & Greens', aliases: ['vegetables', 'veg', 'greens', 'salad'], icon: '🥦' },
  { id: 'fruits', name: 'Fruits & Berries', aliases: ['fruits', 'fruit', 'berries'], icon: '🍎' },
  { id: 'grains', name: 'Grains, Bread & Pasta', aliases: ['grains', 'bread', 'pasta', 'bakery', 'cereal'], icon: '🍞' },
  { id: 'dairy', name: 'Dairy & Plant Alternatives', aliases: ['dairy', 'milk', 'cheese', 'yogurt'], icon: '🥛' },
  { id: 'proteins', name: 'Meat, Seafood & Eggs', aliases: ['meats', 'meat', 'seafood', 'fish', 'poultry', 'eggs', 'protein'], icon: '🥩' },
  { id: 'legumes', name: 'Legumes, Beans & Tofu', aliases: ['legumes', 'beans', 'pulses', 'tofu'], icon: '🫘' },
  { id: 'nuts', name: 'Nuts & Seeds', aliases: ['nuts', 'seeds'], icon: '🥜' },
  { id: 'oils-condiments', name: 'Oils, Condiments & Herbs', aliases: ['oils', 'oil', 'condiments', 'sauces', 'herbs', 'spices'], icon: '🫒' },
  { id: 'beverages', name: 'Beverages & Drinks', aliases: ['beverages', 'drinks', 'tea', 'coffee'], icon: '☕' },
  { id: 'sweeteners-snacks', name: 'Sweeteners & Snacks', aliases: ['sweeteners', 'snacks', 'baking', 'desserts', 'other'], icon: '🍫' },
];

export function getFoodCategoriesList(): { id: string; name: string; count: number; icon: string }[] {
  return FOOD_CATEGORY_DEFINITIONS.map((cat) => {
    const foods = getFoodsByFoodCategory(cat.id);
    return {
      id: cat.id,
      name: cat.name,
      count: foods.length,
      icon: cat.icon,
    };
  }).filter((cat) => cat.count > 0);
}

export function getFoodsByFoodCategory(categoryId: string, includeHidden = false): FodmapFoodItem[] {
  const catDef = FOOD_CATEGORY_DEFINITIONS.find((c) => c.id === categoryId);
  if (!catDef) return [];

  const sourceList = includeHidden ? cachedFoodItems : cachedFoodItems.filter((f) => !f.isHidden);
  const matched = sourceList.filter((item) => {
    const itemCat = (item.category || '').toLowerCase().trim();
    return catDef.aliases.some((alias) => itemCat.includes(alias) || alias.includes(itemCat));
  });

  return matched.sort((a, b) => a.name.localeCompare(b.name));
}

export interface FodmapSubtypeDef {
  id: string;
  name: string;
  chemicalName: string;
  description: string;
  commonExamples: string;
  color: string;
}

export const FODMAP_SUBTYPE_DEFINITIONS: FodmapSubtypeDef[] = [
  {
    id: 'fructans',
    name: 'Fructans',
    chemicalName: 'Oligosaccharides',
    description: 'Found in alliums, wheat, rye, barley, onions, garlic, and inulin prebiotics.',
    commonExamples: 'Garlic, Onions, Wheat, Leeks, Shallots, Spring Onion Bulbs',
    color: '#E11D48',
  },
  {
    id: 'lactose',
    name: 'Lactose',
    chemicalName: 'Disaccharides',
    description: 'Milk sugar found in dairy products, requiring lactase enzyme for absorption.',
    commonExamples: 'Cow Milk, Ice Cream, Soft Cheese, Ricotta, Yogurt, Heavy Cream',
    color: '#2563EB',
  },
  {
    id: 'fructose',
    name: 'Excess Fructose',
    chemicalName: 'Monosaccharides',
    description: 'Free fructose in excess of glucose, overwhelming intestinal GLUT5 transport.',
    commonExamples: 'Honey, Mango, Apples, Pears, Agave Nectar, High-Fructose Corn Syrup',
    color: '#D97706',
  },
  {
    id: 'sorbitol',
    name: 'Sorbitol',
    chemicalName: 'Polyols (Sugar Alcohols)',
    description: 'Naturally present in stone fruits, avocado, and used in sugar-free gums/mints.',
    commonExamples: 'Avocado, Blackberries, Peaches, Plums, Apples, Sugar-free Mints',
    color: '#7C3AED',
  },
  {
    id: 'mannitol',
    name: 'Mannitol',
    chemicalName: 'Polyols (Sugar Alcohols)',
    description: 'Sugar alcohol found in mushrooms, cauliflower, snow peas, and sweet potato.',
    commonExamples: 'Button Mushrooms, Portobello, Cauliflower, Snow Peas, Sweet Potato',
    color: '#059669',
  },
  {
    id: 'gos',
    name: 'GOS (Galacto-oligosaccharides)',
    chemicalName: 'Oligosaccharides',
    description: 'Fermentable oligosaccharides found in legumes, beans, pulses, and some nuts.',
    commonExamples: 'Chickpeas, Lentils, Kidney Beans, Baked Beans, Cashews, Pistachios',
    color: '#EA580C',
  },
];

export function getFodmapSubtypesList(): (FodmapSubtypeDef & { count: number })[] {
  return FODMAP_SUBTYPE_DEFINITIONS.map((sub) => {
    const foods = getFoodsByFodmapSubtype(sub.id);
    return {
      ...sub,
      count: foods.length,
    };
  });
}

export function getFoodsByFodmapSubtype(subtypeId: string, includeHidden = false): FodmapFoodItem[] {
  const cleanId = subtypeId.toLowerCase().trim();
  const sourceList = includeHidden ? cachedFoodItems : cachedFoodItems.filter((f) => !f.isHidden);

  return sourceList.filter((item) => {
    // 1. Check item.fodmaps
    if (item.fodmaps) {
      if (cleanId === 'fructans' && item.fodmaps.fructans >= 2) return true;
      if (cleanId === 'lactose' && item.fodmaps.lactose >= 2) return true;
      if (cleanId === 'fructose' && item.fodmaps.fructose >= 2) return true;
      if (cleanId === 'sorbitol' && item.fodmaps.sorbitol >= 2) return true;
      if (cleanId === 'mannitol' && item.fodmaps.mannitol >= 2) return true;
      if (cleanId === 'gos' && item.fodmaps.gos >= 2) return true;
    }

    // 2. Check notes, swap suggestion, or name heuristics
    const notesAndText = `${item.name} ${item.notes || ''} ${item.swapSuggestion || ''}`.toLowerCase();
    if (cleanId === 'fructans' && (/fructan|allium|garlic|onion|shallot|leek|wheat|rye|barley/i.test(notesAndText))) return true;
    if (cleanId === 'lactose' && (/lactose|milk|dairy|cream|ricotta|yogurt/i.test(notesAndText) && !/lactose-free/i.test(item.name))) return true;
    if (cleanId === 'fructose' && (/fructose|honey|agave|mango|apple|pear/i.test(notesAndText))) return true;
    if (cleanId === 'sorbitol' && (/sorbitol|avocado|blackberry|blackberries|peach|plum|cherry|cherries/i.test(notesAndText))) return true;
    if (cleanId === 'mannitol' && (/mannitol|mushroom|champignon|cauliflower|snow pea|sweet potato/i.test(notesAndText))) return true;
    if (cleanId === 'gos' && (/gos|galacto|legume|chickpea|lentil|kidney bean|cashew|pistachio/i.test(notesAndText))) return true;

    return false;
  }).sort((a, b) => {
    // High rating first, then alphabetical
    const ratingOrder: Record<string, number> = { high: 0, moderate: 1, low: 2 };
    const rA = ratingOrder[a.rating] ?? 3;
    const rB = ratingOrder[b.rating] ?? 3;
    if (rA !== rB) return rA - rB;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Search the FODMAP database by text query
 */
export function searchFodmapDatabase(query: string, maxResults = 12, includeHidden = false): FodmapFoodItem[] {
  ensureAliasIndex();
  const sourceList = includeHidden ? cachedFoodItems : cachedFoodItems.filter((f) => !f.isHidden);
  const clean = query.trim().toLowerCase();
  if (!clean) return sourceList.slice(0, maxResults);

  const cleanNoPunct = clean.replace(/[()[\]{}.,;:*•/\\|"'’#~!?]/g, ' ').replace(/\s+/g, ' ').trim();
  const exact: FodmapFoodItem[] = [];
  const startsWith: FodmapFoodItem[] = [];
  const contains: FodmapFoodItem[] = [];
  const seenIds = new Set<string>();

  // Check direct canonical staple first
  const canonicalStaple = aliasToFoodItemMap.get(clean) || aliasToFoodItemMap.get(cleanNoPunct);
  if (canonicalStaple && !seenIds.has(canonicalStaple.id)) {
    exact.push(canonicalStaple);
    seenIds.add(canonicalStaple.id);
  }

  for (const item of sourceList) {
    if (seenIds.has(item.id)) continue;
    const aliases = generateItemAliases(item.name);
    const isExact = aliases.some((a) => a === clean || a === cleanNoPunct || item.name.toLowerCase() === clean || item.slug === clean);
    const isStartsWith = !isExact && aliases.some((a) => a.startsWith(clean) || a.startsWith(cleanNoPunct) || item.name.toLowerCase().startsWith(clean));
    const isContains = !isExact && !isStartsWith && aliases.some((a) => a.includes(clean) || a.includes(cleanNoPunct) || item.name.toLowerCase().includes(clean) || (item.slug && item.slug.includes(clean)));

    if (isExact && !seenIds.has(item.id)) {
      exact.push(item);
      seenIds.add(item.id);
    } else if (isStartsWith && !seenIds.has(item.id)) {
      startsWith.push(item);
      seenIds.add(item.id);
    } else if (isContains && !seenIds.has(item.id)) {
      contains.push(item);
      seenIds.add(item.id);
    }
  }

  const sortSimpleFirst = (a: FodmapFoodItem, b: FodmapFoodItem) => {
    const aIsMeal = /prepared meal|ready meal/i.test(a.name);
    const bIsMeal = /prepared meal|ready meal/i.test(b.name);
    if (aIsMeal !== bIsMeal) return aIsMeal ? 1 : -1;
    return a.name.length - b.name.length;
  };

  startsWith.sort(sortSimpleFirst);
  contains.sort(sortSimpleFirst);

  return [...exact, ...startsWith, ...contains].slice(0, maxResults);
}

const matchEvaluationCache = new Map<string, IngredientEvaluation>();

export function clearMatchEvaluationCache(): void {
  matchEvaluationCache.clear();
}

export function matchIngredientToFodmap(rawIngredient: string): IngredientEvaluation {
  if (!rawIngredient || typeof rawIngredient !== 'string') {
    return { raw: String(rawIngredient || ''), rating: 'low' };
  }
  const cached = matchEvaluationCache.get(rawIngredient);
  if (cached) return cached;

  const result = _doMatchIngredientToFodmap(rawIngredient);
  if (!result.clinicalGuidance) {
    result.clinicalGuidance = getClinicalGuidanceForFood(
      result.raw,
      result.matchedFood?.category,
      result.matchedFood
    );
  }
  if (matchEvaluationCache.size > 3000) {
    matchEvaluationCache.clear();
  }
  matchEvaluationCache.set(rawIngredient, result);
  return result;
}

function _doMatchIngredientToFodmap(rawIngredient: string): IngredientEvaluation {
  ensureAliasIndex();
  const canonical = canonicalizeIngredient(rawIngredient);
  const canonLower = canonical ? canonical.toLowerCase() : '';
  const clean = rawIngredient.trim().toLowerCase();
  const cleanNoPunct = clean.replace(/[()[\]{}.,;:*•/\\|"'’#~!?]/g, ' ').replace(/\s+/g, ' ').trim();

  // 0. Direct match against Inverted Alias Index & Cached Foods (handles "Brown sugar" <-> "Sugar, brown", "Olive oil" <-> "Oil, olive", etc.)
  const directAliasMatch =
    aliasToFoodItemMap.get(clean) ||
    aliasToFoodItemMap.get(cleanNoPunct) ||
    (canonLower ? aliasToFoodItemMap.get(canonLower) : undefined) ||
    cachedFoodItems.find(
      (i) =>
        i.name.toLowerCase() === clean ||
        (canonLower && i.name.toLowerCase() === canonLower) ||
        i.id.toLowerCase() === clean
    );

  if (directAliasMatch) {
    const culprit = derivePrimaryCulprit(directAliasMatch.fodmaps);
    return {
      raw: rawIngredient,
      matchedFood: directAliasMatch,
      rating: directAliasMatch.rating,
      primaryCulprit: culprit,
      swapSuggestion: directAliasMatch.swapSuggestion,
      servingLimitNote: directAliasMatch.servings?.low?.title
        ? `Low at ${directAliasMatch.servings.low.title}${directAliasMatch.servings.low.grams ? ` (${directAliasMatch.servings.low.grams}g)` : ''}`
        : directAliasMatch.notes,
    };
  }

  // 1. Explicit Certified Safe Low-FODMAP Exceptions & Plant Parts (GREEN)
  if (/banana.*(unripe|firm|green\s*tips?)|(unripe|firm).*banana/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-banana-unripe');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'Fructans',
      servingLimitNote: 'Safe Low FODMAP (1 medium firm banana / 100g) • High resistant starch',
    };
  }

  if (/banana.*(ripe|yellow|spots|brown)|(ripe|brown).*banana/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-banana-ripe');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'high',
      primaryCulprit: 'Fructans, Excess Fructose',
      servingLimitNote: 'High FODMAP at 100g • Low at 35g (1/3 banana)',
      swapSuggestion: 'Unripe green bananas or firm bananas with green tips (which are Low FODMAP)',
    };
  }

  if (/^bananas?$/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-banana-unripe');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'Fructans',
      servingLimitNote: 'Safe limit: 1 medium firm banana (100g)',
    };
  }

  if (/almond butter/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-almond-butter');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP up to 1 tbsp (20g) • Moderate GOS above 32g',
      swapSuggestion: 'Peanut butter or sunflower seed butter',
    };
  }

  if (/garlic[- ]infused|infused.*garlic|garlic.*oil/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-garlic-infused-oil');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe (FODMAP-free) • Fructans cannot dissolve into pure oil',
    };
  }

  if (
    /spring.*(green|tops|leaves)|scallion.*(green|tops|leaves)|green.*(spring\s*onion|scallion|tops|leaves)|onion.*spring.*green.*tops/i.test(
      clean
    ) &&
    !/white.*bulb|bulb.*only|head.*only|whites?\b/i.test(clean)
  ) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-spring-onion-greens');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (up to 75g) • Green leaves contain no fructans, unlike white bulbs',
    };
  }

  if (/chives?/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-chives');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (up to 4g) • Monash certified allium alternative',
    };
  }

  if (/leek (green|leaves|tops)/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-leek-leaves');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (up to 54g) • Green leaves only (white stem has fructans)',
    };
  }

  if (/oyster mushroom|canned.*champignon|rinsed.*canned.*mushroom/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'usda_168580' || i.id === 'core-oyster-mushrooms');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (up to 75g) • Safe low-mannitol mushroom',
    };
  }

  if (/lactose[- ]free/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP • Lactase enzyme treated',
    };
  }

  if (/gluten[- ]free/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP grain alternative',
    };
  }

  if (/sourdough/i.test(clean) && !/high fructan|baker'?s yeast/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      swapSuggestion: 'Authentic slow-fermented sourdough is Low FODMAP',
      servingLimitNote: 'Safe Low FODMAP (up to 2 slices / 52g) • Fermentation degrades fructans',
    };
  }

  if (/\b(almond milk|macadamia milk|rice milk|coconut milk|coconut cream)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP dairy alternative',
    };
  }

  if (/\b(olive oil|extra virgin olive oil|vegetable oil|sunflower oil|canola oil|sesame oil|avocado oil)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe (Naturally FODMAP-free pure lipid)',
    };
  }

  if (/firm tofu|extra firm tofu|tempeh/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (up to 170g) • Water-pressing removes GOS',
    };
  }

  // Seeds & Kernels
  if (/flax|linseed/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-flaxseeds');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Safe Low FODMAP up to 15g (1 tbsp) • Moderate GOS above 15g',
    };
  }

  if (/chia/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-chia-seeds');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Safe Low FODMAP up to 24g (2 tbsp)',
    };
  }

  if (/sunflower seed/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-sunflower-seeds');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Safe Low FODMAP up to 30g (2 tbsp)',
    };
  }

  if (/pumpkin seed|pepita/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-pumpkin-seeds');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Safe Low FODMAP up to 23g (2 tbsp)',
    };
  }

  if (/sesame seed/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-sesame-seeds');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Safe Low FODMAP up to 11g (1 tbsp)',
    };
  }

  if (/hemp seed/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-hemp-seeds');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Safe Low FODMAP up to 20g (2 tbsp)',
    };
  }

  // 2. Strict Monash High-FODMAP Culprits (RED / AVOID)
  // Alliums & Garlic
  if (/\b(garlic|garlic cloves?|crushed garlic|minced garlic|garlic powder|garlic salt|allium)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'Fructans',
      servingLimitNote: 'Avoid in recipes • Very high in fructans (Swap for Garlic-Infused Oil)',
      swapSuggestion: 'Garlic-infused olive oil',
    };
  }

  // Onions, Shallots, Leek Bulbs, Spring Onion Heads/Bulbs
  if (
    /\b(onions?|red onions?|white onions?|yellow onions?|brown onions?|shallots?|leek bulbs?|onion powder|onion salt|diced onions?|scallion bulbs?|spring onion heads?|spring onion bulbs?|spring onion whites?|scallion whites?|scallion heads?)\b/i.test(clean) ||
    /onion.*spring.*white.*bulb|spring.*(head|bulb|white)|scallion.*(head|bulb|white)/i.test(clean)
  ) {
    // Exclude green tops
    if (!/green\s*tops?|scallion\s*greens?|green\s*spring\s*onion|green\s*leaves/i.test(clean)) {
      const isSpringOnionHead = /spring.*(head|bulb|white)|scallion.*(head|bulb|white)|onion.*spring.*white.*bulb/i.test(clean);
      const matched = isSpringOnionHead
        ? cachedFoodItems.find((i) => i.id === 'core-spring-onion-bulb')
        : cachedFoodItems.find((i) => i.id === 'core-onion');
      return {
        raw: rawIngredient,
        matchedFood: matched,
        rating: 'high',
        primaryCulprit: 'Fructans',
        servingLimitNote: 'Avoid in recipes • Very high in fructans (Swap for Spring Onion Green Tops)',
        swapSuggestion: 'Spring onion green tops (scallion greens) or fresh chives',
      };
    }
  }

  // Conventional Mushrooms
  if (/\b(mushrooms?|button mushrooms?|cremini|portobello|shiitake)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'Mannitol',
      servingLimitNote: 'Avoid in recipes • High in mannitol (Swap for Oyster Mushrooms)',
      swapSuggestion: 'Oyster mushrooms or rinsed canned champignons',
    };
  }

  // Polyols & Sugar Alcohol Humectants
  if (/\b(sorbitol|mannitol|xylitol|isomalt|maltitol|e420|e421|e953|e965|e967)\b/i.test(clean) || /humectant.*(sorbitol|mannitol|polyol)/i.test(clean)) {
    const isMannitol = /mannitol|e421/i.test(clean);
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: isMannitol ? 'Mannitol' : 'Sorbitol',
      servingLimitNote: 'High Polyols • Synthetic/extracted polyol humectant (triggers osmotic draw)',
      swapSuggestion: 'Cane sugar, pure maple syrup, or glucose/dextrose',
    };
  }

  // Prebiotic Fibers & Inulin
  if (/\b(inulin|chicory root|chicory root extract|fructo[- ]oligosaccharides|fos|oligofructose)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'Fructans',
      servingLimitNote: 'High Fructans • Concentrated prebiotic fiber highly fermentable by gut bacteria',
      swapSuggestion: 'Dextrose, pure sugar, or corn starch',
    };
  }

  // Non-Wheat Flours & Safe Grains (Maize / Corn / Rice / Tapioca / Cassava / Buckwheat / Sorghum / Millet)
  if (/\b(maize\s*flour|corn\s*flour|cornmeal|polenta|masa\s*harina|cornstarch|corn\s*starch)\b/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-maize-flour');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (100g / ⅔ cup) • Naturally fructan-free corn grain',
    };
  }

  if (/\b(rice\s*flour|white\s*rice\s*flour|brown\s*rice\s*flour|sweet\s*rice\s*flour|glutinous\s*rice\s*flour|rice\s*starch)\b/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-rice-flour');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (100g / ⅔ cup) • Naturally fructan-free rice grain',
    };
  }

  if (/\b(tapioca\s*flour|tapioca\s*starch|cassava\s*flour|arrowroot|arrowroot\s*flour|arrowroot\s*starch)\b/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-tapioca-flour' || i.name.toLowerCase().includes('tapioca flour'));
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (100g / ⅔ cup) • Naturally fructan-free root starch',
    };
  }

  if (/\b(buckwheat|buckwheat\s*flour|buckwheat\s*groats|buckwheat\s*noodles?|soba|kasha)\b/i.test(clean)) {
    const matched = cachedFoodItems.find((i) =>
      i.id === 'core-buckwheat-flour' ||
      i.id === 'core-buckwheat-groats-raw' ||
      i.id === 'core-buckwheat-noodles-soba' ||
      i.name.toLowerCase().includes('buckwheat flour') ||
      i.name.toLowerCase() === 'buckwheat'
    );
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (100g / ⅔ cup) • Naturally fructan-free pseudocereal (~0.18g fructans/100g)',
    };
  }

  if (/\b(sorghum\s*flour|millet\s*flour|teff\s*flour|quinoa\s*flour)\b/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.name.toLowerCase().includes(clean));
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (100g / ⅔ cup)',
    };
  }

  if (/\b(almond\s*flour|almond\s*meal)\b/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-almond-flour');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'moderate',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Caution • Safe limit 24g (2 tbsp); Moderate in GOS at 40g+ (½ cup)',
      swapSuggestion: 'Gluten-free 1-to-1 baking flour or rice flour',
    };
  }

  if (/\b(coconut\s*flour)\b/i.test(clean)) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-coconut-flour');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'moderate',
      primaryCulprit: 'Fructans & Sorbitol',
      servingLimitNote: 'Caution • Safe limit 15g (1 tbsp); High in Fructans/Sorbitol at 30g+ (¼ cup)',
      swapSuggestion: 'Gluten-free 1-to-1 baking flour or tapioca flour',
    };
  }

  // Wheat & High Fructan Grains, Batters, and Crumbs
  if (
    /\b(wheat bread|white bread|baker'?s bread|wheat flour|all[- ]purpose flour|plain flour|wheat pasta|spaghetti|semolina|couscous|barley|rye|batter|breadcrumbs?|crumbs?|predust|panko)\b/i.test(clean) &&
    !/gluten[- ]free|rice|tapioca|maize|corn|buckwheat|almond|coconut|oat|sorghum|millet|cassava/i.test(clean)
  ) {
    const matched = cachedFoodItems.find((i) => i.id === 'core-wheat-flour');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'high',
      primaryCulprit: 'Fructans',
      servingLimitNote: 'Avoid • High in wheat fructans (Swap for Gluten-Free or Sourdough)',
      swapSuggestion: 'Gluten-free alternative or slow-fermented spelt sourdough',
    };
  }

  // Seaweeds & Algae
  if (/\b(nori|nori seaweed|wakame|wakame seaweed|seaweed|spirulina)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (Nori safe up to 5g / 2 sheets)',
    };
  }

  // Wasabi & Horseradish & Mustard
  if (/\b(wasabi|wasabi japonica|horseradish|mustard|mustard oil|mustard seeds?)\b/i.test(clean) && !/sorbitol/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (Pungent heat comes from isothiocyanates, not FODMAPs)',
    };
  }

  // Surimi / Crab / Seafood / Sashimi / Nigiri
  if (/\b(surimi|crab meat|crab extract|crab salad|ebi|prawn nigiri|salmon nigiri|tuna nigiri|sashimi|raw salmon|raw tuna)\b/i.test(clean) && !/sorbitol/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe (0g FODMAPs pure seafood protein)',
    };
  }

  // Safe Starches & Syrups & Vinegars
  if (/\b(tapioca starch|corn starch|cornstarch|potato starch|rice starch|glucose syrup|dextrose|maltodextrin|rice vinegar|spirit vinegar|white vinegar|distilled vinegar)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Low FODMAP (0g FODMAPs, pure starch/glucose/acetic acid)',
    };
  }

  // Conventional High-Lactose Dairy
  if (
    /\b(cow'?s? milk|whole milk|skimmed milk|semi-skimmed milk|heavy cream|double cream|whipping cream|sour cream|ice cream|custard|whey|ricotta|mascarpone|cottage cheese|yogurt|yoghurt|greek yogurt|regular yogurt)\b/i.test(clean) &&
    !/lactose[- ]free|coconut yogurt|almond yogurt|oat yogurt/i.test(clean)
  ) {
    const matched = /yogurt|yoghurt/i.test(clean)
      ? cachedFoodItems.find((i) => i.id === 'core-yogurt')
      : cachedFoodItems.find((i) => i.id === 'core-milk');
    return {
      raw: rawIngredient,
      matchedFood: matched,
      rating: 'high',
      primaryCulprit: 'Lactose',
      servingLimitNote: /yogurt|yoghurt/i.test(clean)
        ? 'High Lactose • Safe up to 1 tbsp (20g); High at 3 tbsp (60g+)'
        : 'Avoid • High in lactose (Swap for Lactose-Free)',
      swapSuggestion: 'Lactose-free milk/yogurt or almond/coconut alternative',
    };
  }

  // High Fructose Sweeteners & Fruits
  if (/\b(honey|agave|high fructose corn syrup|hfcs)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'Excess Fructose',
      servingLimitNote: 'Avoid • High in excess fructose (Swap for 100% Pure Maple Syrup)',
      swapSuggestion: '100% Pure maple syrup or cane sugar',
    };
  }
  if (/\b(apples?|pears?|mangoes?|mango|watermelons?|watermelon)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'Excess Fructose & Sorbitol',
      servingLimitNote: 'Avoid • High in fructose and polyols (Swap for Strawberries)',
      swapSuggestion: 'Strawberries, blueberries, or oranges',
    };
  }

  // Pulses & High GOS Beans
  if (/\b(kidney beans?|black beans?|baked beans?|split peas?|soybeans?)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Avoid • Very high in Galacto-oligosaccharides (Swap for Firm Tofu)',
      swapSuggestion: 'Firm tofu or small portion rinsed canned lentils (¼ cup)',
    };
  }

  // High GOS/Fructan Nuts
  if (/\b(cashews?|pistachios?)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'GOS & Fructans',
      servingLimitNote: 'Avoid • High in GOS and fructans (Swap for Walnuts)',
      swapSuggestion: 'Walnuts, macadamias, or peanuts',
    };
  }

  // Cauliflower
  if (/\b(cauliflower)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'high',
      primaryCulprit: 'Mannitol',
      servingLimitNote: 'Avoid • Very high in mannitol (Swap for Broccoli Florets)',
      swapSuggestion: 'Broccoli florets (heads only, max 75g)',
    };
  }

  // 3. Known Moderate FODMAP Ingredients (AMBER / CAUTION)
  if (/\b(avocado|guacamole)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      primaryCulprit: 'Polyols (Perseitol)',
      servingLimitNote: 'Safe Low FODMAP at 60g (~1/3 avocado) • Moderate at 80g • High in Polyols (Perseitol) at 90g+',
      swapSuggestion: 'Safe up to 60g serving portion',
    };
  }
  if (/\b(almonds?)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'moderate',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Caution • Low at 10 nuts (12g); moderate in larger servings',
      swapSuggestion: 'Limit to 10 almonds or swap for walnuts',
    };
  }
  if (/\b(broccoli|broccoli florets)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'moderate',
      primaryCulprit: 'Fructans',
      servingLimitNote: 'Caution • Low at ¾ cup florets (75g); stalks/stems are High',
      swapSuggestion: 'Eat florets/heads only (max 75g)',
    };
  }
  if (/\b(green bell pepper|green pepper)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'moderate',
      primaryCulprit: 'Sorbitol',
      servingLimitNote: 'Caution • Low at ½ cup (52g); swap for red bell pepper',
      swapSuggestion: 'Use red or orange bell pepper',
    };
  }
  if (/\b(canned chickpeas|chickpeas|canned lentils|lentils)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'moderate',
      primaryCulprit: 'GOS',
      servingLimitNote: 'Caution • Low at ¼ cup (42g) thoroughly rinsed',
      swapSuggestion: 'Rinse thoroughly under water and limit to ¼ cup',
    };
  }

  // 4. Common Naturally Safe Low-FODMAP Staples & Pure Proteins (GREEN)
  if (
    /\b(chicken|chicken breast|chicken thigh|poultry|turkey|beef|steak|mince|ground beef|pork|bacon|ham|lamb|duck|salmon|tuna|cod|fish|prawn|prawns|shrimp|eggs?|egg whites?|egg yolks?)\b/i.test(
      clean
    )
  ) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe (Naturally FODMAP-free pure protein)',
    };
  }
  if (/\b(rice|arborio rice|basmati rice|jasmine rice|white rice|brown rice|quinoa|polenta|oats|rolled oats|cornstarch|potato starch|tapioca)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Monash Low FODMAP staple',
    };
  }
  if (/\b(butter|ghee|parmesan|parmesan cheese|cheddar|cheddar cheese|swiss cheese|mozzarella|feta|feta cheese)\b/i.test(clean)) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe (Naturally low in lactose <1g)',
    };
  }
  if (
    /\b(carrots?|cucumber|cucumbers?|tomatoes?|canned tomatoes?|spinach|baby spinach|zucchini|courgette|lettuce|arugula|rocket|kale|ginger|turmeric|salt|black pepper|white pepper|white wine|red wine|soy sauce|tamari|mustard|mayo|mayonnaise)\b/i.test(
      clean
    )
  ) {
    return {
      raw: rawIngredient,
      rating: 'low',
      servingLimitNote: 'Safe Monash Low FODMAP staple',
    };
  }

  // 5. Fast N-Gram / Phrase lookup against Inverted Alias Index (O(1) lookups)
  const words = cleanNoPunct.split(/\s+/).filter(Boolean);
  for (let len = Math.min(4, words.length); len >= 1; len--) {
    for (let i = 0; i <= words.length - len; i++) {
      const phrase = words.slice(i, i + len).join(' ');
      if (phrase.length < 3 || GENERIC_ADJECTIVES.has(phrase)) continue;

      const item = aliasToFoodItemMap.get(phrase);
      if (item && !item.isHidden) {
        if (/unripe|firm|green/i.test(clean) && /ripe|yellow|spots/i.test(phrase)) continue;
        if (/ripe|yellow|spots/i.test(clean) && /unripe|firm|green/i.test(phrase)) continue;
        if (/green\s*tops?|scallion\s*greens?/i.test(clean) && /white|bulb|head/i.test(phrase)) continue;
        if (/white|bulb|head/i.test(clean) && /green\s*tops?|scallion\s*greens?/i.test(phrase)) continue;

        const culprit = derivePrimaryCulprit(item.fodmaps);
        return {
          raw: rawIngredient,
          matchedFood: item,
          rating: item.rating,
          primaryCulprit: culprit,
          swapSuggestion: item.swapSuggestion,
          servingLimitNote: item.servings?.low?.title
            ? `Low at ${item.servings.low.title}${item.servings.low.grams ? ` (${item.servings.low.grams}g)` : ''}`
            : item.notes,
        };
      }
    }
  }

  // 6. Default fallback: assumed Low FODMAP
  return {
    raw: rawIngredient,
    rating: 'low',
  };
}

export function derivePrimaryCulprit(fodmaps?: FodmapCarbLevels): string | undefined {
  if (!fodmaps) return undefined;
  const culprits: string[] = [];
  if (fodmaps.fructans >= 2) culprits.push('Fructans');
  if (fodmaps.lactose >= 2) culprits.push('Lactose');
  if (fodmaps.gos >= 2) culprits.push('GOS');
  if (fodmaps.fructose >= 2) culprits.push('Excess Fructose');
  if (fodmaps.sorbitol >= 2) culprits.push('Sorbitol');
  if (fodmaps.mannitol >= 2) culprits.push('Mannitol');
  return culprits.length > 0 ? culprits.join(', ') : undefined;
}

/**
 * Evaluate a full list of ingredients (e.g. from recipe, barcode, or image)
 */
export function evaluateFodmapMeal(ingredients: (string | { name?: string; raw?: string; [key: string]: any })[], dishName?: string): FodmapAnalysisResult {
  const safeList: string[] = Array.isArray(ingredients)
    ? ingredients
        .map((i) => {
          if (typeof i === 'string') return i.trim();
          if (i && typeof i === 'object') {
            if (typeof i.name === 'string' && i.name.trim()) return i.name.trim();
            if (typeof i.raw === 'string' && i.raw.trim()) return i.raw.trim();
          }
          return '';
        })
        .filter((s) => s.length > 0)
    : [];
  const evaluations: IngredientEvaluation[] = safeList.map((ing) => matchIngredientToFodmap(ing));

  let highCount = 0;
  let modCount = 0;
  let lowCount = 0;
  const culpritSet = new Set<string>();
  const smartSwaps: { original: string; swap: string; reason: string }[] = [];
  const safePortionTips: string[] = [];

  for (const evalItem of evaluations) {
    if (evalItem.rating === 'high') {
      highCount++;
      if (evalItem.primaryCulprit) {
        evalItem.primaryCulprit.split(',').forEach((c) => culpritSet.add(c.trim()));
      }
      if (evalItem.swapSuggestion) {
        smartSwaps.push({
          original: evalItem.raw,
          swap: evalItem.swapSuggestion,
          reason: evalItem.primaryCulprit ? `High in ${evalItem.primaryCulprit}` : 'High FODMAP load',
        });
      }
    } else if (evalItem.rating === 'moderate') {
      modCount++;
      if (evalItem.primaryCulprit) {
        evalItem.primaryCulprit.split(',').forEach((c) => culpritSet.add(c.trim()));
      }
      if (evalItem.servingLimitNote) {
        safePortionTips.push(`${evalItem.raw}: ${evalItem.servingLimitNote}`);
      }
      if (evalItem.swapSuggestion && !smartSwaps.some((s) => s.original === evalItem.raw)) {
        smartSwaps.push({
          original: evalItem.raw,
          swap: evalItem.swapSuggestion,
          reason: 'Watch portion size',
        });
      }
    } else {
      lowCount++;
    }
  }

  // Compute Overall Rating
  let overallRating: FodmapRating = 'low';
  if (highCount > 0) {
    overallRating = 'high';
  } else if (modCount > 0) {
    overallRating = 'moderate';
  }

  // Formulate clinical summary
  let summary = '';
  if (overallRating === 'low') {
    summary = dishName
      ? `"${dishName}" appears Low FODMAP and friendly for IBS elimination diets.`
      : 'All detected ingredients are Low FODMAP in standard meal servings.';
  } else if (overallRating === 'moderate') {
    summary = `Caution: contains ${modCount} moderate-FODMAP ingredient${modCount > 1 ? 's' : ''}. Keep portions controlled to prevent stacking.`;
  } else {
    const culpritsList = Array.from(culpritSet).join(' & ');
    summary = `High FODMAP risk detected (${highCount} trigger${highCount > 1 ? 's' : ''}${culpritsList ? `, primarily ${culpritsList}` : ''}). Swaps recommended below.`;
  }

  let clinicalGuidance: string | undefined = undefined;
  if (evaluations.length === 1 && evaluations[0].clinicalGuidance) {
    clinicalGuidance = evaluations[0].clinicalGuidance;
  } else if (dishName) {
    clinicalGuidance = getClinicalGuidanceForFood(dishName);
  }

  return {
    overallRating,
    summary,
    detectedIngredients: evaluations,
    highFodmapCount: highCount,
    moderateFodmapCount: modCount,
    lowFodmapCount: lowCount,
    flaggedCulprits: Array.from(culpritSet),
    smartSwaps,
    safePortionTips,
    clinicalGuidance,
  };
}

export function getLowFodmapSubstitute(rawIngredient: string): { original: string; substitute: string; reason: string } | null {
  if (!rawIngredient || typeof rawIngredient !== 'string') return null;
  const clean = rawIngredient.trim().toLowerCase();

  // Alliums
  if (/\b(garlic powder|garlic salt|crushed garlic|garlic cloves?|garlic)\b/i.test(clean) && !/garlic[- ]infused|oil/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Garlic-infused olive oil',
      reason: 'Fructans are water-soluble but not oil-soluble, so infused oil gives full garlic flavor with zero FODMAPs',
    };
  }
  if (/\b(shallots?)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Fresh chives',
      reason: 'Chives provide rich allium taste with negligible fructans',
    };
  }
  if (/\b(onion powder|onion salt|red onions?|white onions?|yellow onions?|brown onions?|onions?|leek bulbs?)\b/i.test(clean) && !/spring onion green|chives/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Spring onion green tops (scallion greens)',
      reason: 'Green tops are certified Low FODMAP by Monash, while white bulbs contain high fructans',
    };
  }

  // Dairy, Lactose & Confectionery
  if (/\b(condensed milk|evaporated milk|dulce de leche)\b/i.test(clean) && !/coconut|lactose[- ]free/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Coconut condensed milk (or lactose-free condensed milk)',
      reason: 'Coconut condensed milk and lactose-free condensed milk provide authentic caramel texture without high lactose loads',
    };
  }
  if (/\b(milk chocolate|white chocolate|chocolate chips?)\b/i.test(clean) && !/dark|dairy[- ]free/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Dark chocolate 70%+ (or dairy-free chocolate chips)',
      reason: 'Dark chocolate contains minimal milk solids and is Monash certified Low FODMAP up to 30g',
    };
  }
  if (/\b(cow'?s? milk|whole milk|semi-skimmed milk|skimmed milk|milk)\b/i.test(clean) && !/lactose[- ]free|almond|oat|coconut|soy|condensed/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Lactose-free milk (or almond milk)',
      reason: 'Provides identical culinary moisture and creaminess without lactose',
    };
  }
  if (/\b(heavy cream|double cream|whipping cream|sour cream|cream)\b/i.test(clean) && !/coconut|lactose[- ]free|oat|ice cream/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Lactose-free cream (or canned coconut cream)',
      reason: 'Prevents osmotic fluid shifts and bloating in the small bowel',
    };
  }
  if (/\b(ricotta|mascarpone|cottage cheese|cream cheese)\b/i.test(clean) && !/lactose[- ]free/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Lactose-free cream cheese (or hard parmesan / aged cheddar)',
      reason: 'Hard cheeses and lactose-free soft cheeses contain virtually 0g lactose',
    };
  }
  if (/\b(shortbread|millionaire shortbread|biscuit|cookies?)\b/i.test(clean) && !/gluten[- ]free/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Gluten-free shortbread (or gluten-free oat cookies)',
      reason: 'Replaces wheat fructans with hypoallergenic rice/oat base',
    };
  }

  // Grains, Bread & Pasta
  if (/\b(wheat bread|white bread|baker'?s bread|bread|toast|bagel|croissant|brioche)\b/i.test(clean) && !/gluten[- ]free|sourdough/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Authentic sourdough bread (or gluten-free bread)',
      reason: 'Traditional sourdough fermentation breaks down fructan chains; gluten-free eliminates wheat fructans',
    };
  }
  if (/\b(wheat pasta|spaghetti|fettuccine|penne|linguine|macaroni|pasta|noodles?|ramen)\b/i.test(clean) && !/gluten[- ]free|rice noodles|soba/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Gluten-free pasta (or brown rice noodles)',
      reason: 'Replaces high-fructan wheat with hypoallergenic rice/corn grain',
    };
  }
  if (
    (/\b(wheat\s*flour|all[- ]purpose\s*flour|plain\s*flour|white\s*flour|self[- ]raising\s*flour|bread\s*flour|semolina)\b/i.test(clean) || clean === 'flour') &&
    !/gluten[- ]free|rice|tapioca|maize|corn|buckwheat|almond|coconut|oat|sorghum|millet|cassava/i.test(clean)
  ) {
    return {
      original: rawIngredient,
      substitute: 'Gluten-free 1-to-1 baking flour (rice/tapioca blend)',
      reason: 'Eliminates wheat fructans',
    };
  }

  // Mushrooms
  if (/\b(button mushrooms?|cremini mushrooms?|portobello mushrooms?|mushrooms?)\b/i.test(clean) && !/oyster|canned/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Oyster mushrooms (or rinsed canned champignons)',
      reason: 'Oyster mushrooms have negligible mannitol compared to button mushrooms',
    };
  }

  // Vegetables
  if (/\b(cauliflower)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Broccoli florets (heads only)',
      reason: 'Broccoli heads are Low FODMAP in generous portions up to 75g',
    };
  }
  if (/\b(asparagus)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Fine green beans',
      reason: 'Green beans are Monash certified Low FODMAP',
    };
  }

  // Sweeteners
  if (/\b(honey|high fructose corn syrup|hfcs|agave syrup|agave)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: '100% Pure maple syrup',
      reason: 'Balanced glucose-fructose ratio that absorbs fully in the upper GI tract',
    };
  }

  // Legumes & Nuts
  if (/\b(cashews?|pistachios?)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Toasted walnuts (or pecans / pumpkin seeds)',
      reason: 'Walnuts and pecans have very low GOS and fructan levels',
    };
  }
  if (/\b(black beans?|kidney beans?|baked beans?|chickpeas?|garbanzo)\b/i.test(clean) && !/canned|rinsed/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Firm tofu (or rinsed canned lentils ¼ cup)',
      reason: 'Firm pressing or canning water leaches out water-soluble GOS',
    };
  }

  // Beverages & Drinks
  if (/\b(kombucha|fermented tea)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Water kefir (or chilled peppermint tea)',
      reason: 'Probiotic water kefir and herbal teas are certified Low FODMAP and eliminate fermentation-derived excess fructose',
    };
  }
  if (/\b(beer|lager|ale|stout|ipa|wheat beer)\b/i.test(clean) && !/gluten[- ]free/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Certified gluten-free beer (or dry hard cider)',
      reason: 'Eliminates barley and wheat fructans while maintaining authentic brew profile',
    };
  }
  if (/\b(apple juice|pear juice|mango juice|fruit juice|fruit punch)\b/i.test(clean) && !/orange|cranberry/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Fresh orange juice (max 125ml) or cranberry juice',
      reason: 'Citrus and cranberry have equal fructose-glucose ratios that absorb properly in the small bowel',
    };
  }
  if (/\b(chamomile tea|fennel tea|chai tea|strong black tea)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Peppermint tea (or fresh ginger / green tea)',
      reason: 'Certified safe herbal teas that relax intestinal smooth muscle without fructan extraction',
    };
  }

  // Fruit
  if (/\b(apples?|pears?)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Strawberries (or blueberries / oranges)',
      reason: 'Low in fructose and sorbitol',
    };
  }
  if (/\b(watermelons?)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Cantaloupe / Rockmelon (up to 120g)',
      reason: 'Cantaloupe is Monash certified Low FODMAP up to 120g, unlike watermelon which is high in fructose and mannitol',
    };
  }
  if (/\b(mangoes?|mango|cherries?|plums?|peaches?|apricots?|blackberries?)\b/i.test(clean)) {
    return {
      original: rawIngredient,
      substitute: 'Fresh strawberries (or pineapple / blueberries)',
      reason: 'Low in fructose and polyols',
    };
  }

  return null;
}

export interface AiCategorySwapResult {
  original: string;
  category: string;
  primaryCulprit?: string;
  substitute: string;
  safeServing?: string;
  reason: string;
  additionalOptions?: string[];
  provider?: string;
}

const AI_SWAP_CACHE = new Map<string, AiCategorySwapResult>();

/**
 * Fetch intelligent category-aware swap recommendations from /api/swap (Gemini 3.5 Flash Lite)
 */
export async function fetchAiCategorySwap(
  ingredientName: string,
  category?: string
): Promise<AiCategorySwapResult | null> {
  const clean = (ingredientName || '').trim();
  if (!clean) return null;

  const cacheKey = clean.toLowerCase();
  if (AI_SWAP_CACHE.has(cacheKey)) {
    return AI_SWAP_CACHE.get(cacheKey)!;
  }

  try {
    const userKey = typeof window !== 'undefined' ? (window.localStorage.getItem('digestlog_gemini_api_key') || '') : '';
    const userModel = typeof window !== 'undefined' ? (window.localStorage.getItem('digestlog_gemini_model') || '') : '';
    const currentUserId = typeof window !== 'undefined' ? (window.localStorage.getItem('digestlog_auth_user') ? JSON.parse(window.localStorage.getItem('digestlog_auth_user')!).userId : 'usr_harry') : 'usr_harry';

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000);

    const res = await fetch('/api/swap', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': currentUserId,
      },
      signal: controller.signal,
      body: JSON.stringify({
        ingredient: clean,
        category,
        geminiApiKey: userKey || undefined,
        geminiModel: userModel || undefined,
        userId: currentUserId,
      }),
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = (await res.json()) as AiCategorySwapResult;
      if (data && data.substitute) {
        AI_SWAP_CACHE.set(cacheKey, data);
        return data;
      }
    }
  } catch (err) {
    console.warn('AI Swap API call error, using local category engine:', err);
  }

  // Fallback to local rule engine
  const localSwap = getLowFodmapSubstitute(clean);
  if (localSwap) {
    const isDrink = /drink|beverage|tea|coffee|juice|beer|wine|cider|soda|kombucha|latte/i.test(clean);
    const result: AiCategorySwapResult = {
      original: clean,
      category: isDrink ? 'Beverages' : 'Foods',
      substitute: localSwap.substitute,
      reason: localSwap.reason,
      provider: 'Local Clinical Engine',
    };
    AI_SWAP_CACHE.set(cacheKey, result);
    return result;
  }

  return null;
}

export function convertMealToLowFodmap(
  rawIngredients: string[],
  dishTitle?: string
): {
  newIngredients: string[];
  modifiedCount: number;
  appliedSwaps: { original: string; substitute: string; reason: string }[];
  newTitle: string;
} {
  const appliedSwaps: { original: string; substitute: string; reason: string }[] = [];
  const newIngredients: string[] = [];

  for (const raw of rawIngredients) {
    const swap = getLowFodmapSubstitute(raw);
    if (swap) {
      newIngredients.push(swap.substitute);
      appliedSwaps.push(swap);
    } else {
      newIngredients.push(raw);
    }
  }

  let newTitle = dishTitle || 'Custom Meal';
  if (appliedSwaps.length > 0 && !newTitle.toLowerCase().includes('low-fodmap')) {
    newTitle = `Low-FODMAP ${newTitle}`;
  }

  return {
    newIngredients,
    modifiedCount: appliedSwaps.length,
    appliedSwaps,
    newTitle,
  };
}

/**
 * Evaluates rolling cumulative FODMAP load across a 4-to-6-hour window to prevent oligosaccharide/polyol stacking.
 */
export function evaluateFodmapStacking(
  currentMealIngredients: string[],
  recentMeals: Meal[] = [],
  windowHours: number = 5,
  currentMealTimestampIso?: string
): FodmapStackingAlert {
  const targetTimeMs = currentMealTimestampIso
    ? new Date(currentMealTimestampIso).getTime()
    : Date.now();
  const windowMs = windowHours * 60 * 60 * 1000;

  // 1. Identify qualifying recent meals within the rolling window
  const relevantMeals = recentMeals.filter((m) => {
    const mTime = new Date(m.timestamp_iso).getTime();
    const diffMs = targetTimeMs - mTime;
    return diffMs > 0 && diffMs <= windowMs;
  });

  type SubtypeKey = 'Fructans' | 'GOS' | 'Lactose' | 'Excess Fructose' | 'Sorbitol' | 'Mannitol';
  const tallies: Record<SubtypeKey, { score: number; sources: Set<string> }> = {
    Fructans: { score: 0, sources: new Set() },
    GOS: { score: 0, sources: new Set() },
    Lactose: { score: 0, sources: new Set() },
    'Excess Fructose': { score: 0, sources: new Set() },
    Sorbitol: { score: 0, sources: new Set() },
    Mannitol: { score: 0, sources: new Set() },
  };

  const contributingMealsMap: Array<{
    mealId?: string;
    mealDescription: string;
    timeAgoHours: number;
    matchedIngredients: string[];
  }> = [];

  const scoreIngredient = (rawIng: string, sourceLabel: string) => {
    const evalRes = matchIngredientToFodmap(rawIng);
    const item = evalRes.matchedFood;

    if (item && item.fodmaps) {
      if (item.fodmaps.fructans >= 2) {
        tallies.Fructans.score += item.fodmaps.fructans === 3 ? 1.5 : 0.8;
        tallies.Fructans.sources.add(`${rawIng} (${sourceLabel})`);
      }
      if (item.fodmaps.gos >= 2) {
        tallies.GOS.score += item.fodmaps.gos === 3 ? 1.5 : 0.8;
        tallies.GOS.sources.add(`${rawIng} (${sourceLabel})`);
      }
      if (item.fodmaps.lactose >= 2) {
        tallies.Lactose.score += item.fodmaps.lactose === 3 ? 1.5 : 0.8;
        tallies.Lactose.sources.add(`${rawIng} (${sourceLabel})`);
      }
      if (item.fodmaps.fructose >= 2) {
        tallies['Excess Fructose'].score += item.fodmaps.fructose === 3 ? 1.5 : 0.8;
        tallies['Excess Fructose'].sources.add(`${rawIng} (${sourceLabel})`);
      }
      if (item.fodmaps.sorbitol >= 2) {
        tallies.Sorbitol.score += item.fodmaps.sorbitol === 3 ? 1.5 : 0.8;
        tallies.Sorbitol.sources.add(`${rawIng} (${sourceLabel})`);
      }
      if (item.fodmaps.mannitol >= 2) {
        tallies.Mannitol.score += item.fodmaps.mannitol === 3 ? 1.5 : 0.8;
        tallies.Mannitol.sources.add(`${rawIng} (${sourceLabel})`);
      }
    } else if (evalRes.rating === 'moderate' || evalRes.rating === 'high') {
      const culpritKey = evalRes.primaryCulprit as SubtypeKey | undefined;
      const targetSubtype = culpritKey && tallies[culpritKey] ? culpritKey : 'Fructans';
      tallies[targetSubtype].score += evalRes.rating === 'high' ? 1.2 : 0.7;
      tallies[targetSubtype].sources.add(`${rawIng} (${sourceLabel})`);
    }
  };

  // Score recent meals within the window
  for (const prevMeal of relevantMeals) {
    const timeAgo = Math.round(((targetTimeMs - new Date(prevMeal.timestamp_iso).getTime()) / (1000 * 60 * 60)) * 10) / 10;
    const prevIngs = (prevMeal.ingredients_tags || '')
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const matched: string[] = [];

    for (const ing of prevIngs) {
      scoreIngredient(ing, `${prevMeal.description || 'Prior meal'} (${timeAgo}h ago)`);
      const ev = matchIngredientToFodmap(ing);
      if (ev.rating !== 'low') {
        matched.push(ing);
      }
    }

    if (matched.length > 0) {
      contributingMealsMap.push({
        mealId: prevMeal.id,
        mealDescription: prevMeal.description || 'Recent Meal',
        timeAgoHours: timeAgo,
        matchedIngredients: matched,
      });
    }
  }

  // Score current proposed meal
  for (const curIng of currentMealIngredients) {
    scoreIngredient(curIng, 'Current meal');
  }

  const stackedSubtypes: Array<{
    subtype: SubtypeKey;
    score: number;
    threshold: number;
    sources: string[];
  }> = [];

  const THRESHOLD = 1.4; // 2+ moderate servings or moderate + high in rolling window
  (Object.keys(tallies) as SubtypeKey[]).forEach((k) => {
    const data = tallies[k];
    if (data.score >= THRESHOLD && data.sources.size >= 2) {
      stackedSubtypes.push({
        subtype: k,
        score: Math.round(data.score * 10) / 10,
        threshold: THRESHOLD,
        sources: Array.from(data.sources),
      });
    }
  });

  const isStackingWarning = stackedSubtypes.length > 0 && (relevantMeals.length > 0 || currentMealIngredients.length >= 2);
  const stackedCategories = stackedSubtypes.map((s) => s.subtype);

  let warningMessage = '';
  if (isStackingWarning) {
    const names = stackedCategories.join(' & ');
    const allSources = stackedSubtypes.flatMap((s) => s.sources).slice(0, 3);
    warningMessage = `Cumulative ${names} load across the last ${windowHours}h exceeds single-window tolerance (${allSources.join(', ')}).`;
  }

  return {
    isStackingWarning,
    cumulativeSubtypes: stackedSubtypes,
    stackedCategories,
    warningMessage,
    contributingMeals: contributingMealsMap,
  };
}

/**
 * Extracts a numeric portion and unit if specified in text (e.g. "2 eggs", "15g honey", "1 tbsp olive oil", "1/2 cup oats", "3 tbsp yoghurt", "1 rasher bacon").
 */
export function extractPortionFromText(rawName: string): { grams: number; unit: string; amount: number } | null {
  const clean = rawName.trim();

  // Match fraction portions e.g. "1/2 cup oats", "1/4 tsp honey", "3/4 tbsp seeds"
  const fracMatch = clean.match(/^(\d+)\s*\/\s*(\d+)\s*(g|ml|tbsp|tablespoon|tablespoons|tsp|teaspoon|teaspoons|cup|cups|slice|slices|piece|pieces|rasher|rashers|egg|eggs|clove|cloves|pot|pots)?\s+(.+)$/i);
  let num: number;
  let unitStr: string;

  if (fracMatch) {
    const denom = parseFloat(fracMatch[2]);
    if (denom === 0) return null;
    num = Math.round((parseFloat(fracMatch[1]) / denom) * 100) / 100;
    unitStr = fracMatch[3] || 'cup';
  } else {
    const m = clean.match(/^(\d+(?:\.\d+)?)\s*(g|ml|tbsp|tablespoon|tablespoons|tsp|teaspoon|teaspoons|cup|cups|slice|slices|piece|pieces|rasher|rashers|egg|eggs|clove|cloves|pot|pots)?\s+(.+)$/i);
    if (!m) return null;
    num = parseFloat(m[1]);
    unitStr = m[2] || 'g';
  }

  if (isNaN(num) || num <= 0) return null;
  const rawUnit = unitStr.toLowerCase();
  let unit = rawUnit;
  if (rawUnit === 'tablespoon' || rawUnit === 'tablespoons') unit = 'tbsp';
  else if (rawUnit === 'teaspoon' || rawUnit === 'teaspoons') unit = 'tsp';

  let grams = num;

  if (unit === 'tbsp') grams = num * (/yoghurt|yogurt/i.test(clean) ? 20 : 15);
  else if (unit === 'tsp') grams = num * (/honey/i.test(clean) ? 7 : 5);
  else if (unit === 'cup' || unit === 'cups') grams = num * (/oats|porridge|granola/i.test(clean) ? 104 : 125);
  else if (unit === 'slice' || unit === 'slices') grams = num * 40;
  else if (unit === 'piece' || unit === 'pieces') grams = num * 50;
  else if (unit === 'egg' || unit === 'eggs') grams = num * 50;
  else if (unit === 'rasher' || unit === 'rashers') grams = num * 20;
  else if (unit === 'clove' || unit === 'cloves') grams = num * 3;
  else if (unit === 'pot' || unit === 'pots') grams = num * 125;
  else if (unit === 'ml') grams = num;

  return { grams: Math.max(1, Math.round(grams)), unit, amount: num };
}

/**
 * Returns the Monash Safe Low-FODMAP portion limit or safety note for an ingredient.
 */
export function getSafeLimitNote(foodName: string, matchedFood?: FodmapFoodItem): string {
  if (matchedFood) {
    if (matchedFood.servings?.low?.grams && matchedFood.servings.low.grams > 0) {
      const g = matchedFood.servings.low.grams;
      const title = matchedFood.servings.low.title ? ` (${matchedFood.servings.low.title})` : '';
      return `Safe limit: ${g}g${title}`;
    }
    if (matchedFood.servings?.low?.title && /avoid/i.test(matchedFood.servings.low.title)) {
      return `High FODMAP: Avoid in elimination`;
    }
    if (matchedFood.rating === 'low') {
      return matchedFood.notes || 'Safe Low FODMAP (No FODMAPs detected)';
    }
    if (matchedFood.rating === 'high') {
      return `High FODMAP: Avoid in elimination`;
    }
    if (matchedFood.rating === 'moderate') {
      return 'Moderate FODMAP: Limit serving size';
    }
  }

  const ev = matchIngredientToFodmap(foodName);
  if (ev.servingLimitNote) return ev.servingLimitNote;
  if (ev.rating === 'high') return 'High FODMAP: Avoid in elimination';
  if (ev.rating === 'moderate') return 'Moderate FODMAP: Limit serving size';
  return 'Safe Low FODMAP';
}

export interface RealisticServingResult {
  grams: number;
  unit: string;
  amount: number;
  servingLimitNote: string;
}

/**
 * Accurately estimates realistic culinary single-portion sizes for ingredients
 * (especially critical for breakfast toppings, seeds, nuts, aromatics, oils, condiments)
 * rather than an arbitrary 100g default which triggers false stacking alarms.
 */
export function getRealisticCulinaryServing(
  rawName: string,
  matchedFood?: FodmapFoodItem
): RealisticServingResult {
  const canonical = canonicalizeIngredient(rawName);
  const ev = matchedFood ? null : matchIngredientToFodmap(rawName);
  const matched = matchedFood || ev?.matchedFood;
  const lower = canonical.toLowerCase().trim();

  // 1. Explicit text portion check
  const parsedPortion = extractPortionFromText(rawName);
  if (parsedPortion) {
    return {
      grams: parsedPortion.grams,
      unit: parsedPortion.unit,
      amount: parsedPortion.amount,
      servingLimitNote: getSafeLimitNote(rawName, matched),
    };
  }

  // 2. High-priority culinary portion dictionary

  // Beverages & Coffees
  if (/^espresso\b|^double espresso\b|\bristretto\b/i.test(lower)) {
    const isDouble = /double/i.test(lower);
    return {
      grams: isDouble ? 60 : 30,
      unit: isDouble ? 'shots' : 'shot',
      amount: isDouble ? 2 : 1,
      servingLimitNote: isDouble ? 'Safe (0g FODMAPs, 2 shots = 60ml)' : 'Safe (0g FODMAPs, 1 shot = 30ml)',
    };
  }
  if (/instant coffee/i.test(lower)) {
    return { grams: 2, unit: 'tsp', amount: 1, servingLimitNote: 'Safe limit: 1-2 tsp (2-4g)' };
  }
  if (/matcha/i.test(lower)) {
    return { grams: 2, unit: 'tsp', amount: 1, servingLimitNote: 'Safe limit: 1 tsp (2g)' };
  }
  if (/coffee|americano|filter coffee|brewed coffee|cafe/i.test(lower)) {
    return { grams: 240, unit: 'cup', amount: 1, servingLimitNote: 'Safe limit: 1 cup (240ml)' };
  }
  if (/tea|earl grey|chai|peppermint tea|green tea|chamomile/i.test(lower)) {
    return { grams: 240, unit: 'cup', amount: 1, servingLimitNote: 'Safe limit: 1 cup (240ml)' };
  }

  // Granola / Oats / Breakfast items
  if (/oats?|porridge|granola|muesli/i.test(lower)) {
    return { grams: 45, unit: 'g', amount: 45, servingLimitNote: 'Safe limit: 52g (1/2 cup dry)' };
  }
  if (/honey/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 7g (1 tsp)' };
  }
  if (/maple syrup/i.test(lower)) {
    return { grams: 20, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 50g (2 tbsp)' };
  }
  if (/almond/i.test(lower)) {
    return { grams: 15, unit: 'g', amount: 15, servingLimitNote: 'Safe limit: 12g (~10 nuts)' };
  }
  if (/walnut/i.test(lower)) {
    return { grams: 20, unit: 'g', amount: 20, servingLimitNote: 'Safe limit: 30g (~10 halves)' };
  }
  if (/pecan/i.test(lower)) {
    return { grams: 20, unit: 'g', amount: 20, servingLimitNote: 'Safe limit: 20g (~10 halves)' };
  }
  if (/chia/i.test(lower)) {
    return { grams: 12, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 24g (2 tbsp)' };
  }
  if (/sunflower seed/i.test(lower)) {
    return { grams: 12, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 30g (2 tbsp)' };
  }
  if (/pumpkin seed|pepita/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 23g (2 tbsp)' };
  }
  if (/flax|linseed/i.test(lower)) {
    return { grams: 10, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 15g (1 tbsp)' };
  }
  if (/sesame seed/i.test(lower)) {
    return { grams: 10, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 11g (1 tbsp)' };
  }
  if (/coconut oil/i.test(lower)) {
    return { grams: 14, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe (FODMAP-free)' };
  }
  if (/coconut flakes|shredded coconut|desiccated coconut/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 30g' };
  }
  if (/blueberry|blueberries/i.test(lower)) {
    return { grams: 50, unit: 'g', amount: 50, servingLimitNote: 'Safe limit: 125g (1 cup)' };
  }
  if (/strawberry|strawberries/i.test(lower)) {
    return { grams: 65, unit: 'g', amount: 65, servingLimitNote: 'Safe limit: 65g (5 medium)' };
  }
  if (/raspberry|raspberries/i.test(lower)) {
    return { grams: 45, unit: 'g', amount: 45, servingLimitNote: 'Safe limit: 60g (30 berries)' };
  }
  if (/banana/i.test(lower)) {
    return { grams: 100, unit: 'banana', amount: 1, servingLimitNote: 'Safe limit: 1 medium firm banana (100g)' };
  }
  if (/cinnamon/i.test(lower)) {
    return { grams: 2, unit: 'tsp', amount: 0.5, servingLimitNote: 'Safe (No FODMAPs detected)' };
  }

  // Alliums & Aromatics
  if (/garlic-infused/i.test(lower)) {
    return { grams: 14, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe (FODMAP-free)' };
  }
  if (/garlic powder/i.test(lower)) {
    return { grams: 1, unit: 'tsp', amount: 0.5, servingLimitNote: 'High Fructans (Avoid in elimination)' };
  }
  if (/garlic/i.test(lower)) {
    return { grams: 3, unit: 'clove', amount: 1, servingLimitNote: 'High Fructans (Avoid in elimination)' };
  }
  if (/onion powder/i.test(lower)) {
    return { grams: 1, unit: 'tsp', amount: 0.5, servingLimitNote: 'High Fructans (Avoid in elimination)' };
  }
  if (/spring onion green|scallion green|green spring onion/i.test(lower)) {
    return { grams: 15, unit: 'g', amount: 15, servingLimitNote: 'Safe limit: 75g (1 cup)' };
  }
  if (/spring onion|scallion/i.test(lower)) {
    return { grams: 15, unit: 'g', amount: 15, servingLimitNote: 'Bulb is High Fructans (use green tops)' };
  }
  if (/shallot/i.test(lower)) {
    return { grams: 15, unit: 'g', amount: 15, servingLimitNote: 'High Fructans (Avoid in elimination)' };
  }
  if (/onion/i.test(lower)) {
    return { grams: 35, unit: 'g', amount: 35, servingLimitNote: 'High Fructans (Avoid in elimination)' };
  }
  if (/chives/i.test(lower)) {
    return { grams: 4, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 4g (1 tbsp)' };
  }
  if (/leek green/i.test(lower)) {
    return { grams: 25, unit: 'g', amount: 25, servingLimitNote: 'Safe limit: 54g (1/2 cup)' };
  }
  if (/leek/i.test(lower)) {
    return { grams: 25, unit: 'g', amount: 25, servingLimitNote: 'Bulb is High Fructans (use green leaves)' };
  }
  if (/ginger/i.test(lower)) {
    return { grams: 5, unit: 'g', amount: 5, servingLimitNote: 'Safe (No FODMAPs detected)' };
  }
  if (/chili|chilli/i.test(lower)) {
    return { grams: 10, unit: 'g', amount: 10, servingLimitNote: 'Safe limit: 28g' };
  }

  // Fats, Oils, Condiments
  if (/oil/i.test(lower)) {
    return { grams: 14, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe (FODMAP-free)' };
  }
  if (/butter|ghee/i.test(lower)) {
    return { grams: 10, unit: 'pat', amount: 1, servingLimitNote: 'Safe limit: 19g (Low lactose)' };
  }
  if (/mayo|mayonnaise/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 40g (2 tbsp)' };
  }
  if (/dressing|vinaigrette|sauce|dip|gravy/i.test(lower)) {
    return { grams: 30, unit: 'tbsp', amount: 2, servingLimitNote: 'Standard serving (2 tbsp / 30g)' };
  }
  if (/mustard/i.test(lower)) {
    return { grams: 10, unit: 'tsp', amount: 2, servingLimitNote: 'Safe limit: 23g (1 tbsp)' };
  }
  if (/soy sauce|tamari/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 42g (2 tbsp)' };
  }
  if (/vinegar/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 21g-42g' };
  }
  if (/lemon juice|lime juice/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 21g (1 tbsp)' };
  }
  if (/sugar/i.test(lower)) {
    return { grams: 4, unit: 'tsp', amount: 1, servingLimitNote: 'Safe limit: 50g' };
  }
  if (/salt|black pepper|pepper|paprika|cumin|oregano|basil|thyme|rosemary|parsley|turmeric|herb|spice/i.test(lower)) {
    return { grams: 2, unit: 'tsp', amount: 0.5, servingLimitNote: 'Safe (Low FODMAP)' };
  }

  // Dairy & Cheeses
  if (/parmesan|pecorino/i.test(lower)) {
    return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 40g (trace lactose)' };
  }
  if (/cheddar|feta|mozzarella|swiss cheese|brie|camembert/i.test(lower)) {
    return { grams: 30, unit: 'g', amount: 30, servingLimitNote: 'Safe limit: 40g' };
  }
  if (/cream cheese|ricotta|mascarpone/i.test(lower)) {
    return { grams: 20, unit: 'tbsp', amount: 1, servingLimitNote: 'High Lactose (Safe limit: 15g)' };
  }
  if (/heavy cream|double cream|whipping cream|cream/i.test(lower)) {
    return { grams: 20, unit: 'tbsp', amount: 1, servingLimitNote: 'High Lactose (Safe limit: 15g)' };
  }
  if (/lactose-free milk/i.test(lower)) {
    return { grams: 150, unit: 'ml', amount: 150, servingLimitNote: 'Safe limit: 250ml' };
  }
  if (/almond milk/i.test(lower)) {
    return { grams: 150, unit: 'ml', amount: 150, servingLimitNote: 'Safe limit: 250ml' };
  }
  if (/oat milk/i.test(lower)) {
    return { grams: 125, unit: 'ml', amount: 125, servingLimitNote: 'Moderate GOS (Safe limit: 30ml)' };
  }
  if (/cow milk|whole milk|semi-skimmed milk|skimmed milk|milk/i.test(lower)) {
    return { grams: 150, unit: 'ml', amount: 150, servingLimitNote: 'High Lactose (Safe limit: 15ml)' };
  }
  if (/yogurt|yoghurt/i.test(lower)) {
    const isLF = /lactose-free/i.test(lower);
    return {
      grams: 60,
      unit: 'tbsp',
      amount: 3,
      servingLimitNote: isLF ? 'Safe limit: 170g' : 'High Lactose (Safe limit: 20g / 1 tbsp)',
    };
  }

  // Proteins & Meats
  if (/egg/i.test(lower)) {
    return { grams: 50, unit: 'egg', amount: 1, servingLimitNote: 'Safe (0g FODMAPs, 1 egg = 50g)' };
  }
  if (/bacon/i.test(lower)) {
    return { grams: 20, unit: 'rasher', amount: 1, servingLimitNote: 'Safe (0g FODMAPs, 1 rasher = 20g)' };
  }
  if (/ham/i.test(lower)) {
    return { grams: 30, unit: 'slices', amount: 2, servingLimitNote: 'Safe (0g FODMAPs, 2 slices = 30g)' };
  }
  if (/chicken breast|turkey breast|chicken fillet/i.test(lower)) {
    return { grams: 140, unit: 'fillet', amount: 1, servingLimitNote: 'Safe (0g FODMAPs, 1 fillet = 140g)' };
  }
  if (/salmon|cod|tuna steak|fish fillet|haddock/i.test(lower)) {
    return { grams: 130, unit: 'fillet', amount: 1, servingLimitNote: 'Safe (0g FODMAPs, 1 fillet = 130g)' };
  }
  if (/steak|beef steak/i.test(lower)) {
    return { grams: 150, unit: 'steak', amount: 1, servingLimitNote: 'Safe (0g FODMAPs, 1 steak = 150g)' };
  }
  if (/chicken|turkey|beef|steak|pork|lamb|salmon|fish|cod|tuna|prawn|shrimp/i.test(lower)) {
    return { grams: 140, unit: 'g', amount: 140, servingLimitNote: 'Safe (0g FODMAPs, pure protein)' };
  }
  if (/tofu/i.test(lower)) {
    return { grams: 100, unit: 'g', amount: 100, servingLimitNote: /silken/i.test(lower) ? 'High FODMAP (Avoid)' : 'Safe limit: 160g' };
  }

  // Grains & Breads
  if (/flour|almond meal|almond flour/i.test(lower)) {
    const isGF = /gluten[- ]free|gf|rice|tapioca|maize|corn|buckwheat|cassava|sorghum|millet/i.test(lower);
    const isAlmond = /almond/i.test(lower);
    const isCoconut = /coconut/i.test(lower);
    if (isCoconut) return { grams: 15, unit: 'tbsp', amount: 1, servingLimitNote: 'Safe limit: 15g (1 tbsp)' };
    if (isAlmond) return { grams: 24, unit: 'g', amount: 24, servingLimitNote: 'Safe limit: 24g (2 tbsp)' };
    if (isGF) return { grams: 50, unit: 'g', amount: 50, servingLimitNote: 'Safe limit: 100g (2/3 cup)' };
    return { grams: 30, unit: 'g', amount: 30, servingLimitNote: 'High Fructans (Safe limit: 30g)' };
  }
  if (/sourdough/i.test(lower)) {
    return { grams: 45, unit: 'slice', amount: 1, servingLimitNote: 'Safe limit: 52g (1 slice)' };
  }
  if (/bagel/i.test(lower)) {
    return {
      grams: 105,
      unit: 'bagel',
      amount: 1,
      servingLimitNote: /gluten-free/i.test(lower) ? 'Safe limit: 105g (1 bagel)' : 'High Fructans (Safe limit: 18g / ~1/6 bagel)',
    };
  }
  if (/bread|toast/i.test(lower)) {
    return {
      grams: 40,
      unit: 'slice',
      amount: 1,
      servingLimitNote: /gluten-free/i.test(lower) ? 'Safe limit: 62g (2 slices)' : 'High Fructans (Safe limit: 24g / ~1/2 slice)',
    };
  }
  if (/pasta|spaghetti|noodle/i.test(lower)) {
    return { grams: 130, unit: 'g', amount: 130, servingLimitNote: /gluten-free/i.test(lower) ? 'Safe limit: 145g' : 'High Fructans (Safe limit: 74g)' };
  }
  if (/rice|quinoa/i.test(lower)) {
    return { grams: 140, unit: 'g', amount: 140, servingLimitNote: 'Safe (No FODMAPs detected)' };
  }
  if (/potato/i.test(lower)) {
    return { grams: 120, unit: 'piece', amount: 1, servingLimitNote: /sweet/i.test(lower) ? 'Safe limit: 75g (Mannitol above 75g)' : 'Safe (No FODMAPs detected)' };
  }

  // Vegetables
  if (/cucumber/i.test(lower)) {
    return { grams: 60, unit: 'g', amount: 60, servingLimitNote: 'Safe (No FODMAPs detected)' };
  }
  if (/celery/i.test(lower)) {
    return { grams: 10, unit: 'stick', amount: 0.25, servingLimitNote: 'Safe limit: 1/4 stick (10g) • High Mannitol at 1 stick' };
  }
  if (/sausage/i.test(lower)) {
    return { grams: 50, unit: 'sausage', amount: 1, servingLimitNote: '1 sausage = 50g (Check garlic/onion seasonings)' };
  }
  if (/tomato/i.test(lower)) {
    const isCherry = /cherry/i.test(lower);
    return isCherry
      ? { grams: 45, unit: 'tomatoes', amount: 3, servingLimitNote: 'Safe limit: 3 cherry tomatoes (45g)' }
      : { grams: 50, unit: 'tomato', amount: 0.5, servingLimitNote: 'Safe limit: 1/2 tomato (50g)' };
  }
  if (/carrot/i.test(lower)) {
    return { grams: 60, unit: 'carrot', amount: 1, servingLimitNote: '1 medium carrot = 60g (FODMAP-free)' };
  }
  if (/spinach|kale|lettuce|salad green|arugula|rocket/i.test(lower)) {
    return { grams: 50, unit: 'g', amount: 50, servingLimitNote: 'Safe limit: 75g' };
  }
  if (/avocado/i.test(lower)) {
    return { grams: 60, unit: 'g', amount: 60, servingLimitNote: 'Safe limit: 60g (Moderate at 80g, High at 90g+)' };
  }
  if (/mushroom/i.test(lower)) {
    return { grams: 35, unit: 'g', amount: 35, servingLimitNote: /oyster/i.test(lower) ? 'Safe limit: 75g' : 'High Mannitol (Avoid in elimination)' };
  }

  // 3. Fallback to matched food low serving (clamped against 500g database disclaimer)
  if (matched?.servings?.low?.grams && matched.servings.low.grams > 0 && matched.servings.low.grams < 400) {
    const g = matched.servings.low.grams;
    return {
      grams: g,
      unit: 'g',
      amount: g,
      servingLimitNote: getSafeLimitNote(rawName, matched),
    };
  }

  // 4. Default by food category or safe realistic fallback
  const safeLimit = getSafeLimitNote(rawName, matched);
  if (matched?.category === 'beverages' || /drink|beverage|liquid|water/i.test(matched?.category || '')) {
    return { grams: 240, unit: 'cup', amount: 1, servingLimitNote: safeLimit };
  }
  if (matched?.category === 'meats' || matched?.category === 'poultry' || matched?.category === 'seafood') {
    return { grams: 140, unit: 'g', amount: 140, servingLimitNote: safeLimit };
  }
  if (matched?.category === 'vegetables') {
    return { grams: 65, unit: 'g', amount: 65, servingLimitNote: safeLimit };
  }
  if (matched?.category === 'fruits') {
    return { grams: 80, unit: 'g', amount: 80, servingLimitNote: safeLimit };
  }
  if (matched?.category === 'grains' || matched?.category === 'cereals') {
    return { grams: 100, unit: 'g', amount: 100, servingLimitNote: safeLimit };
  }
  if (matched?.category === 'fats' || matched?.category === 'oils') {
    return { grams: 14, unit: 'tbsp', amount: 1, servingLimitNote: safeLimit };
  }
  if (matched?.category === 'dairy') {
    return { grams: 30, unit: 'g', amount: 30, servingLimitNote: safeLimit };
  }

  return { grams: 50, unit: 'g', amount: 50, servingLimitNote: safeLimit };
}

export interface PortionUnitDef {
  unit: string;
  label: string;
  gramsPerUnit: number;
  step: number;
}

export interface PortionPreset {
  amount: number;
  unit: string;
  grams: number;
  label: string;
}

export interface FoodPortionConfig {
  defaultAmount: number;
  defaultUnit: string;
  defaultGrams: number;
  monashServingText: string;
  monashSafeGrams?: number;
  units: PortionUnitDef[];
  presets: PortionPreset[];
}

/**
 * Returns household portion units, standard Monash conversions, and quick portion presets
 * for an ingredient to empower intuitive portion estimation (e.g. 1 rasher bacon, 3 tbsp yoghurt).
 */
export function getFoodPortionConfig(
  foodName: string,
  matchedFood?: FodmapFoodItem
): FoodPortionConfig {
  const lower = foodName.toLowerCase().trim();
  const matched = matchedFood || matchIngredientToFodmap(foodName).matchedFood;

  // 1. Bacon / Cured Meats
  if (/bacon|pancetta|prosciutto/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'rasher',
      defaultGrams: 20,
      monashServingText: '1 rasher = 20g (0g FODMAPs, pure protein)',
      monashSafeGrams: 20,
      units: [
        { unit: 'rasher', label: 'Rasher', gramsPerUnit: 20, step: 1 },
        { unit: 'slice', label: 'Slice', gramsPerUnit: 20, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 1, unit: 'rasher', grams: 20, label: '1 rasher (20g)' },
        { amount: 2, unit: 'rashers', grams: 40, label: '2 rashers (40g)' },
        { amount: 3, unit: 'rashers', grams: 60, label: '3 rashers (60g)' },
        { amount: 4, unit: 'rashers', grams: 80, label: '4 rashers (80g)' },
      ],
    };
  }

  // 2. Yoghurt / Yogurt
  if (/yoghurt|yogurt|kefir/i.test(lower)) {
    const isLF = /lactose-free/i.test(lower);
    return {
      defaultAmount: 3,
      defaultUnit: 'tbsp',
      defaultGrams: 60,
      monashServingText: isLF
        ? 'Lactose-Free: Safe up to 170g per sitting'
        : 'Monash threshold: 1 tbsp (20g) Low FODMAP',
      monashSafeGrams: isLF ? 170 : 20,
      units: [
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 20, step: 1 },
        { unit: 'pot', label: 'Pot', gramsPerUnit: 125, step: 0.5 },
        { unit: 'cup', label: 'Cup', gramsPerUnit: 245, step: 0.25 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 1, unit: 'tbsp', grams: 20, label: isLF ? '1 tbsp (20g)' : '1 tbsp (20g) - Safe Limit' },
        { amount: 2, unit: 'tbsp', grams: 40, label: '2 tbsp (40g)' },
        { amount: 3, unit: 'tbsp', grams: 60, label: '3 tbsp (60g)' },
        { amount: 4, unit: 'tbsp', grams: 80, label: '4 tbsp (80g)' },
        { amount: 1, unit: 'pot', grams: 125, label: '1 pot (125g)' },
      ],
    };
  }

  // 3. Bread / Toast / Sourdough / Bagel
  if (/bread|toast|sourdough|bagel|bun|roll/i.test(lower)) {
    const isGF = /gluten-free/i.test(lower);
    const isSD = /sourdough/i.test(lower);
    const safeGrams = isGF ? 62 : isSD ? 52 : 24;
    return {
      defaultAmount: 1,
      defaultUnit: 'slice',
      defaultGrams: 40,
      monashServingText: isGF
        ? 'Gluten-Free: 2 slices (62g) Low FODMAP'
        : isSD
        ? 'Spelt/White Sourdough: 1 slice (52g) Low FODMAP'
        : 'Wheat bread: 1 slice (24g) Low FODMAP, >1 slice High Fructans',
      monashSafeGrams: safeGrams,
      units: [
        { unit: 'slice', label: 'Slice', gramsPerUnit: 40, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 1, unit: 'slice', grams: 40, label: '1 slice (40g)' },
        { amount: 2, unit: 'slices', grams: 80, label: '2 slices (80g)' },
        { amount: 3, unit: 'slices', grams: 120, label: '3 slices (120g)' },
      ],
    };
  }

  // 4. Oats / Porridge / Granola / Muesli
  if (/oats?|porridge|granola|muesli/i.test(lower)) {
    return {
      defaultAmount: 45,
      defaultUnit: 'g',
      defaultGrams: 45,
      monashServingText: 'Monash Safe limit: 1/2 cup dry (52g) / 45g standard bowl',
      monashSafeGrams: 52,
      units: [
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
        { unit: 'cup', label: 'Cup', gramsPerUnit: 104, step: 0.25 },
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 12, step: 1 },
      ],
      presets: [
        { amount: 30, unit: 'g', grams: 30, label: '30g (Snack)' },
        { amount: 45, unit: 'g', grams: 45, label: '45g (Standard)' },
        { amount: 52, unit: 'g', grams: 52, label: '52g (Safe Limit)' },
        { amount: 0.5, unit: 'cup', grams: 52, label: '1/2 cup (52g)' },
        { amount: 1, unit: 'cup', grams: 104, label: '1 cup (104g)' },
      ],
    };
  }

  // 5. Honey / Maple Syrup / Sweeteners
  if (/honey/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'tsp',
      defaultGrams: 7,
      monashServingText: 'Monash Safe limit: 1 tsp (7g) • High Fructose at 1 tbsp',
      monashSafeGrams: 7,
      units: [
        { unit: 'tsp', label: 'Tsp', gramsPerUnit: 7, step: 0.5 },
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 21, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 5 },
      ],
      presets: [
        { amount: 0.5, unit: 'tsp', grams: 3.5, label: '1/2 tsp (3.5g)' },
        { amount: 1, unit: 'tsp', grams: 7, label: '1 tsp (7g) - Safe' },
        { amount: 2, unit: 'tsp', grams: 14, label: '2 tsp (14g)' },
        { amount: 1, unit: 'tbsp', grams: 21, label: '1 tbsp (21g)' },
      ],
    };
  }

  if (/maple syrup|syrup/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'tbsp',
      defaultGrams: 20,
      monashServingText: 'Monash Safe limit: 2 tbsp (50g)',
      monashSafeGrams: 50,
      units: [
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 20, step: 0.5 },
        { unit: 'tsp', label: 'Tsp', gramsPerUnit: 7, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 5 },
      ],
      presets: [
        { amount: 1, unit: 'tsp', grams: 7, label: '1 tsp (7g)' },
        { amount: 1, unit: 'tbsp', grams: 20, label: '1 tbsp (20g)' },
        { amount: 2, unit: 'tbsp', grams: 40, label: '2 tbsp (40g) - Safe' },
      ],
    };
  }

  // 5b. Sugar (White, Brown, Caster)
  if (/sugar|sucrose/i.test(lower) && !/sugar snap/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'tbsp',
      defaultGrams: 12,
      monashServingText: 'Monash Safe limit: 50g (equal parts glucose & fructose)',
      monashSafeGrams: 50,
      units: [
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 12, step: 0.5 },
        { unit: 'tsp', label: 'Tsp', gramsPerUnit: 4, step: 1 },
        { unit: 'cup', label: 'Cup', gramsPerUnit: 200, step: 0.25 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 1, unit: 'tsp', grams: 4, label: '1 tsp (4g)' },
        { amount: 1, unit: 'tbsp', grams: 12, label: '1 tbsp (12g)' },
        { amount: 2, unit: 'tbsp', grams: 24, label: '2 tbsp (24g)' },
        { amount: 50, unit: 'g', grams: 50, label: '50g (Safe limit)' },
      ],
    };
  }

  // 5c. Flour / Almond Meal / Baking Grains
  if (/flour|almond meal|almond flour/i.test(lower)) {
    const isGF = /gluten[- ]free|gf|rice|tapioca|maize|corn|buckwheat|cassava|sorghum|millet/i.test(lower);
    const isAlmond = /almond/i.test(lower);
    const isCoconut = /coconut/i.test(lower);
    const safeGrams = isCoconut ? 15 : isAlmond ? 24 : isGF ? 100 : 30;
    return {
      defaultAmount: 50,
      defaultUnit: 'g',
      defaultGrams: 50,
      monashServingText: isCoconut
        ? 'Coconut flour: Safe limit 15g (1 tbsp) • Moderate at 30g (Fructans/Sorbitol)'
        : isAlmond
        ? 'Almond meal: Safe limit 24g • Moderate at 40g (GOS)'
        : isGF
        ? 'Low-FODMAP Flour (Maize/Rice/GF): Safe at 100g'
        : 'Wheat flour: Safe limit ~30g • High Fructans at 50g+',
      monashSafeGrams: safeGrams,
      units: [
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 10, step: 1 },
        { unit: 'cup', label: 'Cup', gramsPerUnit: 120, step: 0.25 },
      ],
      presets: [
        { amount: 1, unit: 'tbsp', grams: 10, label: '1 tbsp (10g)' },
        { amount: 25, unit: 'g', grams: 25, label: '25g' },
        { amount: 50, unit: 'g', grams: 50, label: '50g' },
        { amount: 75, unit: 'g', grams: 75, label: '75g' },
      ],
    };
  }

  // 6. Oils / Fats / Butter
  if (/oil|butter|ghee|margarine/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'tbsp',
      defaultGrams: 14,
      monashServingText: '1 tbsp = 14g (Pure fat, FODMAP-free)',
      monashSafeGrams: 14,
      units: [
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 14, step: 0.5 },
        { unit: 'tsp', label: 'Tsp', gramsPerUnit: 5, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 5 },
      ],
      presets: [
        { amount: 1, unit: 'tsp', grams: 5, label: '1 tsp (5g)' },
        { amount: 2, unit: 'tsp', grams: 10, label: '2 tsp (10g)' },
        { amount: 1, unit: 'tbsp', grams: 14, label: '1 tbsp (14g)' },
        { amount: 2, unit: 'tbsp', grams: 28, label: '2 tbsp (28g)' },
      ],
    };
  }

  // 7. Eggs
  if (/egg/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'egg',
      defaultGrams: 50,
      monashServingText: '1 egg = 50g (0g FODMAPs, pure protein)',
      monashSafeGrams: 50,
      units: [
        { unit: 'egg', label: 'Egg', gramsPerUnit: 50, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 25 },
      ],
      presets: [
        { amount: 1, unit: 'egg', grams: 50, label: '1 egg (50g)' },
        { amount: 2, unit: 'eggs', grams: 100, label: '2 eggs (100g)' },
        { amount: 3, unit: 'eggs', grams: 150, label: '3 eggs (150g)' },
      ],
    };
  }

  // 8. Nuts
  if (/almond|walnut|pecan|peanut|cashew|hazelnut|pistachio|macadamia/i.test(lower)) {
    return {
      defaultAmount: 10,
      defaultUnit: 'nuts',
      defaultGrams: 12,
      monashServingText: 'Monash Safe limit: ~10 nuts (12g)',
      monashSafeGrams: 12,
      units: [
        { unit: 'nuts', label: 'Nuts', gramsPerUnit: 1.2, step: 5 },
        { unit: 'handful', label: 'Handful', gramsPerUnit: 30, step: 0.5 },
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 15, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 5 },
      ],
      presets: [
        { amount: 10, unit: 'nuts', grams: 12, label: '10 nuts (12g) - Safe' },
        { amount: 15, unit: 'nuts', grams: 18, label: '15 nuts (18g)' },
        { amount: 1, unit: 'handful', grams: 30, label: '1 handful (30g)' },
        { amount: 1, unit: 'tbsp', grams: 15, label: '1 tbsp (15g)' },
      ],
    };
  }

  // 9. Seeds
  if (/seed|chia|pepita|flax|linseed|sesame/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'tbsp',
      defaultGrams: 12,
      monashServingText: 'Monash Safe limit: 1-2 tbsp (12-24g)',
      monashSafeGrams: 12,
      units: [
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 12, step: 0.5 },
        { unit: 'tsp', label: 'Tsp', gramsPerUnit: 4, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 5 },
      ],
      presets: [
        { amount: 1, unit: 'tsp', grams: 4, label: '1 tsp (4g)' },
        { amount: 1, unit: 'tbsp', grams: 12, label: '1 tbsp (12g) - Safe' },
        { amount: 2, unit: 'tbsp', grams: 24, label: '2 tbsp (24g)' },
      ],
    };
  }

  // 10. Milk / Plant Milk
  if (/milk|creamer/i.test(lower)) {
    const isCow = /cow|whole|skim|semi/i.test(lower);
    return {
      defaultAmount: 1,
      defaultUnit: 'cup',
      defaultGrams: 250,
      monashServingText: isCow ? 'High Lactose (Safe: 1 tbsp / 15ml)' : '1 cup = 250ml',
      monashSafeGrams: isCow ? 15 : 250,
      units: [
        { unit: 'cup', label: 'Cup', gramsPerUnit: 250, step: 0.25 },
        { unit: 'splash', label: 'Splash', gramsPerUnit: 30, step: 1 },
        { unit: 'ml', label: 'ml', gramsPerUnit: 1, step: 25 },
      ],
      presets: [
        { amount: 1, unit: 'splash', grams: 30, label: '1 splash (30ml)' },
        { amount: 0.25, unit: 'cup', grams: 60, label: '1/4 cup (60ml)' },
        { amount: 0.5, unit: 'cup', grams: 125, label: '1/2 cup (125ml)' },
        { amount: 1, unit: 'cup', grams: 250, label: '1 cup (250ml)' },
      ],
    };
  }

  // 11. Garlic / Aromatics
  if (/garlic/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'clove',
      defaultGrams: 3,
      monashServingText: '1 clove = 3g (High Fructans in elimination)',
      monashSafeGrams: 0,
      units: [
        { unit: 'clove', label: 'Clove', gramsPerUnit: 3, step: 0.5 },
        { unit: 'tsp', label: 'Tsp', gramsPerUnit: 4, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 1 },
      ],
      presets: [
        { amount: 0.5, unit: 'clove', grams: 1.5, label: '1/2 clove (1.5g)' },
        { amount: 1, unit: 'clove', grams: 3, label: '1 clove (3g)' },
        { amount: 2, unit: 'cloves', grams: 6, label: '2 cloves (6g)' },
      ],
    };
  }

  // 12. Cheese
  if (/cheese|parmesan|cheddar|feta|mozzarella/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'slice',
      defaultGrams: 25,
      monashServingText: 'Monash Safe limit: 40g (low/trace lactose)',
      monashSafeGrams: 40,
      units: [
        { unit: 'slice', label: 'Slice', gramsPerUnit: 25, step: 1 },
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 10, step: 1 },
        { unit: 'cube', label: 'Cube', gramsPerUnit: 30, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 1, unit: 'slice', grams: 25, label: '1 slice (25g)' },
        { amount: 2, unit: 'slices', grams: 50, label: '2 slices (50g)' },
        { amount: 2, unit: 'tbsp', grams: 20, label: '2 tbsp grated (20g)' },
        { amount: 40, unit: 'g', grams: 40, label: '40g (Safe limit)' },
      ],
    };
  }

  // 13. Celery
  if (/celery/i.test(lower)) {
    return {
      defaultAmount: 0.25,
      defaultUnit: 'stick',
      defaultGrams: 10,
      monashServingText: 'Monash Safe limit: 1/4 stick (10g) • >1/3 stick is High Mannitol',
      monashSafeGrams: 10,
      units: [
        { unit: 'stick', label: 'Stick', gramsPerUnit: 40, step: 0.25 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 5 },
      ],
      presets: [
        { amount: 0.25, unit: 'stick', grams: 10, label: '1/4 stick (10g) - Safe' },
        { amount: 0.5, unit: 'stick', grams: 20, label: '1/2 stick (20g) - Caution' },
        { amount: 1, unit: 'stick', grams: 40, label: '1 stick (40g) - High Mannitol' },
        { amount: 2, unit: 'sticks', grams: 80, label: '2 sticks (80g)' },
      ],
    };
  }

  // 14. Banana
  if (/banana/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'banana',
      defaultGrams: 100,
      monashServingText: 'Firm banana: 1 medium (100g) safe • Ripe banana: safe at 35g',
      monashSafeGrams: 100,
      units: [
        { unit: 'banana', label: 'Banana', gramsPerUnit: 100, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 0.5, unit: 'banana', grams: 50, label: '1/2 banana (50g)' },
        { amount: 1, unit: 'banana', grams: 100, label: '1 banana (100g) - Safe (Firm)' },
        { amount: 2, unit: 'bananas', grams: 200, label: '2 bananas (200g)' },
      ],
    };
  }

  // 15. Carrot
  if (/carrot/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'carrot',
      defaultGrams: 60,
      monashServingText: '1 medium carrot = 60g (FODMAP-free, safe in any portion)',
      monashSafeGrams: 60,
      units: [
        { unit: 'carrot', label: 'Carrot', gramsPerUnit: 60, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 0.5, unit: 'carrot', grams: 30, label: '1/2 carrot (30g)' },
        { amount: 1, unit: 'carrot', grams: 60, label: '1 carrot (60g) - Safe' },
        { amount: 2, unit: 'carrots', grams: 120, label: '2 carrots (120g)' },
      ],
    };
  }

  // 16. Tomato
  if (/tomato/i.test(lower)) {
    const isCherry = /cherry/i.test(lower);
    if (isCherry) {
      return {
        defaultAmount: 3,
        defaultUnit: 'tomatoes',
        defaultGrams: 45,
        monashServingText: 'Monash Safe limit: 3 cherry tomatoes (45g) • Fructose above 45g',
        monashSafeGrams: 45,
        units: [
          { unit: 'tomatoes', label: 'Tomatoes', gramsPerUnit: 15, step: 1 },
          { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
        ],
        presets: [
          { amount: 3, unit: 'tomatoes', grams: 45, label: '3 cherry tomatoes (45g) - Safe' },
          { amount: 5, unit: 'tomatoes', grams: 75, label: '5 cherry tomatoes (75g)' },
        ],
      };
    }
    return {
      defaultAmount: 0.5,
      defaultUnit: 'tomato',
      defaultGrams: 50,
      monashServingText: 'Monash Safe limit: 1/2 medium tomato (50g) • Fructose above 75g',
      monashSafeGrams: 50,
      units: [
        { unit: 'tomato', label: 'Tomato', gramsPerUnit: 100, step: 0.25 },
        { unit: 'slice', label: 'Slice', gramsPerUnit: 20, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 },
      ],
      presets: [
        { amount: 0.5, unit: 'tomato', grams: 50, label: '1/2 tomato (50g) - Safe' },
        { amount: 1, unit: 'tomato', grams: 100, label: '1 whole tomato (100g)' },
        { amount: 2, unit: 'slices', grams: 40, label: '2 slices (40g)' },
      ],
    };
  }

  // 17. Sausage
  if (/sausage/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'sausage',
      defaultGrams: 50,
      monashServingText: '1 sausage = 50g (Check garlic/onion seasonings)',
      monashSafeGrams: 50,
      units: [
        { unit: 'sausage', label: 'Sausage', gramsPerUnit: 50, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 25 },
      ],
      presets: [
        { amount: 1, unit: 'sausage', grams: 50, label: '1 sausage (50g)' },
        { amount: 2, unit: 'sausages', grams: 100, label: '2 sausages (100g)' },
        { amount: 3, unit: 'sausages', grams: 150, label: '3 sausages (150g)' },
      ],
    };
  }

  // 18. Sweet Potato
  if (/sweet potato|yam|kumara|satsuma/i.test(lower)) {
    return {
      defaultAmount: 0.5,
      defaultUnit: 'cup',
      defaultGrams: 75,
      monashServingText: 'Monash Safe limit: 1/2 cup diced (75g) • Moderate Mannitol at 100g',
      monashSafeGrams: 75,
      units: [
        { unit: 'cup', label: 'Cup (cubed)', gramsPerUnit: 150, step: 0.25 },
        { unit: 'potato', label: 'Medium Sweet Potato', gramsPerUnit: 130, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 25 },
      ],
      presets: [
        { amount: 0.5, unit: 'cup', grams: 75, label: '1/2 cup (75g) - Safe' },
        { amount: 0.75, unit: 'cup', grams: 110, label: '3/4 cup (110g) - Moderate' },
        { amount: 1, unit: 'cup', grams: 150, label: '1 cup (150g) - High' },
      ],
    };
  }

  // 19. Potato
  if (/potato|potatoes|hash brown|french fries|rosti|gnocchi/i.test(lower) && !/sweet potato|yam|kumara/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'potato',
      defaultGrams: 150,
      monashServingText: '1 medium potato = 150g (0g FODMAPs, pure starch, eat freely)',
      monashSafeGrams: 150,
      units: [
        { unit: 'potato', label: 'Potato', gramsPerUnit: 150, step: 0.5 },
        { unit: 'cup', label: 'Cup', gramsPerUnit: 150, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 25 },
      ],
      presets: [
        { amount: 0.5, unit: 'potato', grams: 75, label: '1/2 potato (75g)' },
        { amount: 1, unit: 'potato', grams: 150, label: '1 potato (150g) - Safe' },
        { amount: 2, unit: 'potatoes', grams: 300, label: '2 potatoes (300g)' },
      ],
    };
  }

  // 20. Espresso & Specialty Coffees
  if (/^espresso\b|^double espresso\b|\bristretto\b/i.test(lower)) {
    const isDouble = /double/i.test(lower);
    return {
      defaultAmount: isDouble ? 2 : 1,
      defaultUnit: isDouble ? 'shots' : 'shot',
      defaultGrams: isDouble ? 60 : 30,
      monashServingText: '1 single shot = 30ml (0g FODMAPs)',
      monashSafeGrams: 30,
      units: [
        { unit: 'shot', label: 'Single Shot', gramsPerUnit: 30, step: 1 },
        { unit: 'double', label: 'Double Shot', gramsPerUnit: 60, step: 1 },
        { unit: 'ml', label: 'ml', gramsPerUnit: 1, step: 15 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 15 },
      ],
      presets: [
        { amount: 1, unit: 'shot', grams: 30, label: '1 single shot (30ml)' },
        { amount: 2, unit: 'shots', grams: 60, label: '1 double shot (60ml)' },
        { amount: 3, unit: 'shots', grams: 90, label: 'Triple shot (90ml)' },
      ],
    };
  }

  // 21. Brewed Coffee & Tea
  if (/coffee|americano|filter coffee|brewed coffee|tea|earl grey|chai|peppermint tea|green tea|chamomile/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'cup',
      defaultGrams: 240,
      monashServingText: '1 cup / mug = 240ml',
      monashSafeGrams: 240,
      units: [
        { unit: 'cup', label: 'Cup / Mug', gramsPerUnit: 240, step: 0.5 },
        { unit: 'splash', label: 'Splash', gramsPerUnit: 30, step: 1 },
        { unit: 'ml', label: 'ml', gramsPerUnit: 1, step: 50 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 50 },
      ],
      presets: [
        { amount: 1, unit: 'cup', grams: 240, label: '1 cup (240ml)' },
        { amount: 1.5, unit: 'cups', grams: 360, label: 'Large mug (360ml)' },
        { amount: 2, unit: 'cups', grams: 480, label: '2 cups (480ml)' },
      ],
    };
  }

  // 22. Oils, Butter & Fats
  if (/oil|olive oil|butter|ghee/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'tbsp',
      defaultGrams: 14,
      monashServingText: '1 tbsp = 14g (FODMAP-free fat)',
      monashSafeGrams: 14,
      units: [
        { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 14, step: 0.5 },
        { unit: 'tsp', label: 'Tsp', gramsPerUnit: 5, step: 1 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 5 },
      ],
      presets: [
        { amount: 1, unit: 'tsp', grams: 5, label: '1 tsp (5g)' },
        { amount: 1, unit: 'tbsp', grams: 14, label: '1 tbsp (14g) - Standard' },
        { amount: 2, unit: 'tbsp', grams: 28, label: '2 tbsp (28g)' },
      ],
    };
  }

  // 23. Chicken & Poultry Fillets
  if (/chicken|turkey breast|chicken breast|chicken fillet/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'fillet',
      defaultGrams: 140,
      monashServingText: '1 fillet = 140g (0g FODMAPs, pure protein)',
      monashSafeGrams: 140,
      units: [
        { unit: 'fillet', label: 'Fillet / Breast', gramsPerUnit: 140, step: 0.5 },
        { unit: 'palm', label: 'Palm Size', gramsPerUnit: 120, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 25 },
      ],
      presets: [
        { amount: 0.5, unit: 'fillet', grams: 70, label: '1/2 fillet (70g)' },
        { amount: 1, unit: 'fillet', grams: 140, label: '1 fillet (140g) - Standard' },
        { amount: 1.5, unit: 'fillets', grams: 210, label: '1.5 fillets (210g)' },
      ],
    };
  }

  // 24. Fish & Salmon Fillets
  if (/salmon|cod|tuna steak|fish fillet|haddock|sea bass/i.test(lower)) {
    return {
      defaultAmount: 1,
      defaultUnit: 'fillet',
      defaultGrams: 130,
      monashServingText: '1 fillet = 130g (0g FODMAPs, pure protein)',
      monashSafeGrams: 130,
      units: [
        { unit: 'fillet', label: 'Fillet', gramsPerUnit: 130, step: 0.5 },
        { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 25 },
      ],
      presets: [
        { amount: 0.5, unit: 'fillet', grams: 65, label: '1/2 fillet (65g)' },
        { amount: 1, unit: 'fillet', grams: 130, label: '1 fillet (130g) - Standard' },
        { amount: 2, unit: 'fillets', grams: 260, label: '2 fillets (260g)' },
      ],
    };
  }

  // 25. Fallback to matched food or realistic category fallback
  const rawGrams = matched?.servings?.low?.grams || matchedFood?.servings?.low?.grams || 100;
  // Sanitize 500g Monash disclaimer ceiling
  const defaultGrams = rawGrams >= 400 ? 100 : rawGrams;
  const safeText = matched?.servings?.low?.title || matchedFood?.servings?.low?.title
    ? `Monash serving: ${matched?.servings?.low?.title || matchedFood?.servings?.low?.title}`
    : `1 serving = ${defaultGrams}g`;

  return {
    defaultAmount: 1,
    defaultUnit: 'serving',
    defaultGrams,
    monashServingText: safeText,
    monashSafeGrams: defaultGrams,
    units: [
      { unit: 'serving', label: 'Serving', gramsPerUnit: defaultGrams, step: 0.5 },
      { unit: 'tbsp', label: 'Tbsp', gramsPerUnit: 15, step: 1 },
      { unit: 'tsp', label: 'Tsp', gramsPerUnit: 5, step: 1 },
      { unit: 'cup', label: 'Cup', gramsPerUnit: 125, step: 0.25 },
      { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 25 },
    ],
    presets: [
      { amount: 0.5, unit: 'serving', grams: Math.max(1, Math.round(defaultGrams * 0.5)), label: `1/2 serving (${Math.max(1, Math.round(defaultGrams * 0.5))}g)` },
      { amount: 1, unit: 'serving', grams: defaultGrams, label: `1 serving (${defaultGrams}g)` },
      { amount: 2, unit: 'servings', grams: defaultGrams * 2, label: `2 servings (${defaultGrams * 2}g)` },
    ],
  };
}

/**
 * Formats an ingredient portion into an intuitive human-readable string (e.g. "1 rasher (20g)", "3 tbsp (60g)", "1/4 stick (10g)").
 */
export function formatIngredientPortion(
  amount: number,
  unit: string,
  gramsEstimate: number,
  multiplier = 1.0
): string {
  const effGrams = Math.round((gramsEstimate || 100) * multiplier);
  const rawAmount = (amount || 1) * multiplier;
  const rounded2 = Math.round(rawAmount * 100) / 100;
  const cleanUnit = (unit || 'g').toLowerCase().trim();

  if (
    cleanUnit === 'g' ||
    cleanUnit === 'gram' ||
    cleanUnit === 'grams' ||
    ((cleanUnit === 'tbsp' || cleanUnit === 'tsp' || cleanUnit === 'cup' || cleanUnit === 'serving') &&
      rawAmount >= 15 &&
      Math.abs(rawAmount - effGrams) <= 1)
  ) {
    return `${effGrams}g`;
  }

  let amountStr = '';
  if (Math.abs(rounded2 - 0.25) < 0.01) amountStr = '1/4';
  else if (Math.abs(rounded2 - 0.5) < 0.01) amountStr = '1/2';
  else if (Math.abs(rounded2 - 0.75) < 0.01) amountStr = '3/4';
  else if (Math.abs(rounded2 - 1.5) < 0.01) amountStr = '1 1/2';
  else amountStr = (Math.round(rawAmount * 10) / 10).toString();

  const effAmount = rounded2;
  let unitStr = cleanUnit;
  if (effAmount > 1) {
    if (cleanUnit === 'rasher') unitStr = 'rashers';
    else if (cleanUnit === 'slice') unitStr = 'slices';
    else if (cleanUnit === 'piece') unitStr = 'pieces';
    else if (cleanUnit === 'egg') unitStr = 'eggs';
    else if (cleanUnit === 'stick') unitStr = 'sticks';
    else if (cleanUnit === 'clove') unitStr = 'cloves';
    else if (cleanUnit === 'cup') unitStr = 'cups';
    else if (cleanUnit === 'pot') unitStr = 'pots';
    else if (cleanUnit === 'portion') unitStr = 'portions';
    else if (cleanUnit === 'banana') unitStr = 'bananas';
    else if (cleanUnit === 'carrot') unitStr = 'carrots';
    else if (cleanUnit === 'tomato') unitStr = 'tomatoes';
    else if (cleanUnit === 'sausage') unitStr = 'sausages';
    else if (cleanUnit === 'potato') unitStr = 'potatoes';
    else if (cleanUnit === 'teaspoon') unitStr = 'teaspoons';
    else if (cleanUnit === 'tablespoon') unitStr = 'tablespoons';
  } else if (effAmount <= 1) {
    if (cleanUnit === 'rashers') unitStr = 'rasher';
    else if (cleanUnit === 'slices') unitStr = 'slice';
    else if (cleanUnit === 'pieces') unitStr = 'piece';
    else if (cleanUnit === 'eggs') unitStr = 'egg';
    else if (cleanUnit === 'sticks') unitStr = 'stick';
    else if (cleanUnit === 'cloves') unitStr = 'clove';
    else if (cleanUnit === 'cups') unitStr = 'cup';
    else if (cleanUnit === 'pots') unitStr = 'pot';
    else if (cleanUnit === 'portions') unitStr = 'portion';
    else if (cleanUnit === 'bananas') unitStr = 'banana';
    else if (cleanUnit === 'carrots') unitStr = 'carrot';
    else if (cleanUnit === 'tomatoes') unitStr = 'tomato';
    else if (cleanUnit === 'sausages') unitStr = 'sausage';
    else if (cleanUnit === 'potatoes') unitStr = 'potato';
    else if (cleanUnit === 'teaspoons') unitStr = 'teaspoon';
    else if (cleanUnit === 'tablespoons') unitStr = 'tablespoon';
  }

  return `${amountStr} ${unitStr} (${effGrams}g)`;
}

/**
 * Returns formatted text for the right-side portion button in ingredient rows.
 */
export function formatPortionButton(
  amount: number,
  unit: string,
  gramsEstimate: number,
  multiplier = 1.0
): { main: string; sub: string } {
  const effGrams = Math.round((gramsEstimate || 100) * multiplier);
  const rawAmount = (amount || 1) * multiplier;
  const rounded2 = Math.round(rawAmount * 100) / 100;
  const cleanUnit = (unit || 'g').toLowerCase().trim();

  if (
    cleanUnit === 'g' ||
    cleanUnit === 'gram' ||
    cleanUnit === 'grams' ||
    ((cleanUnit === 'tbsp' || cleanUnit === 'tsp' || cleanUnit === 'cup' || cleanUnit === 'serving') &&
      rawAmount >= 15 &&
      Math.abs(rawAmount - effGrams) <= 1)
  ) {
    return { main: `${effGrams}g`, sub: 'Edit' };
  }

  let amountStr = '';
  if (Math.abs(rounded2 - 0.25) < 0.01) amountStr = '1/4';
  else if (Math.abs(rounded2 - 0.5) < 0.01) amountStr = '1/2';
  else if (Math.abs(rounded2 - 0.75) < 0.01) amountStr = '3/4';
  else if (Math.abs(rounded2 - 1.5) < 0.01) amountStr = '1 1/2';
  else amountStr = (Math.round(rawAmount * 10) / 10).toString();

  const effAmount = rounded2;
  let unitStr = cleanUnit;
  if (effAmount > 1) {
    if (cleanUnit === 'rasher') unitStr = 'rashers';
    else if (cleanUnit === 'slice') unitStr = 'slices';
    else if (cleanUnit === 'piece') unitStr = 'pieces';
    else if (cleanUnit === 'egg') unitStr = 'eggs';
    else if (cleanUnit === 'stick') unitStr = 'sticks';
    else if (cleanUnit === 'clove') unitStr = 'cloves';
    else if (cleanUnit === 'cup') unitStr = 'cups';
    else if (cleanUnit === 'pot') unitStr = 'pots';
    else if (cleanUnit === 'portion') unitStr = 'portions';
    else if (cleanUnit === 'banana') unitStr = 'bananas';
    else if (cleanUnit === 'carrot') unitStr = 'carrots';
    else if (cleanUnit === 'tomato') unitStr = 'tomatoes';
    else if (cleanUnit === 'sausage') unitStr = 'sausages';
    else if (cleanUnit === 'potato') unitStr = 'potatoes';
    else if (cleanUnit === 'teaspoon') unitStr = 'teaspoons';
    else if (cleanUnit === 'tablespoon') unitStr = 'tablespoons';
  } else if (effAmount <= 1) {
    if (cleanUnit === 'rashers') unitStr = 'rasher';
    else if (cleanUnit === 'slices') unitStr = 'slice';
    else if (cleanUnit === 'pieces') unitStr = 'piece';
    else if (cleanUnit === 'eggs') unitStr = 'egg';
    else if (cleanUnit === 'sticks') unitStr = 'stick';
    else if (cleanUnit === 'cloves') unitStr = 'clove';
    else if (cleanUnit === 'cups') unitStr = 'cup';
    else if (cleanUnit === 'pots') unitStr = 'pot';
    else if (cleanUnit === 'portions') unitStr = 'portion';
    else if (cleanUnit === 'bananas') unitStr = 'banana';
    else if (cleanUnit === 'carrots') unitStr = 'carrot';
    else if (cleanUnit === 'tomatoes') unitStr = 'tomato';
    else if (cleanUnit === 'sausages') unitStr = 'sausage';
    else if (cleanUnit === 'potatoes') unitStr = 'potato';
    else if (cleanUnit === 'teaspoons') unitStr = 'teaspoon';
    else if (cleanUnit === 'tablespoons') unitStr = 'tablespoon';
  }

  return { main: `${amountStr} ${unitStr}`, sub: `(${effGrams}g)` };
}

/**
 * Safely splits top-level ingredient tags without breaking commas inside parentheses or brackets.
 */
export function splitTopLevelIngredients(text: string): string[] {
  if (!text || typeof text !== 'string') return [];
  const results: string[] = [];
  let current = '';
  let parenDepth = 0;
  let bracketDepth = 0;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '(') parenDepth++;
    else if (char === ')') parenDepth = Math.max(0, parenDepth - 1);
    else if (char === '[') bracketDepth++;
    else if (char === ']') bracketDepth = Math.max(0, bracketDepth - 1);

    if ((char === ',' || char === ';' || char === '\n') && parenDepth === 0 && bracketDepth === 0) {
      const trimmed = current.trim();
      if (trimmed) results.push(trimmed);
      current = '';
    } else {
      current += char;
    }
  }
  const lastTrimmed = current.trim();
  if (lastTrimmed) results.push(lastTrimmed);

  return results;
}

/**
 * Decomposes composite sauces, dressings, and recipes with sub-ingredients
 * e.g. "Caesar Dressing (Rapeseed oil, egg yolk, parmesan cheese, lemon juice, garlic purée, anchovy paste...)"
 */
export function decomposeCompositeIngredient(rawText: string): {
  parentName: string;
  subIngredients: string[];
} {
  if (!rawText) return { parentName: '', subIngredients: [] };
  const trimmed = rawText.trim();

  // Pattern: "Name (sub1, sub2, sub3)" or "Name (15%) (sub1, sub2...)"
  const firstParenIdx = trimmed.indexOf('(');
  if (firstParenIdx > 0 && trimmed.endsWith(')')) {
    const match = trimmed.match(/^([^()]+(?:\s*\(\d+%\))?)\s*\((.*)\)$/);
    if (match) {
      const parentName = match[1].replace(/\s*\(\d+%\)/, '').trim();
      const inner = match[2].trim();
      const subs = splitTopLevelIngredients(inner);
      if (subs.length > 1) {
        return { parentName, subIngredients: subs };
      }
    }
  }

  // Pattern: "Name [sub1, sub2, sub3]" or "Name (15%) [sub1, sub2...]"
  const firstBracketIdx = trimmed.indexOf('[');
  if (firstBracketIdx > 0 && trimmed.endsWith(']')) {
    const match = trimmed.match(/^([^\[\]]+(?:\s*\(\d+%\))?)\s*\[(.*)\]$/);
    if (match) {
      const parentName = match[1].replace(/\s*\(\d+%\)/, '').trim();
      const inner = match[2].trim();
      const subs = splitTopLevelIngredients(inner);
      if (subs.length > 1) {
        return { parentName, subIngredients: subs };
      }
    }
  }

  // Pattern: "Name: sub1, sub2, sub3"
  if (trimmed.includes(':')) {
    const colonIdx = trimmed.indexOf(':');
    const parentName = trimmed.slice(0, colonIdx).trim();
    const rest = trimmed.slice(colonIdx + 1).trim();
    const subs = splitTopLevelIngredients(rest);
    if (subs.length > 1) {
      return { parentName, subIngredients: subs };
    }
  }

  return { parentName: trimmed, subIngredients: [] };
}

/**
 * Safely parses ingredient strings, protecting inverted database food names
 * (e.g., "Sugar, white", "Sugar, brown", "Oil, olive", "Pepper, bell, red") and parenthesized sauce lists.
 */
export function splitIngredientTags(text: string): string[] {
  if (!text || typeof text !== 'string') return [];
  ensureAliasIndex();
  const topLevel = splitTopLevelIngredients(text);
  const result: string[] = [];

  for (const part of topLevel) {
    // If it is a composite ingredient with parentheses or brackets, keep it intact
    if (part.includes('(') && part.includes(')')) {
      result.push(part);
      continue;
    }

    const rawParts = part.split(',').map((s) => s.trim()).filter(Boolean);
    for (let i = 0; i < rawParts.length; i++) {
      const current = rawParts[i];
      const next = rawParts[i + 1];

      if (next) {
        const combined = `${current}, ${next}`;
        if (
          aliasToFoodItemMap.has(combined.toLowerCase()) ||
          cachedFoodItems.some((f) => f.name.toLowerCase() === combined.toLowerCase())
        ) {
          result.push(combined);
          i++; // skip next
          continue;
        }
      }
      result.push(current);
    }
  }

  return result;
}

export interface SafePortionBrackets {
  safeGrams: number;
  moderateMaxGrams: number;
  highMinGrams: number;
  safeRange: string;
  moderateRange: string;
  highRange: string;
  primaryCulprit?: string;
  isFodmapFree?: boolean;
}

/**
 * Computes deterministic safe, moderate, and high portion brackets (in grams)
 * from the open chemical composition database and Varney et al. (2017) clinical cutoffs.
 */
export function getSafePortionBrackets(food: FodmapFoodItem | IngredientEvaluation | string): SafePortionBrackets {
  let matched: FodmapFoodItem | undefined;
  if (typeof food === 'string') {
    matched = matchIngredientToFodmap(food).matchedFood;
  } else if (food && 'matchedFood' in food) {
    matched = food.matchedFood;
  } else if (food) {
    matched = food as FodmapFoodItem;
  }

  if (!matched) {
    return {
      safeGrams: 75,
      moderateMaxGrams: 120,
      highMinGrams: 121,
      safeRange: '0g – 75g',
      moderateRange: '76g – 120g',
      highRange: '> 120g',
    };
  }

  const isZero = isZeroFodmapFood(matched.name);
  if (isZero || matched.category === 'meat' || matched.category === 'oils') {
    return {
      safeGrams: 500,
      moderateMaxGrams: 500,
      highMinGrams: 500,
      safeRange: 'Generous serving safe (0g FODMAPs)',
      moderateRange: 'N/A',
      highRange: 'N/A',
      isFodmapFree: true,
    };
  }

  const servings = matched.servings;
  let safeG = servings?.low?.grams;
  let modG = servings?.moderate?.grams;
  let highG = servings?.high?.grams;

  // Derive from compositionPer100g if available
  if (!safeG && matched.compositionPer100g) {
    const comp = matched.compositionPer100g;
    const cat = matched.category || 'vegetables';
    const isGrain = cat === 'grains';
    const maxFructanCutoff = isGrain ? 0.30 : 0.20;

    let minGramsForLimit = 500;
    let limitCulprit = matched.primaryCulprit;
    const fructanVal = comp.fructansG || 0;
    if (fructanVal > 0) {
      const safeCalc = Math.floor((maxFructanCutoff / fructanVal) * 100);
      if (safeCalc < minGramsForLimit) { minGramsForLimit = safeCalc; limitCulprit = 'Fructans'; }
    }
    if (comp.gosG && comp.gosG > 0) {
      const safeCalc = Math.floor((0.20 / comp.gosG) * 100);
      if (safeCalc < minGramsForLimit) { minGramsForLimit = safeCalc; limitCulprit = 'GOS'; }
    }
    if (comp.excessFructoseG && comp.excessFructoseG > 0) {
      const fructoseCutoff = isGrain ? 0.15 : 0.50;
      const safeCalc = Math.floor((fructoseCutoff / comp.excessFructoseG) * 100);
      if (safeCalc < minGramsForLimit) { minGramsForLimit = safeCalc; limitCulprit = 'Excess Fructose'; }
    }
    if (comp.sorbitolG && comp.sorbitolG > 0) {
      const safeCalc = Math.floor((0.20 / comp.sorbitolG) * 100);
      if (safeCalc < minGramsForLimit) { minGramsForLimit = safeCalc; limitCulprit = 'Sorbitol'; }
    }
    if (comp.mannitolG && comp.mannitolG > 0) {
      const safeCalc = Math.floor((0.20 / comp.mannitolG) * 100);
      if (safeCalc < minGramsForLimit) { minGramsForLimit = safeCalc; limitCulprit = 'Mannitol'; }
    }
    if (comp.lactoseG && comp.lactoseG > 0) {
      const safeCalc = Math.floor((1.00 / comp.lactoseG) * 100);
      if (safeCalc < minGramsForLimit) { minGramsForLimit = safeCalc; limitCulprit = 'Lactose'; }
    }

    if (minGramsForLimit < 500) {
      safeG = Math.max(5, minGramsForLimit);
      modG = Math.round(safeG * 1.6);
      highG = modG + 1;
    }
  }

  if (!safeG) {
    if (matched.rating === 'high') {
      safeG = 15;
      modG = 30;
      highG = 31;
    } else if (matched.rating === 'moderate') {
      safeG = 45;
      modG = 75;
      highG = 76;
    } else {
      safeG = 75;
      modG = 125;
      highG = 126;
    }
  }

  if (!modG) modG = Math.round(safeG * 1.5);
  if (!highG) highG = modG + 1;

  return {
    safeGrams: safeG,
    moderateMaxGrams: modG,
    highMinGrams: highG,
    safeRange: `0g – ${safeG}g`,
    moderateRange: `${safeG + 1}g – ${modG}g`,
    highRange: `> ${modG}g`,
    primaryCulprit: matched.primaryCulprit,
  };
}

export interface FodmapSideScaleItem {
  id: 'fructose' | 'lactose' | 'sorbitol' | 'mannitol' | 'fructans' | 'gos';
  name: string;
  chemicalName: string;
  rating: FodmapRating;
  level: 1 | 2 | 3;
  grams: number;
  cutoffGrams: number;
  percentage: number; // 0 to 100+
}

export interface FodmapSideScaleReport {
  overallRating: FodmapRating;
  items: FodmapSideScaleItem[];
  dominantCulprit?: string;
}

/**
 * Calculates real-time 6-FODMAP side scale values for any food item, meal, or ingredient list
 * based on Varney et al. (2017) clinical matrix thresholds.
 */
export function calculateDetailedFodmapSideScale(
  itemOrList: FodmapFoodItem | MealIngredientItem[] | MealIngredientItem | string,
  grams = 100
): FodmapSideScaleReport {
  let excessFructoseG = 0;
  let lactoseG = 0;
  let sorbitolG = 0;
  let mannitolG = 0;
  let fructanG = 0;
  let gosG = 0;

  let overallRating: FodmapRating = 'low';

  if (typeof itemOrList === 'string') {
    if (itemOrList.includes(',') || itemOrList.includes(';')) {
      const items = itemOrList.split(/[,;]/).map((t) => t.trim()).filter(Boolean);
      const ingredientItems: MealIngredientItem[] = items.map((name, idx) => ({
        id: `ing-${idx}-${name}`,
        name,
        canonicalName: name,
        amount: 50,
        unit: 'g',
        rating: 'low',
        gramsEstimate: 50,
      }));
      return calculateDetailedFodmapSideScale(ingredientItems, grams);
    }
    const matched = matchIngredientToFodmap(itemOrList).matchedFood;
    return calculateDetailedFodmapSideScale(matched || {
      id: 'custom',
      name: itemOrList,
      category: 'other',
      rating: 'low',
    }, grams);
  }

  if (Array.isArray(itemOrList)) {
    // Composite meal or recipe
    const processItem = (ing: MealIngredientItem) => {
      if (ing.subIngredients && ing.subIngredients.length > 0) {
        for (const sub of ing.subIngredients) {
          processItem(sub);
        }
        return;
      }
      const ingGrams = ing.gramsEstimate || ing.amount || 50;
      const matched = matchIngredientToFodmap(ing.canonicalName || ing.name).matchedFood;
      if (matched?.compositionPer100g) {
        const factor = ingGrams / 100;
        excessFructoseG += (matched.compositionPer100g.excessFructoseG || 0) * factor;
        lactoseG += (matched.compositionPer100g.lactoseG || 0) * factor;
        sorbitolG += (matched.compositionPer100g.sorbitolG || 0) * factor;
        mannitolG += (matched.compositionPer100g.mannitolG || 0) * factor;
        fructanG += (matched.compositionPer100g.fructansG || 0) * factor;
        gosG += (matched.compositionPer100g.gosG || 0) * factor;
      } else if (matched?.fodmaps) {
        const factor = ingGrams / 100;
        if (matched.fodmaps.fructans === 3) fructanG += 0.4 * factor;
        else if (matched.fodmaps.fructans === 2) fructanG += 0.25 * factor;

        if (matched.fodmaps.lactose === 3) lactoseG += 5.0 * factor;
        else if (matched.fodmaps.lactose === 2) lactoseG += 2.0 * factor;

        if (matched.fodmaps.gos === 3) gosG += 0.4 * factor;
        else if (matched.fodmaps.gos === 2) gosG += 0.25 * factor;

        if (matched.fodmaps.fructose === 3) excessFructoseG += 0.8 * factor;
        else if (matched.fodmaps.fructose === 2) excessFructoseG += 0.5 * factor;

        if (matched.fodmaps.sorbitol === 3) sorbitolG += 0.45 * factor;
        else if (matched.fodmaps.sorbitol === 2) sorbitolG += 0.25 * factor;

        if (matched.fodmaps.mannitol === 3) mannitolG += 0.45 * factor;
        else if (matched.fodmaps.mannitol === 2) mannitolG += 0.25 * factor;
      }
      if (ing.rating === 'high') overallRating = 'high';
      else if (ing.rating === 'moderate' && overallRating !== 'high') overallRating = 'moderate';
    };

    for (const ing of itemOrList) {
      processItem(ing);
    }
  } else {
    // Single food item or ingredient
    const singleFood = ('canonicalName' in itemOrList)
      ? matchIngredientToFodmap(itemOrList.canonicalName || itemOrList.name).matchedFood
      : (itemOrList as FodmapFoodItem);

    const effGrams = ('gramsEstimate' in itemOrList) ? (itemOrList.gramsEstimate || grams) : grams;
    const factor = effGrams / 100;

    if (singleFood?.compositionPer100g) {
      excessFructoseG = (singleFood.compositionPer100g.excessFructoseG || 0) * factor;
      lactoseG = (singleFood.compositionPer100g.lactoseG || 0) * factor;
      sorbitolG = (singleFood.compositionPer100g.sorbitolG || 0) * factor;
      mannitolG = (singleFood.compositionPer100g.mannitolG || 0) * factor;
      fructanG = (singleFood.compositionPer100g.fructansG || 0) * factor;
      gosG = (singleFood.compositionPer100g.gosG || 0) * factor;
    } else if (singleFood?.fodmaps) {
      if (singleFood.fodmaps.fructans === 3) fructanG = 0.4 * factor;
      else if (singleFood.fodmaps.fructans === 2) fructanG = 0.25 * factor;

      if (singleFood.fodmaps.lactose === 3) lactoseG = 5.0 * factor;
      else if (singleFood.fodmaps.lactose === 2) lactoseG = 2.0 * factor;

      if (singleFood.fodmaps.gos === 3) gosG = 0.4 * factor;
      else if (singleFood.fodmaps.gos === 2) gosG = 0.25 * factor;

      if (singleFood.fodmaps.fructose === 3) excessFructoseG = 0.8 * factor;
      else if (singleFood.fodmaps.fructose === 2) excessFructoseG = 0.5 * factor;

      if (singleFood.fodmaps.sorbitol === 3) sorbitolG = 0.45 * factor;
      else if (singleFood.fodmaps.sorbitol === 2) sorbitolG = 0.25 * factor;

      if (singleFood.fodmaps.mannitol === 3) mannitolG = 0.45 * factor;
      else if (singleFood.fodmaps.mannitol === 2) mannitolG = 0.25 * factor;
    }
    overallRating = singleFood?.rating || 'low';
  }

  // Clinical Cutoffs (Varney et al. 2017)
  const cutoffs = {
    fructose: 0.50,
    lactose: 1.00,
    sorbitol: 0.20,
    mannitol: 0.20,
    fructans: 0.20,
    gos: 0.20,
  };

  const calcItem = (
    id: 'fructose' | 'lactose' | 'sorbitol' | 'mannitol' | 'fructans' | 'gos',
    name: string,
    chemicalName: string,
    valG: number,
    cutoff: number
  ): FodmapSideScaleItem => {
    const rounded = Math.round(valG * 100) / 100;
    const pct = Math.min(300, Math.round((valG / cutoff) * 100));
    let rating: FodmapRating = 'low';
    let level: 1 | 2 | 3 = 1;
    if (valG > cutoff * 1.5) {
      rating = 'high';
      level = 3;
    } else if (valG > cutoff) {
      rating = 'moderate';
      level = 2;
    }
    return {
      id,
      name,
      chemicalName,
      rating,
      level,
      grams: rounded,
      cutoffGrams: cutoff,
      percentage: pct,
    };
  };

  const items: FodmapSideScaleItem[] = [
    calcItem('fructans', 'Fructans', 'Oligosaccharides', fructanG, cutoffs.fructans),
    calcItem('gos', 'GOS', 'Galacto-oligosaccharides', gosG, cutoffs.gos),
    calcItem('fructose', 'Excess Fructose', 'Monosaccharide', excessFructoseG, cutoffs.fructose),
    calcItem('lactose', 'Lactose', 'Disaccharide', lactoseG, cutoffs.lactose),
    calcItem('sorbitol', 'Polyols (Sorbitol / Perseitol)', 'Polyol', sorbitolG, cutoffs.sorbitol),
    calcItem('mannitol', 'Mannitol', 'Polyol', mannitolG, cutoffs.mannitol),
  ];

  let maxItem = items[0];
  for (const it of items) {
    if (it.percentage > maxItem.percentage) {
      maxItem = it;
    }
  }

  if (maxItem.percentage > 150) overallRating = 'high';
  else if (maxItem.percentage > 100 && overallRating !== 'high') overallRating = 'moderate';

  return {
    overallRating,
    items,
    dominantCulprit: maxItem.percentage > 50 ? maxItem.name : undefined,
  };
}

/**
 * Detects whether an ingredient is a micro-additive, humectant, condiment,
 * leavening agent, preservative, or seasoning in an ingredient list.
 */
export function isMicroAdditiveOrCondiment(name: string): boolean {
  if (!name || typeof name !== 'string') return false;
  const lower = name.toLowerCase().trim();
  if (/^e\s*\d+/i.test(lower)) return true;
  if (/sorbitol|mannitol|xylitol|maltitol|isomalt|erythritol|sucralose|aspartame|acesulfame|stevia/i.test(lower)) return true;
  if (/xanthan|guar gum|carrageenan|pectin|cellulose|locust bean|gum arabic|agar|gelatin|thickener|stabilizer|emulsifier/i.test(lower)) return true;
  if (/citric acid|ascorbic acid|lactic acid|acetic acid|malic acid|tartaric acid|acidity regulator|preservative|antioxidant/i.test(lower)) return true;
  if (/spirit vinegar|rice vinegar|distilled vinegar|white vinegar|cider vinegar|vinegar/i.test(lower)) return true;
  if (/salt|sea salt|sodium chloride|potassium chloride|baking powder|baking soda|yeast|yeast extract/i.test(lower)) return true;
  if (/flavouring|flavoring|seasoning|spice|spices|black pepper|white pepper|paprika|turmeric|chili powder/i.test(lower)) return true;
  return false;
}

/**
 * Converts a comma-separated string or array of ingredient names/tags
 * into structured MealIngredientItem objects with accurate Monash FODMAP ratings,
 * realistic culinary serving portions, macro profiles, and nested sub-ingredients for sauces.
 * 
 * Accurately prevents multi-ingredient packaged food scans (e.g. 49-ingredient sushi sets)
 * from blowing up into thousands of grams by distributing pack weight in regulatory descending order.
 */
export function convertTagsToIngredientItems(
  tags: string | string[],
  options?: { totalDishWeightGrams?: number }
): MealIngredientItem[] {
  const rawList = Array.isArray(tags)
    ? tags
        .map((s) => {
          if (typeof s === 'string') return s.trim();
          if (s && typeof s === 'object') return ((s as any).name || (s as any).raw || '').trim();
          return '';
        })
        .filter(Boolean)
    : splitIngredientTags(tags);

  if (rawList.length === 0) return [];

  // Check if we need proportional pack distribution (packaged food with >= 4 ingredients)
  const isMultiItemPack = rawList.length >= 4;
  const targetTotalGrams = options?.totalDishWeightGrams || (isMultiItemPack ? 260 : 0);

  // Pre-calculate allocated grams for top-level ingredients if multi-item pack
  let allocatedGramsMap: number[] = [];
  if (isMultiItemPack && targetTotalGrams > 0) {
    let unallocatedBudget = targetTotalGrams;
    const explicitGrams: (number | null)[] = rawList.map((raw) => {
      const p = extractPortionFromText(raw);
      return p ? p.grams : null;
    });

    // Subtract explicit grams from budget
    explicitGrams.forEach((g) => {
      if (g !== null) unallocatedBudget = Math.max(10, unallocatedBudget - g);
    });

    const unassignedIndices = rawList
      .map((_, idx) => idx)
      .filter((idx) => explicitGrams[idx] === null);

    if (unassignedIndices.length > 0) {
      // Separate micro-additives from bulk ingredients
      const microIndices: number[] = [];
      const bulkIndices: number[] = [];

      unassignedIndices.forEach((idx) => {
        const decomp = decomposeCompositeIngredient(rawList[idx]);
        const base = decomp.parentName || rawList[idx];
        if (isMicroAdditiveOrCondiment(base)) {
          microIndices.push(idx);
        } else {
          bulkIndices.push(idx);
        }
      });

      // Allocate 0.2g - 1.5g for micro additives
      let microSum = 0;
      const microGrams: Record<number, number> = {};
      microIndices.forEach((idx, mIdx) => {
        const mg = Math.max(0.2, Math.round((1.5 - mIdx * 0.15) * 10) / 10);
        microGrams[idx] = mg;
        microSum += mg;
      });

      const remainingBulkBudget = Math.max(20, unallocatedBudget - microSum);

      // Distribute remaining bulk in descending exponential order (e.g. 50%, 25%, 15%, 8%...)
      const decayWeights = bulkIndices.map((_, i) => Math.exp(-0.45 * i));
      const totalDecay = decayWeights.reduce((a, b) => a + b, 0) || 1;

      allocatedGramsMap = rawList.map((_, idx) => {
        if (explicitGrams[idx] !== null) return explicitGrams[idx]!;
        if (microGrams[idx] !== undefined) return microGrams[idx];
        const bulkPos = bulkIndices.indexOf(idx);
        if (bulkPos !== -1) {
          const share = (decayWeights[bulkPos] / totalDecay) * remainingBulkBudget;
          return Math.max(1, Math.round(share * 10) / 10);
        }
        return 10;
      });
    }
  }

  return rawList.map((rawName, index) => {
    // Check if this is a composite sauce / multi-ingredient item
    const decomp = decomposeCompositeIngredient(rawName);
    const hasSubIngredients = decomp.subIngredients.length > 0;
    const baseName = hasSubIngredients ? decomp.parentName : rawName;

    const canonical = canonicalizeIngredient(baseName);
    const ev = matchIngredientToFodmap(baseName);
    const matched = ev.matchedFood;

    // Determine parent grams
    let defaultGrams: number;
    let amount: number;
    let unit: string;
    let realisticLimitNote: string | undefined;

    const explicitPortion = extractPortionFromText(baseName);
    if (explicitPortion) {
      defaultGrams = explicitPortion.grams;
      amount = explicitPortion.amount;
      unit = explicitPortion.unit;
      realisticLimitNote = getSafeLimitNote(baseName, matched);
    } else if (allocatedGramsMap[index] !== undefined) {
      defaultGrams = allocatedGramsMap[index];
      amount = defaultGrams;
      unit = 'g';
      realisticLimitNote = getSafeLimitNote(baseName, matched);
    } else {
      const realistic = getRealisticCulinaryServing(baseName, matched);
      defaultGrams = realistic.grams;
      amount = realistic.amount;
      unit = realistic.unit;
      realisticLimitNote = realistic.servingLimitNote;
    }

    // Sub-ingredients decomposition with proportional sub-weights
    let subItems: MealIngredientItem[] | undefined;
    if (hasSubIngredients) {
      const subList = decomp.subIngredients;
      const subDecay = subList.map((_, i) => Math.exp(-0.4 * i));
      const totalSubDecay = subDecay.reduce((a, b) => a + b, 0) || 1;

      subItems = subList.map((subRaw, subIdx) => {
        const subCanon = canonicalizeIngredient(subRaw);
        const subEv = matchIngredientToFodmap(subRaw);
        const subMatched = subEv.matchedFood;

        const subExplicit = extractPortionFromText(subRaw);
        let subGrams: number;
        if (subExplicit) {
          subGrams = subExplicit.grams;
        } else if (isMicroAdditiveOrCondiment(subRaw)) {
          subGrams = Math.max(0.1, Math.round((Math.min(defaultGrams * 0.05, 1.0) / (subIdx + 1)) * 100) / 100);
        } else {
          const subShare = (subDecay[subIdx] / totalSubDecay) * defaultGrams;
          subGrams = Math.max(0.1, Math.round(subShare * 10) / 10);
        }

        const subDyn = getDynamicIngredientRating(subRaw, subGrams, subEv.rating, subMatched);

        return {
          id: `sub-ing-${Date.now()}-${index}-${subIdx}-${Math.random().toString(36).slice(2, 6)}`,
          name: formatIngredientTitle(subRaw) || subRaw.trim(),
          canonicalName: subCanon,
          amount: subGrams,
          unit: 'g',
          gramsEstimate: subGrams,
          rating: subDyn.rating,
          primaryCulprit: subDyn.primaryCulprit || subEv.primaryCulprit,
          servingLimitNote: subDyn.note || subEv.servingLimitNote,
          fodmaps: subMatched?.fodmaps,
          calories: Math.round((subGrams / 100) * 50),
          protein: Math.round((subGrams / 100) * 1 * 10) / 10,
          fat: Math.round((subGrams / 100) * 0.5 * 10) / 10,
          carbs: Math.round((subGrams / 100) * 10 * 10) / 10,
        };
      });
    }

    // Macro profile estimation per 100g scaled to defaultGrams
    let calories = 50;
    let protein = 1;
    let fat = 0.5;
    let carbs = 10;

    const lower = canonical.toLowerCase();
    if (/chicken|turkey/.test(lower)) {
      calories = 165; protein = 31; fat = 3.6; carbs = 0;
    } else if (/beef|steak/.test(lower)) {
      calories = 250; protein = 26; fat = 15; carbs = 0;
    } else if (/pork|bacon/.test(lower)) {
      calories = 240; protein = 24; fat = 16; carbs = 0;
    } else if (/salmon|tuna|fish|cod|prawn|shrimp/.test(lower)) {
      calories = 180; protein = 24; fat = 8; carbs = 0;
    } else if (/dressing|sauce|mayo/.test(lower)) {
      calories = 350; protein = 1; fat = 35; carbs = 4;
    } else if (/oil/.test(lower)) {
      calories = 884; protein = 0; fat = 100; carbs = 0;
    } else if (/butter/.test(lower)) {
      calories = 717; protein = 0.8; fat = 81; carbs = 0.1;
    } else if (/cheese|parmesan/.test(lower)) {
      calories = 380; protein = 25; fat = 30; carbs = 2;
    } else if (/egg/.test(lower)) {
      calories = 143; protein = 13; fat = 9.5; carbs = 0.7;
    } else if (/rice|quinoa/.test(lower)) {
      calories = 130; protein = 2.7; fat = 0.3; carbs = 28;
    } else if (/pasta|noodle/.test(lower)) {
      calories = 131; protein = 5; fat = 1.1; carbs = 25;
    } else if (/bread|sourdough/.test(lower)) {
      calories = 265; protein = 9; fat = 3.2; carbs = 49;
    } else if (/potato/.test(lower)) {
      calories = 87; protein = 1.9; fat = 0.1; carbs = 20;
    } else if (/spinach|kale|lettuce|arugula|rocket|cucumber/.test(lower)) {
      calories = 20; protein = 2; fat = 0.2; carbs = 3;
    }

    const scale = defaultGrams / 100;
    const itemCalories = Math.round(calories * scale);
    const itemProtein = Math.round(protein * scale * 10) / 10;
    const itemFat = Math.round(fat * scale * 10) / 10;
    const itemCarbs = Math.round(carbs * scale * 10) / 10;

    const cleanRaw = formatIngredientTitle(baseName) || baseName.trim();

    let finalRating: FodmapRating = ev.rating;
    let finalCulprit = ev.primaryCulprit;

    if (subItems && subItems.length > 0) {
      const highSub = subItems.find((s) => s.rating === 'high');
      const modSub = subItems.find((s) => s.rating === 'moderate');
      if (highSub) {
        finalRating = 'high';
        finalCulprit = highSub.primaryCulprit || 'Fructans';
      } else if (modSub) {
        finalRating = 'moderate';
        finalCulprit = modSub.primaryCulprit;
      }
    } else {
      const dynamicEval = getDynamicIngredientRating(baseName, defaultGrams, ev.rating, matched);
      finalRating = dynamicEval.rating;
      if (dynamicEval.primaryCulprit) finalCulprit = dynamicEval.primaryCulprit;
    }

    return {
      id: `ing-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`,
      name: cleanRaw,
      canonicalName: canonical,
      amount,
      unit,
      gramsEstimate: defaultGrams,
      rating: finalRating,
      primaryCulprit: finalCulprit,
      servingLimitNote: ev.servingLimitNote || realisticLimitNote,
      fodmaps: matched?.fodmaps,
      calories: itemCalories,
      protein: itemProtein,
      fat: itemFat,
      carbs: itemCarbs,
      isCertifiedSwap: isProtectedLowFodmap(baseName) || isProtectedLowFodmap(canonical),
      subIngredients: subItems,
      isExpanded: false,
    };
  });
}

/**
 * Rescales an entire dish's total weight proportionally while keeping
 * ingredient weight ratios and recalculating FODMAP evaluations accurately.
 */
export function scaleDishTotalWeight(dish: PlateDish, targetTotalGrams: number): PlateDish {
  const currentTotal = dish.ingredients.reduce((sum, ing) => sum + (ing.gramsEstimate || 0), 0);
  if (currentTotal <= 0 || targetTotalGrams <= 0) return dish;
  const ratio = targetTotalGrams / currentTotal;

  const newIngredients = dish.ingredients.map((ing) => {
    const oldGrams = ing.gramsEstimate || 100;
    const newGrams = Math.max(0.1, Math.round(oldGrams * ratio * 10) / 10);

    let newAmount = ing.amount;
    if (ing.unit === 'g' || ing.unit === 'ml' || !ing.unit) {
      newAmount = newGrams;
    }

    const dynamic = getDynamicIngredientRating(ing.name, newGrams, undefined, matchIngredientToFodmap(ing.name).matchedFood);

    let newSub = ing.subIngredients;
    if (newSub && newSub.length > 0) {
      const subTotal = newSub.reduce((s, sub) => s + (sub.gramsEstimate || 0), 0);
      const subRatio = subTotal > 0 ? newGrams / subTotal : ratio;
      newSub = newSub.map((sub) => {
        const subOldG = sub.gramsEstimate || 10;
        const subNewG = Math.max(0.05, Math.round(subOldG * subRatio * 100) / 100);
        const subDyn = getDynamicIngredientRating(sub.name, subNewG, undefined, matchIngredientToFodmap(sub.name).matchedFood);
        return {
          ...sub,
          amount: (sub.unit === 'g' || sub.unit === 'ml' || !sub.unit) ? subNewG : sub.amount,
          gramsEstimate: subNewG,
          rating: subDyn.rating,
          primaryCulprit: subDyn.primaryCulprit || sub.primaryCulprit,
        };
      });
    }

    return {
      ...ing,
      amount: newAmount,
      gramsEstimate: newGrams,
      rating: dynamic.rating,
      primaryCulprit: dynamic.primaryCulprit || ing.primaryCulprit,
      servingLimitNote: dynamic.note || ing.servingLimitNote,
      subIngredients: newSub,
      calories: ing.calories ? Math.round(ing.calories * ratio) : undefined,
      protein: ing.protein ? Math.round(ing.protein * ratio * 10) / 10 : undefined,
      fat: ing.fat ? Math.round(ing.fat * ratio * 10) / 10 : undefined,
      carbs: ing.carbs ? Math.round(ing.carbs * ratio * 10) / 10 : undefined,
    };
  });

  return {
    ...dish,
    ingredients: newIngredients,
  };
}

/**
 * Returns true if the food is pure protein, pure fat, or a zero-FODMAP staple
 * (e.g. poultry, meat, fish, eggs, oil, butter, rice, potatoes, carrots, cucumbers).
 * In Monash clinical science, these never cause or contribute to FODMAP stacking.
 */
export function isZeroFodmapFood(name: string): boolean {
  if (!name || typeof name !== 'string') return false;
  const lower = name.toLowerCase().trim();
  return (
    /^(chicken|turkey|beef|steak|pork|lamb|bacon|ham|sausage|sausages|prosciutto|pancetta|egg|eggs|salmon|tuna|cod|fish|prawn|shrimp|rice|brown rice|white rice|jasmine rice|basmati rice|potato|potatoes|carrot|carrots|cucumber|cucumbers|table sugar|oil|olive oil|butter|ghee|salt|black pepper|cheddar|cheddar cheese|parmesan|parmesan cheese|swiss cheese|pecorino|manchego|gruyere)$/i.test(
      lower
    ) ||
    /chicken breast|chicken thigh|minced beef|ground beef|sirloin|ribeye|pork sausage|beef sausage|egg yolk|egg white|boiled egg|fried egg|scrambled egg|poached egg|cheddar cheese|parmesan cheese/i.test(
      lower
    )
  );
}

/**
 * Determines which FODMAP subtypes an ingredient contributes to based on Monash testing.
 */
export function getFoodStackableSubtypes(
  foodName: string,
  matchedFood?: FodmapFoodItem,
  evPrimaryCulprit?: string
): FodmapSubtype[] {
  const lower = foodName.toLowerCase().trim();
  const subtypes: Set<FodmapSubtype> = new Set();

  if (matchedFood?.fodmaps) {
    if (matchedFood.fodmaps.fructans >= 2) subtypes.add('Fructans');
    if (matchedFood.fodmaps.gos >= 2) subtypes.add('GOS');
    if (matchedFood.fodmaps.lactose >= 2) subtypes.add('Lactose');
    if (matchedFood.fodmaps.fructose >= 2) subtypes.add('Excess Fructose');
    if (matchedFood.fodmaps.sorbitol >= 2) subtypes.add('Sorbitol');
    if (matchedFood.fodmaps.mannitol >= 2) subtypes.add('Mannitol');
  }

  const culprit = (evPrimaryCulprit || matchedFood?.notes || '').toLowerCase();
  if (culprit.includes('fructan')) subtypes.add('Fructans');
  if (culprit.includes('gos') || culprit.includes('galacto')) subtypes.add('GOS');
  if (culprit.includes('lactose')) subtypes.add('Lactose');
  if (culprit.includes('fructose')) subtypes.add('Excess Fructose');
  if (culprit.includes('sorbitol') || culprit.includes('perseitol') || culprit.includes('polyol')) subtypes.add('Sorbitol');
  if (culprit.includes('mannitol')) subtypes.add('Mannitol');

  if (/banana/i.test(lower)) {
    subtypes.add('Fructans');
    if (/\b(ripe|yellow|spots|brown)\b/i.test(lower) && !/unripe|firm|green/i.test(lower)) {
      subtypes.add('Excess Fructose');
    }
  }
  if (/oats?|porridge|granola|wheat|bread|sourdough|pasta|cereal|barley|rye|spelt/i.test(lower)) {
    subtypes.add('Fructans');
    if (/oats?|granola|cereal/i.test(lower)) subtypes.add('GOS');
  }
  if (/blueberry|blueberries|raspberry|raspberries|strawberry|strawberries|cranberr/i.test(lower)) {
    subtypes.add('Fructans');
    subtypes.add('Excess Fructose');
  }
  if (/chia|flax|linseed|pepita|pumpkin seed|sunflower seed|sesame seed/i.test(lower)) {
    subtypes.add('GOS');
  }
  if (/almond|walnut|pecan|hazelnut|cashew|pistachio|nut/i.test(lower)) {
    subtypes.add('GOS');
    if (/cashew|pistachio/i.test(lower)) subtypes.add('Fructans');
  }
  if (/celery|mushroom|cauliflower/i.test(lower)) {
    subtypes.add('Mannitol');
  }
  if (/avocado|blackberry|blackberries|cherry|cherries|plum|apricot|peach/i.test(lower)) {
    subtypes.add('Sorbitol');
  }
  if (/apple|pear|mango|watermelon|honey|agave|hfcs/i.test(lower)) {
    subtypes.add('Excess Fructose');
    if (/apple|pear/i.test(lower)) subtypes.add('Sorbitol');
  }
  if (/milk|yogurt|yoghurt|cheese|ricotta|cream|ice cream/i.test(lower) && !/lactose[- ]free/i.test(lower)) {
    if (!/cheddar|parmesan|swiss cheese|pecorino|manchego|gruyere/i.test(lower)) {
      subtypes.add('Lactose');
    }
  }
  if (/chickpea|lentil|bean|legume|soy|edamame/i.test(lower)) {
    subtypes.add('GOS');
  }
  if (/onion|garlic|shallot|leek|scallion/i.test(lower)) {
    subtypes.add('Fructans');
  }
  if (/broccoli|cabbage|brussels sprout|zucchini|courgette/i.test(lower)) {
    subtypes.add('Fructans');
  }

  return Array.from(subtypes);
}

/**
 * Dynamically determines an ingredient's traffic light rating (LOW, MED, HIGH)
 * based on the exact quantity/grams on the plate compared to Monash thresholds.
 */
export function getDynamicIngredientRating(
  ingredientName: string,
  grams: number,
  baseRating?: FodmapRating,
  matchedFood?: FodmapFoodItem
): { rating: FodmapRating; note?: string; primaryCulprit?: string } {
  if (!ingredientName) return { rating: 'low' };
  const canonical = canonicalizeIngredient(ingredientName);
  if (isZeroFodmapFood(canonical) || isZeroFodmapFood(ingredientName)) {
    return { rating: 'low', note: 'Naturally 0g FODMAPs' };
  }

  // Explicit Avocado / Guacamole Perseitol evaluation (60g low, 80g moderate, 90g+ high)
  if (/\b(avocado|guacamole)\b/i.test(ingredientName)) {
    if (grams >= 90) {
      return {
        rating: 'high',
        note: `High at ${grams}g (Polyols/Perseitol > 80g)`,
        primaryCulprit: 'Polyols (Perseitol)',
      };
    }
    if (grams > 60) {
      return {
        rating: 'moderate',
        note: `Moderate at ${grams}g (Polyols/Perseitol > 60g)`,
        primaryCulprit: 'Polyols (Perseitol)',
      };
    }
    return {
      rating: 'low',
      note: `Safe portion at ${grams}g (up to 60g)`,
      primaryCulprit: 'Polyols (Perseitol)',
    };
  }

  const ev = matchedFood
    ? { matchedFood, rating: matchedFood.rating, primaryCulprit: derivePrimaryCulprit(matchedFood.fodmaps) }
    : matchIngredientToFodmap(ingredientName);
  const matched = matchedFood || ev.matchedFood;
  const culprit = ev.primaryCulprit || derivePrimaryCulprit(matched?.fodmaps);

  const lowGrams = matched?.servings?.low?.grams;
  const modGrams = matched?.servings?.moderate?.grams;
  const highGrams = matched?.servings?.high?.grams;

  // 1. Direct Quantitative Chemical Composition Calculation
  if (matched?.compositionPer100g) {
    const comp = matched.compositionPer100g;
    const ratio = grams / 100;
    const excessFructose = (comp.excessFructoseG || 0) * ratio;
    const lactose = (comp.lactoseG || 0) * ratio;
    const sorbitol = (comp.sorbitolG || 0) * ratio;
    const mannitol = (comp.mannitolG || 0) * ratio;
    const fructans = (comp.fructansG || 0) * ratio;
    const gos = (comp.gosG || 0) * ratio;

    const isGrain = matched.category === 'grains' || /bread|flour|pasta|cereal|grain|cracker|oat|wheat|rye|barley/i.test(ingredientName);
    const fructanCutoffLow = isGrain ? 0.30 : 0.20;
    const fructanCutoffMod = isGrain ? 0.60 : 0.40;

    const scores = [
      { name: 'Fructans', ingested: fructans, low: fructanCutoffLow, mod: fructanCutoffMod },
      { name: 'GOS', ingested: gos, low: 0.20, mod: 0.40 },
      { name: 'Lactose', ingested: lactose, low: 1.00, mod: 4.00 },
      { name: 'Excess Fructose', ingested: excessFructose, low: 0.50, mod: 1.00 },
      { name: 'Sorbitol', ingested: sorbitol, low: 0.20, mod: 0.40 },
      { name: 'Mannitol', ingested: mannitol, low: 0.20, mod: 0.40 },
    ];

    let maxStatus: FodmapRating = 'low';
    let activeCulprit: string | undefined;
    let maxGramsIngested = 0;

    for (const s of scores) {
      if (s.ingested > s.mod) {
        maxStatus = 'high';
        activeCulprit = s.name;
        maxGramsIngested = s.ingested;
        break;
      } else if (s.ingested > s.low) {
        maxStatus = 'moderate';
        activeCulprit = s.name;
        maxGramsIngested = s.ingested;
      }
    }

    if (maxStatus === 'high') {
      return {
        rating: 'high',
        note: `High at ${grams}g (${Math.round(maxGramsIngested * 100) / 100}g ${activeCulprit})`,
        primaryCulprit: activeCulprit || culprit,
      };
    }
    if (maxStatus === 'moderate') {
      return {
        rating: 'moderate',
        note: `Moderate at ${grams}g (${Math.round(maxGramsIngested * 100) / 100}g ${activeCulprit})`,
        primaryCulprit: activeCulprit || culprit,
      };
    }
    if (lowGrams && lowGrams > 0) {
      return { rating: 'low', note: `Safe serve (up to ${lowGrams}g)`, primaryCulprit: culprit };
    }
    return { rating: 'low', note: `Safe portion at ${grams}g`, primaryCulprit: culprit };
  }

  // 2. Portion threshold fallback
  if (highGrams && highGrams > 0 && grams >= highGrams) {
    return { rating: 'high', note: `High at ${grams}g (Threshold: ${highGrams}g)`, primaryCulprit: culprit };
  }
  if (modGrams && modGrams > 0 && grams >= modGrams) {
    return { rating: 'moderate', note: `Moderate at ${grams}g (Caution: ${modGrams}g)`, primaryCulprit: culprit };
  }
  if (lowGrams && lowGrams > 0 && grams <= lowGrams) {
    return { rating: 'low', note: `Safe serve (Safe up to ${lowGrams}g)`, primaryCulprit: culprit };
  }

  // If the food has a base rating of moderate or high (e.g. avocado, almonds, legumes), apply portion scaling
  if (matched?.rating === 'high' || matched?.rating === 'moderate' || baseRating === 'high' || baseRating === 'moderate') {
    if (lowGrams && lowGrams > 0) {
      if (grams > lowGrams * 2.0) {
        return { rating: 'high', note: `High at ${grams}g (Safe limit: ${lowGrams}g)`, primaryCulprit: culprit };
      }
      if (grams > lowGrams * 1.2) {
        return { rating: 'moderate', note: `Moderate at ${grams}g (Safe limit: ${lowGrams}g)`, primaryCulprit: culprit };
      }
      return { rating: 'low', note: `Safe up to ${lowGrams}g`, primaryCulprit: culprit };
    }
    return { rating: matched?.rating || baseRating || 'moderate', primaryCulprit: culprit };
  }

  const lower = canonical.toLowerCase();
  if (/flax|linseed/i.test(lower)) {
    if (grams > 30) return { rating: 'high', note: 'High GOS above 30g (2 tbsp)', primaryCulprit: 'GOS' };
    if (grams > 15) return { rating: 'moderate', note: 'Moderate GOS above 15g (1 tbsp)', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe Low FODMAP up to 15g (1 tbsp)', primaryCulprit: 'GOS' };
  }
  if (/chia/i.test(lower)) {
    if (grams > 48) return { rating: 'high', note: 'High GOS above 48g', primaryCulprit: 'GOS' };
    if (grams > 24) return { rating: 'moderate', note: 'Moderate GOS above 24g (2 tbsp)', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe Low FODMAP up to 24g (2 tbsp)', primaryCulprit: 'GOS' };
  }
  if (/sunflower seed/i.test(lower)) {
    if (grams > 50) return { rating: 'high', note: 'High GOS above 50g', primaryCulprit: 'GOS' };
    if (grams > 30) return { rating: 'moderate', note: 'Moderate GOS above 30g (2 tbsp)', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe Low FODMAP up to 30g (2 tbsp)', primaryCulprit: 'GOS' };
  }
  if (/pumpkin seed|pepita/i.test(lower)) {
    if (grams > 50) return { rating: 'high', note: 'High GOS above 50g', primaryCulprit: 'GOS' };
    if (grams > 23) return { rating: 'moderate', note: 'Moderate GOS above 23g (2 tbsp)', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe Low FODMAP up to 23g (2 tbsp)', primaryCulprit: 'GOS' };
  }
  if (/sesame seed/i.test(lower)) {
    if (grams > 30) return { rating: 'high', note: 'High GOS above 30g', primaryCulprit: 'GOS' };
    if (grams > 11) return { rating: 'moderate', note: 'Moderate GOS above 11g (1 tbsp)', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe Low FODMAP up to 11g (1 tbsp)', primaryCulprit: 'GOS' };
  }
  if (/hemp seed/i.test(lower)) {
    if (grams > 40) return { rating: 'high', note: 'High GOS above 40g', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe Low FODMAP up to 20g (2 tbsp)', primaryCulprit: 'GOS' };
  }
  if (/almond butter/i.test(lower)) {
    if (grams > 32) return { rating: 'moderate', note: 'Moderate GOS at 32g+', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe Low FODMAP at 1 tbsp (20g)', primaryCulprit: 'GOS' };
  }
  if (/\b(almonds?)\b/i.test(lower)) {
    if (grams > 24) return { rating: 'high', note: 'High GOS above 24g', primaryCulprit: 'GOS' };
    if (grams > 12) return { rating: 'moderate', note: 'Moderate GOS above 12g', primaryCulprit: 'GOS' };
    return { rating: 'low', note: 'Safe up to 10 nuts (12g)', primaryCulprit: 'GOS' };
  }
  if (/yogurt|yoghurt/i.test(lower) && !/lactose[- ]free|coconut/i.test(lower)) {
    if (grams >= 50) return { rating: 'high', note: 'High Lactose at 3 tbsp (60g+)', primaryCulprit: 'Lactose' };
    if (grams > 25) return { rating: 'moderate', note: 'Moderate Lactose at 2 tbsp (40g)', primaryCulprit: 'Lactose' };
    return { rating: 'low', note: 'Safe up to 1 tbsp (20g)', primaryCulprit: 'Lactose' };
  }
  if (/celery/i.test(lower)) {
    if (grams >= 25) return { rating: 'high', note: 'High Mannitol (1 stick)', primaryCulprit: 'Mannitol' };
    if (grams > 12) return { rating: 'moderate', note: 'Moderate Mannitol (1/2 stick)', primaryCulprit: 'Mannitol' };
    return { rating: 'low', note: 'Safe up to 1/4 stick (10g)', primaryCulprit: 'Mannitol' };
  }

  return { rating: ev.rating || baseRating || 'low', primaryCulprit: culprit };
}

/**
 * Calculates comprehensive FODMAP stacking for an entire plate (multi-dish)
 * as well as rolling accumulation from prior meals within a 4-5 hour window
 * based on Monash University clinical stacking research.
 */
export function calculatePlateFodmapStacking(
  dishes: PlateDish[],
  recentMeals: Meal[] = [],
  currentTimestampIso: string = new Date().toISOString()
): DetailedFodmapStackingReport {
  const currentTimeMs = new Date(currentTimestampIso).getTime();
  const rollingWindowHours = 5; // 5 hours digestion lookback

  const subtypeList: FodmapSubtype[] = [
    'Fructans',
    'GOS',
    'Lactose',
    'Excess Fructose',
    'Sorbitol',
    'Mannitol',
  ];

  const mapSubtypeKey: Record<FodmapSubtype, keyof FodmapCarbLevels> = {
    'Fructans': 'fructans',
    'GOS': 'gos',
    'Lactose': 'lactose',
    'Excess Fructose': 'fructose',
    'Sorbitol': 'sorbitol',
    'Mannitol': 'mannitol',
  };

  const loads: Record<FodmapSubtype, FodmapSubtypeLoad> = {
    'Fructans': { subtype: 'Fructans', loadScore: 0, threshold: 1.0, percentOfThreshold: 0, status: 'Safe', contributingIngredients: [] },
    'GOS': { subtype: 'GOS', loadScore: 0, threshold: 1.0, percentOfThreshold: 0, status: 'Safe', contributingIngredients: [] },
    'Lactose': { subtype: 'Lactose', loadScore: 0, threshold: 1.0, percentOfThreshold: 0, status: 'Safe', contributingIngredients: [] },
    'Excess Fructose': { subtype: 'Excess Fructose', loadScore: 0, threshold: 1.0, percentOfThreshold: 0, status: 'Safe', contributingIngredients: [] },
    'Sorbitol': { subtype: 'Sorbitol', loadScore: 0, threshold: 1.0, percentOfThreshold: 0, status: 'Safe', contributingIngredients: [] },
    'Mannitol': { subtype: 'Mannitol', loadScore: 0, threshold: 1.0, percentOfThreshold: 0, status: 'Safe', contributingIngredients: [] },
  };

  // 1. Calculate loads from current plate dishes
  for (const dish of dishes) {
    const multiplier = dish.portionMultiplier || 1.0;
    for (const ing of dish.ingredients || []) {
      const netGrams = (ing.gramsEstimate || 100) * multiplier;
      const canonical = canonicalizeIngredient(ing.name);

      // Pure protein, pure fat, and zero-FODMAP staple foods NEVER contribute to FODMAP stacking
      if (isZeroFodmapFood(canonical) || isZeroFodmapFood(ing.name)) {
        continue;
      }

      const ev = matchIngredientToFodmap(ing.name);
      const matched = ev.matchedFood;
      const stackableSubtypes = getFoodStackableSubtypes(ing.name, matched, ev.primaryCulprit);
      const dynamicEval = getDynamicIngredientRating(ing.name, netGrams, undefined, matched);

      const effectiveSubtypes = new Set(stackableSubtypes);
      if (ev.primaryCulprit) {
        const c = ev.primaryCulprit.toLowerCase();
        if (c.includes('fructan')) effectiveSubtypes.add('Fructans');
        if (c.includes('gos') || c.includes('galacto')) effectiveSubtypes.add('GOS');
        if (c.includes('lactose')) effectiveSubtypes.add('Lactose');
        if (c.includes('fructose')) effectiveSubtypes.add('Excess Fructose');
        if (c.includes('sorbitol')) effectiveSubtypes.add('Sorbitol');
        if (c.includes('mannitol')) effectiveSubtypes.add('Mannitol');
      }

      // If a food portion is moderate or high, ensure it stacks into an active subtype
      if (effectiveSubtypes.size === 0 && (dynamicEval.rating === 'moderate' || dynamicEval.rating === 'high')) {
        const notes = (matched?.notes || '').toLowerCase();
        if (notes.includes('fructan')) effectiveSubtypes.add('Fructans');
        else if (notes.includes('gos')) effectiveSubtypes.add('GOS');
        else if (notes.includes('lactose')) effectiveSubtypes.add('Lactose');
        else if (notes.includes('fructose')) effectiveSubtypes.add('Excess Fructose');
        else if (notes.includes('sorbitol')) effectiveSubtypes.add('Sorbitol');
        else if (notes.includes('mannitol')) effectiveSubtypes.add('Mannitol');
        else effectiveSubtypes.add('Fructans');
      }

      for (const st of subtypeList) {
        const carbKey = mapSubtypeKey[st];
        let carbIntensity = ing.fodmaps?.[carbKey] ?? matched?.fodmaps?.[carbKey];

        // If not explicitly defined, check primaryCulprit
        if (carbIntensity === undefined || carbIntensity === null) {
          if (ev.primaryCulprit?.toLowerCase().includes(st.toLowerCase())) {
            carbIntensity = ev.rating === 'high' ? 3 : 2;
          } else {
            carbIntensity = 1;
          }
        }

        let load = 0;
        const isTarget = effectiveSubtypes.has(st) || carbIntensity >= 2;

        // Dynamically adjust load based on whether current quantity is Safe Low, Moderate, or High
        if (dynamicEval.rating === 'low') {
          // Food portion is in the green / safe Low-FODMAP zone
          if (effectiveSubtypes.has(st)) {
            const safeLimit = matched?.servings?.low?.grams || 50;
            // Standard safe green serve contributes ~20-25% of the single-meal threshold
            load = Math.min(0.28, (netGrams / safeLimit) * 0.22);
          } else {
            load = 0;
          }
        } else if (dynamicEval.rating === 'moderate') {
          // Food portion exceeded safe threshold into moderate territory
          if (isTarget) {
            const safeLimit = matched?.servings?.low?.grams || 50;
            const modLimit = matched?.servings?.moderate?.grams || (safeLimit * 1.3);
            load = Math.min(0.90, 0.40 + Math.max(0, (netGrams - safeLimit) / Math.max(1, modLimit - safeLimit)) * 0.40);
          }
        } else if (dynamicEval.rating === 'high') {
          // Food portion is high FODMAP
          if (isTarget) {
            const highLimit = matched?.servings?.high?.grams || matched?.servings?.moderate?.grams || (matched?.servings?.low?.grams ? matched.servings.low.grams * 1.4 : 35);
            load = Math.max(1.0, netGrams / highLimit);
          }
        }

        if (load >= 0.05) {
          loads[st].loadScore += load;
          loads[st].contributingIngredients.push({
            name: ing.name,
            grams: Math.round(netGrams),
            dishName: dish.title || 'Plate',
            subScore: Math.round(load * 100) / 100,
          });
        }
      }
    }
  }

  // 2. Rolling lookback from meals within 5 hours
  const relevantRecent = recentMeals.filter((m) => {
    if (!m.timestamp_iso) return false;
    const diffHours = (currentTimeMs - new Date(m.timestamp_iso).getTime()) / (1000 * 60 * 60);
    return diffHours > 0.05 && diffHours <= rollingWindowHours;
  });

  let rollingPriorContribution = 0;
  for (const prevMeal of relevantRecent) {
    const hoursAgo = Math.round(((currentTimeMs - new Date(prevMeal.timestamp_iso).getTime()) / (1000 * 60 * 60)) * 10) / 10;
    const decayFactor = Math.max(0, 1 - (hoursAgo / rollingWindowHours));

    const ingList = (prevMeal.ingredients_tags || '')
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);

    for (const ingName of ingList) {
      const ev = matchIngredientToFodmap(ingName);
      if (ev.rating === 'low') continue;
      const matched = ev.matchedFood;

      for (const st of subtypeList) {
        const carbKey = mapSubtypeKey[st];
        const intensity = matched?.fodmaps?.[carbKey] || (ev.primaryCulprit?.toLowerCase().includes(st.toLowerCase()) ? 2 : 1);
        if (intensity >= 2) {
          const load = (intensity === 3 ? 0.7 : 0.35) * decayFactor;
          loads[st].loadScore += load;
          rollingPriorContribution += load;
          loads[st].contributingIngredients.push({
            name: `${ingName} (${hoursAgo}h ago)`,
            grams: matched?.servings?.moderate?.grams || 50,
            dishName: prevMeal.description || 'Prior meal',
            subScore: Math.round(load * 100) / 100,
          });
        }
      }
    }
  }

  // 3. Finalize scores and thresholds
  let maxScore = 0;
  let highestCategory: FodmapSubtype = 'Fructans';
  const activeWarnings: string[] = [];
  const safePortionTips: string[] = [];

  for (const st of subtypeList) {
    const score = Math.round(loads[st].loadScore * 10) / 10;
    loads[st].loadScore = score;
    const percent = Math.round((score / loads[st].threshold) * 100);
    loads[st].percentOfThreshold = percent;

    if (score > maxScore) {
      maxScore = score;
      highestCategory = st;
    }

    if (percent > 100) {
      loads[st].status = 'Overload';
      activeWarnings.push(`High ${st} load (${percent}% of single-meal limit). Consider reducing portion or swapping ingredients.`);
    } else if (percent >= 75) {
      loads[st].status = 'Caution';
      activeWarnings.push(`Approaching ${st} threshold (${percent}%). Allow 4 hours before having more ${st}.`);
    } else {
      loads[st].status = 'Safe';
    }

    if (loads[st].contributingIngredients.length >= 2) {
      safePortionTips.push(`Multiple ${st} sources detected (${loads[st].contributingIngredients.map((c) => c.name).join(', ')}). Space these items across different meals.`);
    }
  }

  const highestPercent = Math.round(maxScore * 100);
  const isStackingWarning = maxScore > 1.0;

  let overallPlateRating: FodmapRating = 'low';
  if (maxScore > 1.0) {
    overallPlateRating = 'high';
  } else if (maxScore >= 0.75) {
    overallPlateRating = 'moderate';
  }

  return {
    overallPlateRating,
    isStackingWarning,
    highestCategory,
    highestPercent,
    subtypes: subtypeList.map((st) => loads[st]),
    activeWarnings,
    safePortionTips,
    priorMealLookback: {
      windowHours: rollingWindowHours,
      mealsCount: relevantRecent.length,
      priorLoadMultiplier: Math.round(rollingPriorContribution * 10) / 10,
    },
  };
}

/**
 * Evaluates the overall FODMAP rating (low, moderate, high) for a logged meal.
 */
export function getMealFodmapRating(meal: Meal): FodmapRating {
  if (!meal) return 'low';

  // Fast path: use existing evaluated overallPlateRating or fodmap_rating
  if (meal.stacking_report?.overallPlateRating) {
    return meal.stacking_report.overallPlateRating;
  }
  if (meal.fodmap_rating) {
    return meal.fodmap_rating;
  }

  if (meal.dishes && meal.dishes.length > 0) {
    const report = calculatePlateFodmapStacking(meal.dishes, [], meal.timestamp_iso);
    meal.fodmap_rating = report.overallPlateRating;
    return report.overallPlateRating;
  }

  if (meal.ingredients && meal.ingredients.length > 0) {
    const dish: PlateDish = {
      id: 'dish-eval',
      title: meal.description || 'Meal',
      portionMultiplier: meal.portionMultiplier || 1.0,
      ingredients: meal.ingredients,
    };
    const report = calculatePlateFodmapStacking([dish], [], meal.timestamp_iso);
    meal.fodmap_rating = report.overallPlateRating;
    return report.overallPlateRating;
  }

  const tagsStr = meal.ingredients_tags || meal.description || '';
  if (!tagsStr.trim()) return 'low';

  const items = convertTagsToIngredientItems(tagsStr);
  const dish: PlateDish = {
    id: 'dish-eval',
    title: meal.description || 'Meal',
    portionMultiplier: meal.portionMultiplier || 1.0,
    ingredients: items,
  };
  const report = calculatePlateFodmapStacking([dish], [], meal.timestamp_iso);
  meal.fodmap_rating = report.overallPlateRating;
  return report.overallPlateRating;
}

const mealReportCache = new Map<string, {
  rating: FodmapRating;
  dominantCulprit?: string;
  sideScale: FodmapSideScaleReport;
}>();

export function clearMealReportCache(): void {
  mealReportCache.clear();
}

/**
 * Returns a comprehensive FODMAP report for any logged meal, including
 * exact 6-FODMAP side-scale levels and dominant trigger culprit.
 */
export function getMealFodmapReport(meal: Meal): {
  rating: FodmapRating;
  dominantCulprit?: string;
  sideScale: FodmapSideScaleReport;
} {
  if (!meal) {
    return {
      rating: 'low',
      sideScale: { overallRating: 'low', items: [] },
    };
  }

  const cacheKey = `${meal.id || 'draft'}_${(meal as any).updated_at || meal.timestamp_iso || meal.date || ''}_${meal.ingredients_tags || ''}_${meal.portionMultiplier || 1}_${(meal.dishes || []).length}`;
  const cached = mealReportCache.get(cacheKey);
  if (cached) return cached;

  const rating = getMealFodmapRating(meal);
  const allIngredients = meal.dishes && meal.dishes.length > 0
    ? meal.dishes.flatMap((d) => (d.ingredients || []).map((i) => ({
        ...i,
        gramsEstimate: (i.gramsEstimate || 100) * (d.portionMultiplier || 1.0),
      })))
    : (meal.ingredients && meal.ingredients.length > 0
        ? meal.ingredients.map((i) => ({
            ...i,
            gramsEstimate: (i.gramsEstimate || 100) * (meal.portionMultiplier || 1.0),
          }))
        : convertTagsToIngredientItems(meal.ingredients_tags || meal.description || ''));

  const sideScale = calculateDetailedFodmapSideScale(allIngredients);
  const finalRating = sideScale.overallRating || rating;
  const result = {
    rating: finalRating,
    dominantCulprit: sideScale.dominantCulprit || (meal.stacking_report?.isStackingWarning ? meal.stacking_report?.highestCategory : undefined),
    sideScale,
  };

  if (mealReportCache.size > 2000) mealReportCache.clear();
  mealReportCache.set(cacheKey, result);
  return result;
}


